import { useVideoScoreDisplay } from '../services/videoScoreDisplay';
import { scoreFill } from './NoteMapGrid';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RichTextEditor, RichTextEditorHandle } from './RichTextEditor';
import { TextConflictBanner } from './TextConflictBanner';

// Module-level: tracks which note IDs have already been auto-checked this session.
// Prevents repeated auto-checks when aiCheckContentHash isn't persisted yet.
const autoCheckedThisSession = new Set<string>();

// Set to true to test the WYSIWYG TipTap editor (spike — ExpandedNoteModal only)
const ENABLE_RICH_TEXT_EDITOR = true;
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { X, Minimize2, Maximize2, Eye, Expand, Shrink, Type, TextQuote, Loader2, PenTool, FileText, Bold, Italic, List, Check, GripVertical, LifeBuoy, Copy, Clipboard, Mic, Brain, Sparkles, Settings, Link as LinkIcon, Link2, ArrowUpRight, PanelRightClose, PanelRightOpen, PanelLeft, Sigma, Search, ArrowLeft, Plus, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Edit, Trash2, RefreshCw, CaseSensitive, Tag, Upload, ImagePlus, WrapText, BookOpen, ListTree, Zap, CheckCircle2, AlertTriangle, XCircle, Monitor, PenLine, Clock, Lightbulb, Info, Pin, MoreHorizontal, SunMoon, GraduationCap, BrainCircuit, Globe, Youtube, Bookmark, Highlighter } from 'lucide-react';
import { MindMapNode, Language, NodeAttachment, RelatedVideoRef, YouTubeVideoEntry, Collection } from '../types';
import { getVideosForNode } from '../services/youtubeService';
import { getAuth } from 'firebase/auth';
import { subscribeCollections, addItemToCollection, removeItemFromCollection, createCollection } from '../services/collectionsService';
import { formatNoteContent, transcribeAndRefineAudio, refineText, analyzeLineByLine, extractImageHashtags, quickCheckNote, convertSpokenToWritten, callHintForNote, callGenerateWritingHint, pinpointError, semanticSearchNotes, searchAcrossNotebooks, sendTutorSessionEmail, translateNoteToEnglish, translateText, CrossNotebookSearchResult, VoiceTutorSettings, DEFAULT_TUTOR_SETTINGS, getNodeContent, listAllVideos, AllVideosEntry } from '../services/geminiService';
import { SketchPad } from './SketchPad';
import { insertMarkdown, parseMarkdown, reflowText, applyKatexToElement } from '../utils/markdownUtils';
import { ImageGalleryModal } from './ImageGalleryModal';
import { MathAutocomplete } from './MathAutocomplete';
import { MathSymbolPickerModal } from './MathSymbolPickerModal';
import { searchMathSymbols, MathSymbol, TRIGGER_MAP, refreshSymbolCache, getAllSymbols } from '../utils/mathSymbols';
import { recordSymbolUsage, getSymbolUsages, symbolKey } from '../utils/symbolUsage';
import { getAdjacentNoteIds } from '../utils/noteSequence';
import { useHistory } from '../hooks/useHistory';
import { registerUndoScope } from '../hooks/useUndoArbiter';
import { useIsMobile } from '../hooks/useIsMobile';
import { NodeImageSlider } from './NodeImageSlider';
import { OutlinePanel } from './OutlinePanel';
import { VoiceTutorModal } from './VoiceTutorModal';
import { VoiceTutorLiveModal } from './VoiceTutorLiveModal';
import posthog from 'posthog-js';

/** Renders markdown+math and post-processes with KaTeX auto-render to catch any
 *  $...$ that parseMarkdown missed when window.katex was not yet ready. */
const MathContent = ({ text, className }: { text: string; className?: string }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Re-parse in effect so window.katex is guaranteed to be loaded
        el.innerHTML = parseMarkdown(text);
        applyKatexToElement(el);
        // Retry once in case KaTeX CDN was still loading
        // @ts-ignore
        if (!window.katex) {
            const timer = setTimeout(() => {
                el.innerHTML = parseMarkdown(text);
                applyKatexToElement(el);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [text]);

    return <div ref={ref} className={className} />;
};

/**
 * Accurately calculates the pixel coordinates of the cursor (caret) in a textarea
 * by using a mirror div technique. This accounts for text wrapping, font size, and container width.
 * @param textarea - The textarea element
 * @param caretPosition - The character position of the cursor
 * @returns { x, y } coordinates relative to the viewport
 */
function getCaretCoordinates(textarea: HTMLTextAreaElement, caretPosition: number): { x: number; y: number } {
    // Create a mirror div with identical styling
    const mirror = document.createElement('div');
    const computed = getComputedStyle(textarea);

    // Copy all relevant styles
    // WARNING: If you change the textarea styles in CSS, make sure to update this list!
    const styleProps = [
        'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
        'letterSpacing', 'wordSpacing', 'lineHeight',
        'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
        'border', 'borderWidth', 'boxSizing',
        'whiteSpace', 'wordWrap', 'wordBreak', 'overflowWrap'
    ];

    styleProps.forEach(prop => {
        mirror.style[prop as any] = computed[prop as any];
    });

    // Position off-screen but maintain dimensions
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.top = '0';
    mirror.style.left = '0';
    mirror.style.width = `${textarea.clientWidth}px`; // Match textarea width for wrapping
    mirror.style.height = 'auto';
    mirror.style.overflow = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap'; // Ensure wrapping matches textarea

    // Insert mirror into DOM
    document.body.appendChild(mirror);

    try {
        // Get text up to cursor position
        const textBeforeCaret = textarea.value.substring(0, caretPosition);

        // Create a span to mark the cursor position
        mirror.textContent = textBeforeCaret;
        const span = document.createElement('span');
        span.textContent = textarea.value.substring(caretPosition, caretPosition + 1) || '.'; // Use next char or placeholder
        mirror.appendChild(span);

        // Get the position of the span (cursor position) relative to mirror
        const spanRect = span.getBoundingClientRect();
        const textareaRect = textarea.getBoundingClientRect();

        // Calculate position relative to viewport
        const x = textareaRect.left + (spanRect.left - document.body.getBoundingClientRect().left);
        const y = textareaRect.top + (spanRect.top - document.body.getBoundingClientRect().top) - textarea.scrollTop;

        return { x, y };
    } finally {
        // Clean up
        document.body.removeChild(mirror);
    }
}

interface ExpandedNoteModalProps {
    /** Switch to / drop a text version kept by a sync conflict. */
    onResolveTextConflict?: (action: 'restore' | 'dismiss', index: number) => void;
    /** Opens the draw-on-screen board (the recording's), without recording. */
    onDrawOnScreen?: () => void;
    node: MindMapNode;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: (id: string, data: Partial<MindMapNode>) => void;
    onGetFramework: (id: string) => void;
    onAnalyze: (id: string, mode?: 'simple' | 'detailed' | 'pinpoint') => void;
    onCancelAnalyze?: (id: string) => void;
    onGenerateTitle?: (id: string) => Promise<string>;
    onOpenSettings?: () => void;
    linkedNodes?: { id: string; title: string; linkId: string; linkColor?: string; relationType?: 'parent' | 'child' | 'peer'; notebookId?: string; notebookTitle?: string }[];
    onNavigateToNote?: (noteId: string) => void;
    language: Language;
    readOnly?: boolean;
    allNodes?: MindMapNode[]; // For search functionality
    onCreateLink?: (targetId: string, targetNotebookId?: string, targetTitle?: string, targetNotebookTitle?: string) => void;
    // Linking two notes from the outline (forwarded to OutlinePanel)
    pendingLink?: { id: string; title: string } | null;
    onPendingLinkChange?: (p: { id: string; title: string } | null) => void;
    onLinkNotes?: (sourceId: string, targetId: string, targetNotebookId?: string, targetTitle?: string, targetNotebookTitle?: string) => void;
    onUnlink?: (targetId: string) => void;
    onAddRelatedVideo?: (ref: Omit<RelatedVideoRef, 'id' | 'createdAt'>) => void;
    onRemoveRelatedVideo?: (refId: string) => void;
    allNotebooks?: { id: string; title: string; nodes: MindMapNode[] }[]; // For cross-notebook search
    onNavigateCrossNotebook?: (notebookId: string, nodeId: string) => void;
    currentNotebookId?: string;
    outlinePanelOpen?: boolean;
    onToggleOutline?: () => void;
    onCreateChildNote?: () => void;
    mediaPanelOpen?: boolean;
    onToggleMediaPanel?: () => void;
    lastSavedTime?: Date | null;
    syncState?: 'saved' | 'saving' | 'error';
    onTagSearch?: (tag: string) => void;
    onBack?: () => void;
    backLabel?: string;
    defaultFullscreen?: boolean;
    onEnterOutlineLinkMode?: () => void;
    // Mobile "Outline" tab — embeds OutlinePanel directly, mirrors the App-level OutlinePanel usages
    onApplyOrder?: (orderedNodes: MindMapNode[]) => void;
    onAutoTitle?: (id: string) => Promise<void>;
    onAutoTitleBranch?: (rootId: string) => Promise<void>;
    onRenameNode?: (id: string, newTitle: string) => void;
    linkMode?: boolean;
    onLinkSelect?: (targetId: string, notebookId?: string, nodeTitle?: string, notebookTitle?: string) => void;
    onCancelLinkMode?: () => void;
    onCreateChildNoteAt?: (parentId: string) => void;
    userApiKeys?: { gemini?: string; openai?: string };
    findReplaceVersion?: number;
    externalFindQuery?: string;
    onSharePost?: () => void;
    onUploadYouTube?: () => void;
    onLoadNotebook?: (notebookId: string) => Promise<void>;
    /** When true, uses fixed+z-[300] so the modal floats above other full-screen overlays (e.g. Threads Manager). Default: absolute+z-[200] so MediaPanel flex-sibling still appears alongside. */
    elevateAboveOverlay?: boolean;
}

// ─── Tutor Settings Popover ───────────────────────────────────────────────────
const TUTOR_STORAGE_KEY = 'sb_voice_tutor_settings';
const TUTOR_VOICES = {
    vietnamese: [{ id: 'vi-male-b', label: 'Nam' }, { id: 'vi-female-a', label: 'Nữ' }],
    english: [{ id: 'en-male-a', label: 'Male 1' }, { id: 'en-male-b', label: 'Male 2' }, { id: 'en-female-a', label: 'Female 1' }, { id: 'en-female-b', label: 'Female 2' }],
};
const TUTOR_VALID_VOICE_IDS = new Set(['vi-male-b', 'vi-female-a', 'en-male-a', 'en-male-b', 'en-female-a', 'en-female-b']);
const TUTOR_STYLES = [
    { id: 'polite',   emoji: '🎓', label: 'Lịch sự',    labelEn: 'Polite',   tipVi: 'Trang trọng, xưng bạn/mình — phù hợp mọi ngữ cảnh',     tipEn: 'Formal & respectful tone, suitable for all contexts' },
    { id: 'friendly', emoji: '😊', label: 'Thân thiện',  labelEn: 'Friendly', tipVi: 'Vui vẻ, năng động, hay dùng cảm thán tự nhiên',           tipEn: 'Upbeat and lively, uses natural exclamations' },
    { id: 'maytao',   emoji: '👊', label: 'Mày Tao',     labelEn: 'Casual',   tipVi: 'Bạn thân cực thân — xưng mày/tao hoàn toàn',             tipEn: 'Very casual, talks to you like a close friend' },
    { id: 'brutal',   emoji: '💀', label: 'Thẳng Thắn',  labelEn: 'Brutal',   tipVi: 'Nói thẳng không vòng vo — khen hay chê đều thật lòng',   tipEn: 'Straight shooter — blunt praise or criticism' },
];
const TUTOR_SPEEDS = [
    { value: 0.85, label: '0.85×' },
    { value: 1.0,  label: '1.0×' },
    { value: 1.1,  label: '1.1×' },
    { value: 1.25, label: '1.25×' },
];

function loadTutorSettings(fallbackLang: string): { lang: 'vietnamese' | 'english'; settings: VoiceTutorSettings } {
    try {
        const raw = localStorage.getItem(TUTOR_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const lang: 'vietnamese' | 'english' = parsed.lang ?? (fallbackLang === 'vietnamese' ? 'vietnamese' : 'english');
            const settings: VoiceTutorSettings = { ...DEFAULT_TUTOR_SETTINGS, ...parsed.settings };
            if (!TUTOR_VALID_VOICE_IDS.has(settings.voiceId)) settings.voiceId = lang === 'vietnamese' ? 'vi-male-b' : 'en-male-a';
            return { lang, settings };
        }
    } catch {}
    const lang = fallbackLang === 'vietnamese' ? 'vietnamese' : 'english';
    return { lang, settings: { ...DEFAULT_TUTOR_SETTINGS, voiceId: lang === 'vietnamese' ? 'vi-male-b' : 'en-male-a' } };
}

const TutorSettingsPopover: React.FC<{ language: string; onClose: () => void }> = ({ language, onClose }) => {
    const initial = loadTutorSettings(language);
    const [lang, setLang] = useState<'vietnamese' | 'english'>(initial.lang);
    const [settings, setSettings] = useState<VoiceTutorSettings>(initial.settings);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        try { localStorage.setItem(TUTOR_STORAGE_KEY, JSON.stringify({ lang, settings })); } catch {}
    }, [lang, settings]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const isVi = lang === 'vietnamese';
    const voices = isVi ? TUTOR_VOICES.vietnamese : TUTOR_VOICES.english;

    return (
        <div ref={ref} className="absolute top-full right-0 mt-1 z-[200] w-64 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-xl p-3 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Voice Tutor Settings</span>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"><X size={13} /></button>
            </div>

            {/* Language */}
            <div>
                <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">Language</div>
                <div className="flex gap-1.5">
                    {(['vietnamese', 'english'] as const).map(l => (
                        <button key={l} onClick={() => {
                            setLang(l);
                            const wrongVoice = l === 'vietnamese' ? !settings.voiceId.startsWith('vi') : !settings.voiceId.startsWith('en');
                            if (wrongVoice) setSettings(s => ({ ...s, voiceId: l === 'vietnamese' ? 'vi-male-b' : 'en-male-a' }));
                        }} className={`flex-1 py-1 rounded-lg text-xs font-semibold transition-colors ${lang === l ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                            {l === 'vietnamese' ? '🇻🇳 Tiếng Việt' : '🇬🇧 English'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Voice */}
            <div>
                <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">Voice</div>
                <div className="flex gap-1.5 flex-wrap">
                    {voices.map(v => (
                        <button key={v.id} onClick={() => setSettings(s => ({ ...s, voiceId: v.id }))}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${settings.voiceId === v.id ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                            {v.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Speed */}
            <div>
                <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">Speed</div>
                <div className="flex gap-1">
                    {TUTOR_SPEEDS.map(sp => (
                        <button key={sp.value} onClick={() => setSettings(s => ({ ...s, speed: sp.value }))}
                            className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors ${settings.speed === sp.value ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                            {sp.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Style */}
            <div>
                <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">Style</div>
                <div className="grid grid-cols-2 gap-1">
                    {TUTOR_STYLES.map(st => {
                        const tip = isVi ? st.tipVi : st.tipEn;
                        return (
                            <div key={st.id} className="relative group">
                                <button onClick={() => setSettings(s => ({ ...s, style: st.id }))}
                                    className={`w-full py-1.5 rounded-lg text-xs transition-colors flex items-center justify-center gap-1 ${settings.style === st.id ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                    <span>{st.emoji}</span>
                                    <span>{isVi ? st.label : st.labelEn}</span>
                                </button>
                                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-[300] shadow-lg">
                                    {tip}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Role */}
            <div>
                <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {isVi ? 'Vai trò AI' : 'AI Role'}
                </div>
                <div className="flex gap-1.5">
                    {([
                        { id: 'feynman', emoji: '🧑‍🎓', label: isVi ? 'Feynman' : 'Feynman',
                          tip: isVi ? 'AI giả vờ không biết gì — bạn đóng vai người dạy, giải thích cho AI' : 'AI pretends to know nothing — you become the teacher' },
                        { id: 'buddy',   emoji: '👫',    label: isVi ? 'Bạn học' : 'Study Buddy',
                          tip: isVi ? 'Cùng ôn bài như hai người bạn — không áp lực, cùng nhau khám phá' : 'Review together as friends — relaxed, no pressure' },
                    ] as const).map(r => (
                        <div key={r.id} className="relative group flex-1">
                            <button onClick={() => setSettings(s => ({ ...s, role: r.id }))}
                                className={`w-full py-1 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1 ${settings.role === r.id ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                <span>{r.emoji}</span><span>{r.label}</span>
                            </button>
                            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-[300] shadow-lg max-w-[180px] text-center leading-tight">
                                {r.tip}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* STT Mode */}
            <div>
                <div className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {isVi ? 'Nhận giọng nói' : 'Speech Recognition'}
                </div>
                <div className="flex gap-1.5">
                    {([
                        { id: 'realtime', label: 'Real-time',
                          tip: isVi ? 'Hiện chữ ngay khi nói — nhanh, nhưng từ tiếng Anh trong tiếng Việt có thể bị sai' : 'Shows text as you speak — fast but may mishear English words in Vietnamese' },
                        { id: 'gemini',   label: 'Gemini',
                          tip: isVi ? 'Ghi âm xong mới gửi — chính xác hơn khi nói tiếng Việt xen tiếng Anh' : 'Records then transcribes — better accuracy for mixed Vietnamese & English' },
                    ] as const).map(m => (
                        <div key={m.id} className="relative group flex-1">
                            <button onClick={() => setSettings(s => ({ ...s, sttMode: m.id }))}
                                className={`w-full py-1 rounded-lg text-xs font-medium transition-colors ${settings.sttMode === m.id ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                {m.label}
                            </button>
                            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-[300] shadow-lg max-w-[180px] text-center leading-tight">
                                {m.tip}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
// ─────────────────────────────────────────────────────────────────────────────

function highlightHtmlText(html: string, query: string): string {
    if (!query.trim()) return html;
    const q = query.toLowerCase();
    const qLen = query.length;
    const result: string[] = [];
    let i = 0;
    let inTag = false;
    while (i < html.length) {
        const ch = html[i];
        if (ch === '<') { inTag = true; result.push(ch); i++; }
        else if (ch === '>') { inTag = false; result.push(ch); i++; }
        else if (inTag) { result.push(ch); i++; }
        else if (html.slice(i, i + qLen).toLowerCase() === q) {
            result.push('<mark class="find-replace-highlight">');
            result.push(html.slice(i, i + qLen));
            result.push('</mark>');
            i += qLen;
        } else { result.push(ch); i++; }
    }
    return result.join('');
}

const Tip: React.FC<{ text: string; children: React.ReactNode; side?: 'top' | 'bottom'; align?: 'center' | 'right' }> = ({ text, children, side = 'top', align = 'center' }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const show = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setPos({
      top: side === 'top' ? r.top - 8 : r.bottom + 8,
      left: align === 'right' ? r.right : r.left + r.width / 2,
    });
  }, [side, align]);
  return (
    <div className="inline-flex" ref={wrapRef} onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      {children}
      {pos && createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform: `translateX(${align === 'right' ? '-100%' : '-50%'}) ${side === 'top' ? 'translateY(-100%)' : ''}`, zIndex: 99999, pointerEvents: 'none' }}
          className="px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg shadow-lg w-max max-w-[220px] text-center whitespace-pre-wrap"
        >
          {text}
          <span className={`absolute ${side === 'top' ? 'top-full border-t-gray-900 dark:border-t-gray-700' : 'bottom-full border-b-gray-900 dark:border-b-gray-700'} ${align === 'right' ? 'right-3' : 'left-1/2 -translate-x-1/2'} border-4 border-transparent`} />
        </div>,
        document.body
      )}
    </div>
  );
};

export const ExpandedNoteModal: React.FC<ExpandedNoteModalProps> = ({
    onDrawOnScreen,
    node,
    isOpen,
    onClose,
    onUpdate,
    onGetFramework,
    onAnalyze,
    onCancelAnalyze,
    onGenerateTitle,
    onOpenSettings,
    linkedNodes = [],
    onNavigateToNote,
    language,
    readOnly = false,
    allNodes = [],
    onCreateLink,
    pendingLink,
    onPendingLinkChange,
    onLinkNotes,
    onUnlink,
    onAddRelatedVideo,
    onRemoveRelatedVideo,
    allNotebooks = [],
    onNavigateCrossNotebook,
    currentNotebookId,
    outlinePanelOpen = false,
    onToggleOutline,
    onCreateChildNote,
    mediaPanelOpen = false,
    onToggleMediaPanel,
    lastSavedTime,
    syncState,
    onTagSearch,
    onBack,
    backLabel,
    defaultFullscreen = false,
    onEnterOutlineLinkMode,
    onApplyOrder,
    onAutoTitle,
    onAutoTitleBranch,
    onRenameNode,
    linkMode = false,
    onLinkSelect,
    onCancelLinkMode,
    onCreateChildNoteAt,
    userApiKeys,
    findReplaceVersion,
    onResolveTextConflict,
    externalFindQuery = '',
    onSharePost,
    onUploadYouTube,
    onLoadNotebook,
    elevateAboveOverlay = false,
}) => {
    const videoScoreDisplay = useVideoScoreDisplay(); // switched on in YouTube Manager's map tab
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const richEditorRef = useRef<RichTextEditorHandle | null>(null);
    const noteContentRef = useRef<HTMLDivElement>(null);
    const glowRafRef = useRef<number | null>(null);
    const [activeTab, setActiveTab] = useState<'write' | 'draw'>('write');
    // Mobile-only bottom tab bar (Note / Info / Outline), unrelated to activeTab above
    const [mobileSection, setMobileSection] = useState<'note' | 'info' | 'outline'>('note');
    const isMobileViewport = useIsMobile();
    // Mobile-only: collapse header/tabs/toolbar chrome to give the text more room
    const [mobileChromeCollapsed, setMobileChromeCollapsed] = useState(false);
    // Mobile-only: dropdown to pick which child note to jump to, when a note has more than one
    const [mobileChildNavOpen, setMobileChildNavOpen] = useState(false);
    const [imageGallery, setImageGallery] = useState<{ isOpen: boolean; initialIndex: number } | null>(null);
    const [imgInverted, setImgInverted] = useState(() => localStorage.getItem('sb_image_invert') === 'true');
    const [annotatingImage, setAnnotatingImage] = useState<{ src: string; type: 'main' | 'attachment'; id?: string } | null>(null);
    // Empty notes start in edit mode so user can type immediately; notes with content start in view mode.
    const [isEditing, setIsEditing] = useState(!node.content?.trim());
    const [isBoldActive, setIsBoldActive] = useState(false);
    const [showRaw, setShowRaw] = useState(false);
    const [isTitleGenerating, setIsTitleGenerating] = useState(false);
    const [isExtractingTags, setIsExtractingTags] = useState(false);
    const [addTagInput, setAddTagInput] = useState<string | null>(null);
    const addTagInputRef = useRef<HTMLInputElement>(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const imageFileInputRef = useRef<HTMLInputElement>(null);
    const [showFindReplace, setShowFindReplace] = useState(false);
    const [findQuery, setFindQuery] = useState('');
    const [matchCase, setMatchCase] = useState(false);
    const [replaceText, setReplaceText] = useState('');
    const [matchIndex, setMatchIndex] = useState(0);
    const [totalMatches, setTotalMatches] = useState(0);
    const findBarInputRef = useRef<HTMLInputElement>(null);
    const pendingCursorRef = useRef<{ clientX: number; clientY: number; scrollTop: number } | null>(null);

    // Local content state for smooth typing (with Undo/Redo history)
    const {
        state: localContent,
        setState: setLocalContent,
        undo,
        redo,
    } = useHistory(node.content || '');

    // ── Register this note's text-content undo with the centralized arbiter (§5.4) ──
    // undo/redo from useHistory are stable (useCallback with [] deps) so no ref-
    // forwarding is needed for them; isOpen/readOnly are read through refs since this
    // effect only runs once per mount.
    //
    // isActive is deliberately scoped to "the plain textarea itself is focused right
    // now" (document.activeElement), NOT just "this modal is open" — this note has TWO
    // other editable surfaces that must be left alone:
    //   - the title <input> (and other inputs in the modal) — should keep native
    //     browser undo, not have it hijacked into undoing the note's CONTENT;
    //   - the rich-text mode (RichTextEditor, ProseMirror/TipTap) — StarterKit ships
    //     its own undo/redo (history) extension, already working correctly at the
    //     keystroke level. Since this arbiter's listener runs in the capture phase
    //     (fires before the event ever reaches ProseMirror's own keymap), claiming it
    //     here would silently replace ProseMirror's fine-grained undo with this
    //     blur-debounced localContent one — worse, not better. Only claim when the
    //     plain textarea has focus so ProseMirror keeps handling its own Ctrl+Z
    //     untouched (this scope's isActive() is false, the notebook fallback scope
    //     also declines since allowNativeEditableTargets is false there, so the
    //     keypress reaches ProseMirror exactly as before this migration).
    const isOpenRef = useRef(isOpen);
    useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
    const readOnlyForUndoRef = useRef(readOnly);
    useEffect(() => { readOnlyForUndoRef.current = readOnly; }, [readOnly]);
    useEffect(() => {
        const handle = registerUndoScope({
            id: `note-content-${node.id}`,
            isActive: () => isOpenRef.current && !readOnlyForUndoRef.current && document.activeElement === textareaRef.current,
            allowNativeEditableTargets: true,
            undo,
            redo,
            getUndoLabel: () => null, // per-keystroke text undo — too noisy to toast on every step
            getRedoLabel: () => null,
        });
        handle.activate();
        return () => handle.unregister();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const debouncedUpdateRef = useRef<NodeJS.Timeout | null>(null);
    const isSelectingRef = useRef(false); // Prevent duplicate autocomplete selections
    const lastSelectionRef = useRef<{ start: number; end: number } | null>(null); // Store cursor position
    const isMathPickerInteractionRef = useRef(false); // Track if interacting with math picker to prevent blur

    // Recording Mode State
    // Default to Real-time on Desktop (>=768px), Legacy on Mobile
    const isMobile = window.innerWidth < 768;
    const [useRealTimeDictation, setUseRealTimeDictation] = useState(!isMobile);

    // Audio Recording State (Legacy Mode)
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [hasTranscriptionError, setHasTranscriptionError] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const savedAudioBlobRef = useRef<Blob | null>(null); // Backup audio for retry
    const baseContentBeforeRecordingRef = useRef('');
    const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const MAX_RECORDING_SECONDS = 180; // 3 minutes auto-stop

    // Real-time Speech Recognition State
    const recognitionRef = useRef<any>(null);
    const [isListening, setIsListening] = useState(false);
    
    // Persistent buffers for real-time transcription to prevent data loss
    const finalTranscriptRef = useRef('');
    const interimTranscriptRef = useRef('');


    // Math Autocomplete State
    const [autocomplete, setAutocomplete] = useState<{
        visible: boolean;
        position: { x: number; y: number };
        suggestions: MathSymbol[];
        selectedIndex: number;
        triggerStart: number;
        query: string;
        // '\\' when opened via the mobile `\` trigger (that character must stay part of what gets
        // replaced), '' when opened via Ctrl/Alt+Space (no literal prefix character to remove).
        triggerChar: string;
    }>({
        visible: false,
        position: { x: 0, y: 0 },
        suggestions: [],
        selectedIndex: 0,
        triggerStart: 0,
        query: '',
        triggerChar: '',
    });

    // Math Picker Modal State
    const [isMathPickerOpen, setIsMathPickerOpen] = useState(false);

    // Search Overlay State
    const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchScope, setSearchScope] = useState<'current' | 'all'>('current');
    const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('keyword');
    const [semanticResults, setSemanticResults] = useState<import('../types').SemanticSearchResult[]>([]);
    const [semanticLoading, setSemanticLoading] = useState(false);
    const [semanticError, setSemanticError] = useState<string | null>(null);
    const [crossResults, setCrossResults] = useState<CrossNotebookSearchResult[]>([]);
    const [crossLoading, setCrossLoading] = useState(false);
    const [crossError, setCrossError] = useState<string | null>(null);
    const crossDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const semanticDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [modalSearchHistory, setModalSearchHistory] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem('sb_modal_search_history') || '[]'); } catch { return []; } });
    const [showModalHistoryDropdown, setShowModalHistoryDropdown] = useState(false);
    const pendingModalSearchRef = useRef<string>('');
    const [searchHoverPreview, setSearchHoverPreview] = useState<{ node: MindMapNode; top: number; left: number } | null>(null);
    const [searchHoverExpanded, setSearchHoverExpanded] = useState(false);
    const searchHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [linkHoverPreview, setLinkHoverPreview] = useState<{ node: MindMapNode; top: number; left: number; notebookId?: string; notebookTitle?: string } | null>(null);
    const linkHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [linkHoverExpanded, setLinkHoverExpanded] = useState(false);
    const [pinnedPreviews, setPinnedPreviews] = useState<Array<{ id: string; node: MindMapNode; x: number; y: number; expanded: boolean; notebookId?: string }>>([]);
    const draggingPinRef = useRef<{ id: string; offsetX: number; offsetY: number } | null>(null);
    const [tagContextMenu, setTagContextMenu] = useState<{ tag: string; x: number; y: number } | null>(null);

    // "Attach related video" — a browsable gallery of every video the author has ever recorded
    // (fetched once via listAllVideos, cached for the rest of the session), not a note search.
    // Per explicit product feedback: picking a video should mean picking a video, not guessing
    // which note might have one attached.
    const [showVideoGallery, setShowVideoGallery] = useState(false);
    const [videoGalleryList, setVideoGalleryList] = useState<AllVideosEntry[] | null>(null);
    const [videoGalleryLoading, setVideoGalleryLoading] = useState(false);
    const [videoGalleryError, setVideoGalleryError] = useState<string | null>(null);
    const [videoGalleryFilter, setVideoGalleryFilter] = useState('');
    const [videoGalleryNotebookFilter, setVideoGalleryNotebookFilter] = useState(''); // '' = all notebooks
    const openVideoGallery = () => {
        setShowVideoGallery(true);
        if (videoGalleryList || videoGalleryLoading) return;
        setVideoGalleryLoading(true);
        setVideoGalleryError(null);
        listAllVideos().then(setVideoGalleryList).catch(() => {
            setVideoGalleryError(language === 'vietnamese' ? 'Không tải được danh sách video' : 'Could not load videos');
        }).finally(() => setVideoGalleryLoading(false));
    };

    // "My Courses" — a live subscription (cheap: users have few courses) drives both which
    // courses already contain this note (shown as pills) and the "add to course" picker's list.
    const [allCollections, setAllCollections] = useState<Collection[]>([]);
    const [showCoursePicker, setShowCoursePicker] = useState(false);
    const [newCourseTitle, setNewCourseTitle] = useState('');
    useEffect(() => {
        const uid = getAuth().currentUser?.uid;
        if (!uid) return;
        return subscribeCollections(uid, setAllCollections);
    }, []);
    const isThisNote = (i: { nodeId: string; notebookId: string }) => i.nodeId === node.id && i.notebookId === (currentNotebookId || '');
    const containingCollections = allCollections.filter(c => c.items.some(isThisNote));
    const currentNotebookTitle = () => allNotebooksRef.current.find(nb => nb.id === currentNotebookId)?.title || '';
    const addThisNoteTo = async (collectionId: string, items: Collection['items']) => {
        const uid = getAuth().currentUser?.uid;
        if (!uid || !currentNotebookId) return;
        await addItemToCollection(uid, collectionId, items, { nodeId: node.id, notebookId: currentNotebookId, nodeTitle: node.title || '', notebookTitle: currentNotebookTitle() });
    };
    const createCourseWithThisNote = async () => {
        const uid = getAuth().currentUser?.uid;
        const title = newCourseTitle.trim();
        if (!uid || !title) return;
        const id = await createCollection(uid, title);
        await addThisNoteTo(id, []);
        setNewCourseTitle('');
    };

    const saveModalSearchHistory = (query: string) => {
        if (!query.trim() || query.trim().length < 2) return;
        setModalSearchHistory(prev => {
            const next = [query.trim(), ...prev.filter(h => h !== query.trim())].slice(0, 8);
            localStorage.setItem('sb_modal_search_history', JSON.stringify(next));
            return next;
        });
    };

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 200);
        return () => clearTimeout(t);
    }, [searchQuery]);

    useEffect(() => {
        if (debouncedSearchQuery.trim().length >= 2) {
            pendingModalSearchRef.current = debouncedSearchQuery.trim();
        } else {
            pendingModalSearchRef.current = '';
        }
    }, [debouncedSearchQuery]);

    // Keep allNotebooks in a ref — cross-notebook stubs don't change in ways that matter for search
    // (eviction/loading events don't change stub content, only the active notebook's nodes do)
    const allNotebooksRef = useRef(allNotebooks);
    allNotebooksRef.current = allNotebooks;

    // Memoize HTML rendering — parseMarkdown can produce different KaTeX ID strings on
    // repeated calls, so we cache the result to prevent unnecessary DOM updates that
    // would clear any active text selection in the view-mode overlay.
    const parsedContentHTML = useMemo(() => parseMarkdown(localContent), [localContent]);
    const displayedContentHTML = useMemo(
        () => externalFindQuery ? highlightHtmlText(parsedContentHTML, externalFindQuery) : parsedContentHTML,
        [parsedContentHTML, externalFindQuery]
    );

    // Pre-built search index for modal search — rebuilt only when current notebook nodes change
    const modalSearchIndex = useMemo(() => {
        const entries: { id: string; notebookId: string | null; notebookTitle: string | null; titleLower: string; tagsLower: string; contentLower: string }[] = [];
        for (const n of allNodes) {
            entries.push({ id: n.id, notebookId: null, notebookTitle: null, titleLower: n.title.toLowerCase(), tagsLower: (n.tags || []).join(' ').toLowerCase(), contentLower: n.content.toLowerCase() });
        }
        for (const nb of allNotebooksRef.current) {
            if (nb.id === currentNotebookId) continue;
            for (const n of nb.nodes) {
                entries.push({ id: n.id, notebookId: nb.id, notebookTitle: nb.title, titleLower: n.title.toLowerCase(), tagsLower: (n.tags || []).join(' ').toLowerCase(), contentLower: n.content.toLowerCase() });
            }
        }
        return entries;
    }, [allNodes, currentNotebookId]);

    // Navigation History State — stores origin note so back button works across notebooks too
    type HistoryEntry = { nodeId: string; notebookId: string | null; noteTitle: string };
    const [navigationHistory, setNavigationHistory] = useState<HistoryEntry[]>([]);

    // Drawing Management State
    const [activeDrawingId, setActiveDrawingId] = useState<string | null>(null);
    const [showDrawingGallery, setShowDrawingGallery] = useState(false);

    // Line-by-Line Analysis State
    const [isLineByLineAnalyzing, setIsLineByLineAnalyzing] = useState(false);

    // Auto-show gallery if drawings exist on mount
    useEffect(() => {
        if (getAllDrawings().length > 0 && !activeDrawingId) {
            setShowDrawingGallery(true);
        }
    }, []); // Only run on mount

    // Get all drawings (legacy drawingBase64 + attachments with type='drawing')
    const getAllDrawings = () => {
        const drawings: Array<{ id: string; content: string; isLegacy?: boolean }> = [];
        
        // Legacy drawing
        if (node.drawingBase64) {
            drawings.push({ id: 'legacy', content: node.drawingBase64, isLegacy: true });
        }
        
        // New attachments
        if (node.attachments) {
            node.attachments
                .filter(att => att.type === 'drawing')
                .forEach(att => {
                    if (drawings.some(d => d.id === att.id)) return;
                    drawings.push({ id: att.id, content: att.content });
                });
        }
        
        return drawings;
    };


    // Refresh symbol cache on mount
    useEffect(() => {
        refreshSymbolCache();
    }, []);

    // Cross-link context menu
    const [linkContextMenu, setLinkContextMenu] = useState<{ x: number; y: number; nodeId: string; nodeTitle: string; notebookId?: string } | null>(null);
    const [linksMoreMenuOpen, setLinksMoreMenuOpen] = useState(false);
    const [copiedCrossLink, setCopiedCrossLink] = useState<{ nodeId: string; nodeTitle: string; notebookId?: string; notebookTitle?: string } | null>(() => {
        try { return JSON.parse(localStorage.getItem('sb_copied_crosslink') || 'null'); } catch { return null; }
    });

    useEffect(() => {
        if (!linkContextMenu) return;
        const close = () => setLinkContextMenu(null);
        window.addEventListener('click', close, { once: true });
        return () => window.removeEventListener('click', close);
    }, [linkContextMenu]);

    // Mobile: auto-switch to the "Info" tab once a full AI Check (not Quick Check) finishes
    const aiCheckTriggeredRef = useRef(false);
    const wasAnalyzingRef = useRef(node.isAnalyzing);
    useEffect(() => {
        const wasAnalyzing = wasAnalyzingRef.current;
        wasAnalyzingRef.current = node.isAnalyzing;
        if (isMobileViewport && aiCheckTriggeredRef.current && wasAnalyzing && !node.isAnalyzing && node.analysis) {
            setMobileSection('info');
            aiCheckTriggeredRef.current = false;
        }
    }, [node.isAnalyzing, node.analysis, isMobileViewport]);

    // Mobile: jump to the "Outline" tab when entering cross-link picking mode from the Info tab
    useEffect(() => {
        if (isMobileViewport && linkMode) setMobileSection('outline');
    }, [isMobileViewport, linkMode]);

    // Sync local content ONLY when switching to a different node, or when Find & Replace externally updates this node.
    // Do NOT sync during typing or when autosave runs.
    useEffect(() => {
        setLocalContent(node.content || '', { forceSnapshot: true });
        setIsEditing(false);

        if (ENABLE_RICH_TEXT_EDITOR) {
            richEditorRef.current?.setContent(node.content || '');
        }

        // Cancel any pending debounced updates when switching nodes
        if (debouncedUpdateRef.current) {
            clearTimeout(debouncedUpdateRef.current);
            debouncedUpdateRef.current = null;
        }
    // findReplaceVersion bumps when Find & Replace updates this node externally
    }, [node.id, findReplaceVersion]); // eslint-disable-line react-hooks/exhaustive-deps

    // Flush debounced updates on unmount or close
    useEffect(() => {
        return () => {
            if (debouncedUpdateRef.current) {
                clearTimeout(debouncedUpdateRef.current);
            }
        };
    }, []); // Empty deps - only run on mount/unmount

    // Resizable Sidebar State — fraction of container width so it scales with outer panel resize
    const [sidebarFraction, setSidebarFraction] = useState(0.36);
    const [isResizing, setIsResizing] = useState(false);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    // While the PDF panel is open the modal is squeezed, so the sidebar collapses to a slim rail
    // (score badge + icons); clicking the badge opens the full feedback as an overlay drawer instead
    // of a permanently cramped column. The pre-PDF open/closed state is restored when the PDF closes.
    const [showFeedbackDrawer, setShowFeedbackDrawer] = useState(false);
    const sidebarWasOpenBeforePdfRef = useRef<boolean | null>(null);
    useEffect(() => {
        if (mediaPanelOpen) {
            if (sidebarWasOpenBeforePdfRef.current === null) sidebarWasOpenBeforePdfRef.current = isSidebarOpen;
            setIsSidebarOpen(false);
        } else if (sidebarWasOpenBeforePdfRef.current !== null) {
            setIsSidebarOpen(sidebarWasOpenBeforePdfRef.current);
            sidebarWasOpenBeforePdfRef.current = null;
            setShowFeedbackDrawer(false);
        }
    }, [mediaPanelOpen]);
    const [sidebarOnLeft, setSidebarOnLeft] = useState(() => { try { return localStorage.getItem('expanded_sidebar_left') === '1'; } catch { return false; } });
    const [isFullscreen, setIsFullscreen] = useState(defaultFullscreen);
    const [showMobileToolsMenu, setShowMobileToolsMenu] = useState(false);
    const mobileToolsButtonRef = useRef<HTMLButtonElement>(null);
    const [mobileToolsMenuPos, setMobileToolsMenuPos] = useState({ top: 0, left: 0 });

    // BYOK AI Chat state
    const [showAiChat, setShowAiChat] = useState(false);
    const [aiChatMessages, setAiChatMessages] = useState<{ role: 'user' | 'assistant'; content: string; images?: string[] }[]>([]);
    const [aiChatInput, setAiChatInput] = useState('');
    const [aiChatLoading, setAiChatLoading] = useState(false);
    const [aiChatModelId, setAiChatModelId] = useState<string>(() => {
        if (userApiKeys?.gemini) return 'gemini-3.5-flash-lite';
        if (userApiKeys?.openai) return 'gpt-4o';
        return 'gemini-3.5-flash-lite';
    });
    const aiChatEndRef = useRef<HTMLDivElement>(null);
    const aiChatInputRef = useRef<HTMLTextAreaElement>(null);
    const aiChatAbortRef = useRef<AbortController | null>(null);
    const [isChatRecording, setIsChatRecording] = useState(false);
    const [isChatTranscribing, setIsChatTranscribing] = useState(false);
    const chatMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chatAudioChunksRef = useRef<Blob[]>([]);

    const [quickCheckResult, setQuickCheckResult] = useState<{ status: 'on_track' | 'caution' | 'critical'; verdict: string; confirmedPoints: string[]; nextCaution?: string | null } | null>(null);
    const [isQuickChecking, setIsQuickChecking] = useState(false);
    const [showVoiceTutor, setShowVoiceTutor] = useState(false);
    const [showVoiceTutorLive, setShowVoiceTutorLive] = useState(false);
    const [showTutorSettingsPopover, setShowTutorSettingsPopover] = useState(false);
    const [showAiMenu, setShowAiMenu] = useState(false);
    const [isTutorSpeaking, setIsTutorSpeaking] = useState(false);
    // The virtual classmate in a recording (RecordingClassmate) makes the note glow the same way.
    const [isClassmateSpeaking, setIsClassmateSpeaking] = useState(false);
    useEffect(() => {
        const on = (e: Event) => setIsClassmateSpeaking(!!(e as CustomEvent).detail);
        window.addEventListener('classmate-speaking', on);
        return () => window.removeEventListener('classmate-speaking', on);
    }, []);
    const isAnyTutorSpeaking = isTutorSpeaking || isClassmateSpeaking;
    const [highlightedImageIndex, setHighlightedImageIndex] = useState<number | null>(null);
    const highlightedImgElRef = useRef<HTMLElement | null>(null);
    const [tutorSelectedText, setTutorSelectedText] = useState('');
    const [tutorHoverLine, setTutorHoverLine] = useState('');
    const hoverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reset image highlight when navigating to a different note
    useEffect(() => { setHighlightedImageIndex(null); setIsEditing(false); }, [node.id]);

    // Capture text selection when Voice Tutor is open.
    // Use mouseup/keyup instead of selectionchange — selectionchange fires mid-double-click
    // causing state updates that re-render the HTML overlay and wipe the in-progress selection.
    // mouseup/keyup only fire after the user has finished selecting.
    useEffect(() => {
        if (!showVoiceTutor) { setTutorSelectedText(''); return; }
        const handleSelectionEnd = () => {
            const sel = window.getSelection();
            const text = sel?.toString().trim() ?? '';
            if (text.length >= 10) setTutorSelectedText(text);
            else if (text.length === 0) setTutorSelectedText('');
        };
        document.addEventListener('mouseup', handleSelectionEnd);
        document.addEventListener('keyup', handleSelectionEnd);
        return () => {
            document.removeEventListener('mouseup', handleSelectionEnd);
            document.removeEventListener('keyup', handleSelectionEnd);
        };
    }, [showVoiceTutor]);

    // Detect which paragraph/line the mouse is hovering when Voice Tutor is open (layer 3 context)
    useEffect(() => {
        if (!showVoiceTutor) { setTutorHoverLine(''); return; }
        const container = noteContentRef.current;
        if (!container) return;

        const onMove = (e: MouseEvent) => {
            if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);
            hoverDebounceRef.current = setTimeout(() => {
                const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
                if (!target || !container.contains(target)) { setTutorHoverLine(''); return; }
                // Rich text editor: find nearest block element
                const block = target.closest('p, h1, h2, h3, li, blockquote') as HTMLElement | null;
                if (block && container.contains(block)) {
                    const text = block.textContent?.trim() ?? '';
                    if (text.length >= 5) { setTutorHoverLine(text.slice(0, 500)); return; }
                }
                // Textarea fallback: estimate line by offsetY / lineHeight
                if (textareaRef.current) {
                    const ta = textareaRef.current;
                    const rect = ta.getBoundingClientRect();
                    const cs = getComputedStyle(ta);
                    const lh = parseFloat(cs.lineHeight) || 20;
                    const pt = parseFloat(cs.paddingTop) || 8;
                    const lineIdx = Math.floor((e.clientY - rect.top + ta.scrollTop - pt) / lh);
                    const lines = ta.value.split('\n');
                    if (lineIdx >= 0 && lineIdx < lines.length) {
                        const ctx = lines.slice(Math.max(0, lineIdx - 1), Math.min(lines.length, lineIdx + 2)).join('\n').trim();
                        if (ctx.length >= 5) setTutorHoverLine(ctx.slice(0, 500));
                    }
                }
            }, 350);
        };
        const onLeave = () => { if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current); setTutorHoverLine(''); };

        container.addEventListener('mousemove', onMove);
        container.addEventListener('mouseleave', onLeave);
        return () => {
            container.removeEventListener('mousemove', onMove);
            container.removeEventListener('mouseleave', onLeave);
            if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);
        };
    }, [showVoiceTutor]);

    // Track which image DOM element to glow when highlightedImageIndex changes
    useEffect(() => {
        highlightedImgElRef.current = highlightedImageIndex !== null
            ? (document.querySelector(`[data-tutor-img="${highlightedImageIndex}"]`) as HTMLElement | null)
            : null;
    }, [highlightedImageIndex]);

    useEffect(() => {
        const textEl = noteContentRef.current;
        if (!isAnyTutorSpeaking) {
            if (glowRafRef.current) cancelAnimationFrame(glowRafRef.current);
            if (textEl) textEl.style.boxShadow = '';
            if (highlightedImgElRef.current) highlightedImgElRef.current.style.boxShadow = '';
            return;
        }
        const loop = () => {
            const amp = parseFloat(document.documentElement.style.getPropertyValue('--vt-amp') || '0');
            const lvl = Math.max(0.04, amp);
            const imgEl = highlightedImgElRef.current;
            if (imgEl) {
                // Glow on image container — same formula as text area but outer (no inset)
                if (textEl) textEl.style.boxShadow = '';
                imgEl.style.boxShadow =
                    `0 0 ${(8 + lvl * 38).toFixed(0)}px ${(2 + lvl * 9).toFixed(0)}px rgba(139,92,246,${(0.08 + lvl * 0.52).toFixed(2)}),` +
                    `0 0 ${(22 + lvl * 60).toFixed(0)}px ${(5 + lvl * 20).toFixed(0)}px rgba(139,92,246,${(0.03 + lvl * 0.22).toFixed(2)})`;
            } else {
                // Glow on text area — inset
                if (textEl) textEl.style.boxShadow =
                    `inset 0 0 ${(8 + lvl * 38).toFixed(0)}px ${(2 + lvl * 9).toFixed(0)}px rgba(139,92,246,${(0.08 + lvl * 0.52).toFixed(2)}),` +
                    `inset 0 0 ${(22 + lvl * 60).toFixed(0)}px ${(5 + lvl * 20).toFixed(0)}px rgba(139,92,246,${(0.03 + lvl * 0.22).toFixed(2)})`;
            }
            glowRafRef.current = requestAnimationFrame(loop);
        };
        glowRafRef.current = requestAnimationFrame(loop);
        return () => {
            if (glowRafRef.current) cancelAnimationFrame(glowRafRef.current);
            if (textEl) textEl.style.boxShadow = '';
            if (highlightedImgElRef.current) highlightedImgElRef.current.style.boxShadow = '';
        };
    }, [isAnyTutorSpeaking, highlightedImageIndex]);
    const [isToolbarCompact, setIsToolbarCompact] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [sidebarWidth, setSidebarWidth] = useState(999);
    const [feedbackExpanded, setFeedbackExpanded] = useState(false);
    const [detailedExpanded, setDetailedExpanded] = useState(false);
    const [lineByLineExpanded, setLineByLineExpanded] = useState(false);
    const [quickCheckPinpoint, setQuickCheckPinpoint] = useState<{ isLoading: boolean; errorPoints: { quote: string; explanation: string }[] | null }>({ isLoading: false, errorPoints: null });
    const [isConvertingToWritten, setIsConvertingToWritten] = useState(false);
    const [showConvertBanner, setShowConvertBanner] = useState(false);
    const convertBannerTimerRef = useRef<NodeJS.Timeout | null>(null);

    const [showHintNudge, setShowHintNudge] = useState(false);
    const [hintResult, setHintResult] = useState<string | null>(null);
    const [isHinting, setIsHinting] = useState(false)
    const [isTranslating, setIsTranslating] = useState(false);
    const [showTranslationPopover, setShowTranslationPopover] = useState(false);
    const translationPopoverRef = useRef<HTMLDivElement>(null);
    const [isAutoReChecking, setIsAutoReChecking] = useState(false);
    const autoReCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [aiSectionFocus, setAISectionFocus] = useState<{ title: string; color: string; items: { quote?: string; text: string }[] } | null>(null);
    const hintPauseTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hintDismissedAtRef = useRef<number>(0);
    const [writingHintDismissed, setWritingHintDismissed] = useState(false);
    const [hintIndex, setHintIndex] = useState(0);
    const [hintVisible, setHintVisible] = useState(true);
    const hintHoveredRef = useRef(false);

    const FONT_SIZES = [14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 46, 50];
    const [editorFontSize, setEditorFontSizeRaw] = useState<number>(() => {
        const saved = localStorage.getItem('sb_editor_font_size');
        const n = saved ? parseInt(saved, 10) : 16;
        return FONT_SIZES.includes(n) ? n : 16;
    });
    const setEditorFontSize = (size: number) => {
        localStorage.setItem('sb_editor_font_size', String(size));
        setEditorFontSizeRaw(size);
    };

    useEffect(() => {
        if (isOpen && activeTab === 'write' && textareaRef.current && !readOnly) {
            // Small delay to allow layout to settle
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 100);
        }
    }, [isOpen, activeTab, readOnly]);

    // Reset hint state when note changes
    useEffect(() => {
        setShowHintNudge(false);
        setHintResult(null);
        setIsHinting(false);
        hintDismissedAtRef.current = 0;
        setWritingHintDismissed(false);
        setHintIndex(0);
        setHintVisible(true);
        if (hintPauseTimerRef.current) clearTimeout(hintPauseTimerRef.current);
    }, [node.id]);

    // Auto-rotate hint carousel — cycles every 4.5s when hint card is visible
    // Use boolean threshold instead of node.content to avoid restarting interval on every keystroke
    const contentIsShort = (node.content || '').trim().length < 200;
    useEffect(() => {
        const isCardVisible = isOpen && !!node.imageBase64 && contentIsShort && !writingHintDismissed && !readOnly;
        if (!isCardVisible) return;
        const fixedCount = 3;
        const poolSize = node.writingHint ? fixedCount + 1 : fixedCount;
        if (poolSize <= 1) return;
        const timer = setInterval(() => {
            if (hintHoveredRef.current) return;
            setHintVisible(false);
            setTimeout(() => {
                setHintIndex(i => i + 1);
                setHintVisible(true);
            }, 350);
        }, 4500);
        return () => clearInterval(timer);
    }, [isOpen, node.imageBase64, contentIsShort, node.writingHint, writingHintDismissed, readOnly]);

    // Re-surface writing hint card when a new writingHint is set (screenshot just inserted)
    const prevWritingHintRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (node.writingHint && node.writingHint !== prevWritingHintRef.current) {
            setWritingHintDismissed(false);
        }
        prevWritingHintRef.current = node.writingHint;
    }, [node.writingHint]);

    // Lazy auto-extraction: when a note opens without tags/title/writingHint, generate them silently
    useEffect(() => {
        if (!isOpen || readOnly) return;
        const timer = setTimeout(() => {
            const needsTags = node.imageBase64 && (!node.tags || node.tags.length === 0);
            const needsTitle = !node.title?.trim() && (node.content || node.imageBase64);
            const needsHint = node.imageBase64 && !node.writingHint && (node.content || '').trim().length < 50;
            if (needsTags) {
                triggerExtractTags(node.imageBase64!, node.imageMimeType || 'image/png');
            } else if (needsHint) {
                // Note already has tags — just generate the writing hint (not rate-limited)
                callGenerateWritingHint(node.imageBase64!, node.imageMimeType || 'image/png', language)
                    .then(hint => { if (hint) onUpdate(node.id, { writingHint: hint }); })
                    .catch(() => {});
            }
            if (needsTitle && onGenerateTitle) {
                onGenerateTitle(node.id);
            }
        }, 1500);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, node.id]);

    // Pause detection for "Cần gợi ý?" hint chip
    // Use boolean threshold instead of node.content to avoid resetting 90s timer on every keystroke
    const contentHasEnough = (node.content || '').trim().length > 30;
    useEffect(() => {
        const hasSource = !!node.imageBase64;
        const isWriting = isOpen && activeTab === 'write' && !readOnly && isEditing;
        const recentlyDismissed = Date.now() - hintDismissedAtRef.current < 3 * 60 * 1000;

        if (hintPauseTimerRef.current) clearTimeout(hintPauseTimerRef.current);

        if (!isWriting || !hasSource || !contentHasEnough || showHintNudge || hintResult || recentlyDismissed) return;

        hintPauseTimerRef.current = setTimeout(() => {
            setShowHintNudge(true);
        }, 90_000);

        return () => {
            if (hintPauseTimerRef.current) clearTimeout(hintPauseTimerRef.current);
        };
    }, [contentHasEnough, node.imageBase64, isOpen, activeTab, readOnly, isEditing, showHintNudge, hintResult]);

    // Close autocomplete when switching modes or tabs
    useEffect(() => {
        if (autocomplete.visible) {
            setAutocomplete(prev => ({ ...prev, visible: false }));
        }
    }, [isEditing, activeTab]);

    // Close autocomplete when clicking outside
    useEffect(() => {
        if (!autocomplete.visible) return;

        const handleClickOutside = (e: MouseEvent) => {
            // The menu itself stops propagation, so any click reaching here is "outside"
            setAutocomplete(prev => ({ ...prev, visible: false }));
        };

        window.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, [autocomplete.visible]);



    // Resize Logic
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            const containerWidth = contentContainerRef.current?.clientWidth ?? 800;
            // When sidebar is on right: drag left → sidebar grows. When on left: drag right → sidebar grows.
            const delta = sidebarOnLeft ? -e.movementX : e.movementX;
            setSidebarFraction(prev => {
                const newWidth = prev * containerWidth - delta;
                // Keep sidebar between 200px and 70% of container
                const clamped = Math.max(200, Math.min(newWidth, containerWidth * 0.70));
                return clamped / containerWidth;
            });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, sidebarOnLeft]);

    // Global drag listeners for pinned preview windows
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (!draggingPinRef.current) return;
            const { id, offsetX, offsetY } = draggingPinRef.current;
            setPinnedPreviews(prev => prev.map(p => p.id === id ? { ...p, x: e.clientX - offsetX, y: e.clientY - offsetY } : p));
        };
        const onUp = () => { draggingPinRef.current = null; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, []);



    const triggerExtractTags = async (imageBase64: string, mimeType: string) => {
        if (readOnly || isExtractingTags) return;
        setIsExtractingTags(true);
        try {
            const { tags: newTags, title, hint } = await extractImageHashtags([{ base64: imageBase64, mimeType }], language);
            const updates: Record<string, unknown> = {};
            if (newTags.length > 0) {
                updates.tags = Array.from(new Set([...(node.tags || []), ...newTags]));
            }
            if (title && !node.title?.trim()) {
                updates.title = title;
                updates.titleSource = 'ai';
            }
            if (hint && !node.writingHint) {
                updates.writingHint = hint;
            }
            if (Object.keys(updates).length > 0) onUpdate(node.id, updates);
        } finally {
            setIsExtractingTags(false);
        }
    };

    const processImageFile = (file: File) => {
        if (readOnly || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result as string;
            if (node.imageBase64) {
                const newAttachment: NodeAttachment = {
                    id: Math.random().toString(36).substring(7),
                    type: 'image',
                    content: base64,
                    mimeType: file.type,
                    createdAt: Date.now()
                };
                onUpdate(node.id, { attachments: [...(node.attachments || []), newAttachment] });
            } else {
                onUpdate(node.id, { imageBase64: base64, imageMimeType: file.type });
            }
            triggerExtractTags(base64, file.type);
        };
        reader.readAsDataURL(file);
    };

    const handleModalDragEnter = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); if (!readOnly) setIsDragActive(true); };
    const handleModalDragLeave = (e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIsDragActive(false);
    };
    const handleModalDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
    const handleModalDrop = (e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragActive(false);
        if (!readOnly && e.dataTransfer.files?.[0]) processImageFile(e.dataTransfer.files[0]);
    };

    // Compact toolbar when modal is too narrow (e.g. PDF viewer open alongside)
    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setIsToolbarCompact(entry.contentRect.width < 680);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Narrow sidebar: adapt UI density when sidebar is squeezed by PDF panel
    useEffect(() => {
        const el = sidebarRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setSidebarWidth(entry.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const handleFindKey = (e: KeyboardEvent) => {
            // Plain Ctrl+F only: Ctrl+Shift+F is the notebook-wide search (App.tsx).
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && typeof e.key === 'string' && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                posthog.capture('note_feature_used', { feature: 'find_replace' });
                setShowFindReplace(true);
                setIsEditing(true);
                setTimeout(() => findBarInputRef.current?.focus(), 80);
            }
        };
        window.addEventListener('keydown', handleFindKey);
        // Lets other Ctrl+F handlers (the PDF viewer behind this modal) know the shortcut is taken.
        document.body.dataset.expandedNoteOpen = '1';
        return () => { window.removeEventListener('keydown', handleFindKey); delete document.body.dataset.expandedNoteOpen; };
    }, []);

    useEffect(() => {
        if (!showTranslationPopover) return;
        const handler = (e: MouseEvent) => {
            if (translationPopoverRef.current && !translationPopoverRef.current.contains(e.target as Node)) {
                setShowTranslationPopover(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showTranslationPopover]);

    // Lazy re-check: silently upgrade legacy / stale checks when note is opened
    useEffect(() => {
        if (readOnly || !currentNotebookId || !node.content?.trim()) return;
        if (node.isAnalyzing || isAutoReChecking) return;

        // Auto-check if: no analysis at all, OR has old-schema analysis (no aiCheckVersion).
        // Notes with aiCheckVersion='v2' are up-to-date — skip.
        const needsCheck = !node.analysis || !node.aiCheckVersion;
        if (!needsCheck) return;
        // Max one auto-check per note per session to avoid repeat firing
        if (autoCheckedThisSession.has(node.id)) return;

        autoReCheckTimerRef.current = setTimeout(async () => {
            if (node.isAnalyzing) return;
            autoCheckedThisSession.add(node.id);
            setIsAutoReChecking(true);
            try {
                await onAnalyze(node.id);
            } catch { /* silent */ } finally {
                setIsAutoReChecking(false);
            }
        }, 3000);

        return () => {
            if (autoReCheckTimerRef.current) clearTimeout(autoReCheckTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.id]);

    useEffect(() => {
        if (!showFindReplace || !findQuery) {
            setTotalMatches(0);
            setMatchIndex(0);
            return;
        }
        const count = richEditorRef.current?.findMatches(findQuery, matchCase) ?? 0;
        setTotalMatches(count);
        setMatchIndex(0);
        if (count > 0) {
            richEditorRef.current?.goToMatch(findQuery, 0, matchCase);
            requestAnimationFrame(() => findBarInputRef.current?.focus());
        }
    }, [findQuery, showFindReplace, matchCase]);


    const handleEnterEdit = () => {
        if (readOnly) return;
        // Capture view scroll ratio before switching — editor mounts fresh and we sync it after.
        const container = noteContentRef.current;
        const viewRange = container ? container.scrollHeight - container.clientHeight : 0;
        const ratio = viewRange > 0 ? (container!.scrollTop / viewRange) : 0;
        setIsEditing(true);
        // Double rAF: first frame renders the editor DOM, second frame has reliable dimensions.
        requestAnimationFrame(() => requestAnimationFrame(() => {
            richEditorRef.current?.focus();
            const editorScrollEl = richEditorRef.current?.getScrollElement?.();
            if (editorScrollEl) {
                const editorRange = editorScrollEl.scrollHeight - editorScrollEl.clientHeight;
                if (editorRange > 0) editorScrollEl.scrollTop = ratio * editorRange;
            }
        }));
    };

    const handleDone = () => {
        // Sync view scroll to match editor position before switching back to view mode.
        const editorScrollEl = richEditorRef.current?.getScrollElement?.();
        const editorRange = editorScrollEl ? editorScrollEl.scrollHeight - editorScrollEl.clientHeight : 0;
        const ratio = editorRange > 0 ? (editorScrollEl!.scrollTop / editorRange) : 0;
        setIsEditing(false);
        requestAnimationFrame(() => {
            const container = noteContentRef.current;
            if (!container) return;
            const viewRange = container.scrollHeight - container.clientHeight;
            if (viewRange > 0) container.scrollTop = ratio * viewRange;
        });
    };

    const handleQuickCheck = async () => {
        if (isQuickChecking) return;
        setIsQuickChecking(true);
        setQuickCheckResult(null);
        setQuickCheckPinpoint({ isLoading: false, errorPoints: null });
        // Flush latest editor content in case localContent is stale (RichTextEditor debounce not yet fired)
        const contentToCheck = ENABLE_RICH_TEXT_EDITOR && richEditorRef.current
            ? richEditorRef.current.getMarkdown()
            : localContent;
        try {
            const result = await quickCheckNote(contentToCheck, node.imageBase64 ?? null, node.imageMimeType ?? null, language);
            setQuickCheckResult(result);
        } catch {
            setQuickCheckResult({ status: 'caution', verdict: language === 'vietnamese' ? 'Không thể kiểm tra lúc này, thử lại sau.' : 'Could not check right now, please try again.', confirmedPoints: [], nextCaution: null });
        } finally {
            setIsQuickChecking(false);
        }
    };

    const handlePinpointFromQuickCheck = async () => {
        if (!quickCheckResult || quickCheckPinpoint.isLoading) return;
        const existingFeedback = [quickCheckResult.verdict, quickCheckResult.nextCaution].filter(Boolean).join(' ');
        setQuickCheckPinpoint({ isLoading: true, errorPoints: null });
        try {
            const result = await pinpointError(localContent, node.imageBase64 ?? null, node.imageMimeType ?? null, existingFeedback, language);
            setQuickCheckPinpoint({ isLoading: false, errorPoints: result.errorPoints });
        } catch {
            setQuickCheckPinpoint({ isLoading: false, errorPoints: [] });
        }
    };

    const handleConvertToWritten = async () => {
        if (!localContent?.trim() || isConvertingToWritten) return;
        setIsConvertingToWritten(true);
        setShowConvertBanner(false);
        try {
            const converted = await convertSpokenToWritten(localContent, language);
            setLocalContent(converted, { forceSnapshot: true });
            if (ENABLE_RICH_TEXT_EDITOR) richEditorRef.current?.setContent(converted);
            if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
            debouncedUpdateRef.current = setTimeout(() => {
                onUpdate(node.id, { content: converted });
            }, 100);
            // Show undo hint banner for 6 seconds
            setShowConvertBanner(true);
            if (convertBannerTimerRef.current) clearTimeout(convertBannerTimerRef.current);
            convertBannerTimerRef.current = setTimeout(() => setShowConvertBanner(false), 6000);
        } catch {
            // silently fail — user still has original content
        } finally {
            setIsConvertingToWritten(false);
        }
    };

    const handleInsertMarkdown = (prefix: string, suffix: string) => {
        if (ENABLE_RICH_TEXT_EDITOR && richEditorRef.current) {
            if (prefix === '**' && suffix === '**') richEditorRef.current.toggleBold();
            else if (prefix === '~~' && suffix === '~~') richEditorRef.current.toggleUnderline();
            else if (prefix === '-\n' || prefix === '- ') richEditorRef.current.toggleBulletList();
            else richEditorRef.current.insertText(prefix + suffix);
            return;
        }
        if (!textareaRef.current || readOnly) return;
        const newText = insertMarkdown(textareaRef.current, prefix, suffix);
        if (newText !== null) {
            setLocalContent(newText, { forceSnapshot: true });
            onUpdate(node.id, { content: newText });
            textareaRef.current.focus();
        }
    };

    // Toggle text case: UPPER ↔ lower
    const handleToggleCase = () => {
        if (readOnly) return;
        if (ENABLE_RICH_TEXT_EDITOR && richEditorRef.current) {
            const selected = richEditorRef.current.getSelectedText();
            if (!selected) return;
            const isAllUpper = selected === selected.toUpperCase();
            richEditorRef.current.transformSelectionText(
                t => isAllUpper ? t.toLowerCase() : t.toUpperCase()
            );
            return;
        }
        if (!textareaRef.current) return;
        const ta = textareaRef.current;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        if (start === end) return;
        const selected = ta.value.substring(start, end);
        const transformed = selected === selected.toUpperCase()
            ? selected.toLowerCase()
            : selected.toUpperCase();
        const newText = ta.value.substring(0, start) + transformed + ta.value.substring(end);
        setLocalContent(newText, { forceSnapshot: true });
        onUpdate(node.id, { content: newText });
        setTimeout(() => { ta.focus(); ta.setSelectionRange(start, start + transformed.length); }, 0);
    };

    const handleReflowSelection = () => {
        if (readOnly) return;
        if (ENABLE_RICH_TEXT_EDITOR && richEditorRef.current) {
            richEditorRef.current.reflowSelection();
            return;
        }
        if (!textareaRef.current) return;
        const ta = textareaRef.current;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        if (start === end) return;
        const reflowed = reflowText(ta.value.substring(start, end));
        const newText = ta.value.substring(0, start) + reflowed + ta.value.substring(end);
        setLocalContent(newText, { forceSnapshot: true });
        onUpdate(node.id, { content: newText });
        setTimeout(() => { ta.focus(); ta.setSelectionRange(start, start + reflowed.length); }, 0);
    };

    const handleInsertQuestion = (question: string) => {
        if (!textareaRef.current || readOnly) return;
        const qText = `**Q: ${question}**\n\n`;

        const textarea = textareaRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const before = text.substring(0, start);
        const after = text.substring(end);

        const newContent = `${before}${qText}${after}`;
        setLocalContent(newContent, { forceSnapshot: true });
        onUpdate(node.id, { content: newContent });

        setTimeout(() => {
            textarea.focus();
            // Move cursor after inserted text
            const newCursorPos = start + qText.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    // Transcribe audio blob (extracted for retry functionality)
    const transcribeAudioBlob = async (audioBlob: Blob) => {
        setIsTranscribing(true);
        setHasTranscriptionError(false);

        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
            const base64String = reader.result as string;
            const cleanBase64 = base64String.split(',')[1];
            try {
                const refinedText = await transcribeAndRefineAudio(cleanBase64, language);
                if (refinedText) {
                    const hasExisting = !!(localContent?.trim());
                    const newContent = hasExisting ? `${localContent}\n\n${refinedText}` : refinedText;
                    setLocalContent(newContent, { forceSnapshot: true });
                    onUpdate(node.id, { content: newContent });
                    if (ENABLE_RICH_TEXT_EDITOR) {
                        // Use setContent when note is empty to avoid leading blank lines
                        // from appendText's "\n\n" prefix on an empty document
                        if (hasExisting) {
                            richEditorRef.current?.appendText(refinedText);
                        } else {
                            richEditorRef.current?.setContent(refinedText);
                        }
                    }
                    savedAudioBlobRef.current = null;
                    setHasTranscriptionError(false);
                }
            } catch (err) {
                console.error("Transcription failed", err);
                setHasTranscriptionError(true);
                // Keep the audio blob for retry
            } finally {
                setIsTranscribing(false);
            }
        };
    };

    // Retry transcription with saved audio
    const handleRetryTranscription = () => {
        if (savedAudioBlobRef.current) {
            transcribeAudioBlob(savedAudioBlobRef.current);
        }
    };

    // Cancel voice input and restore content to before recording started
    const handleCancelVoice = () => {
        if (isListening && recognitionRef.current) {
            recognitionRef.current.onend = null; // prevent save logic in onend
            recognitionRef.current.abort();
            setIsListening(false);
            const restored = baseContentBeforeRecordingRef.current;
            setLocalContent(restored);
            onUpdate(node.id, { content: restored });
            if (ENABLE_RICH_TEXT_EDITOR) richEditorRef.current?.setContent(restored);
            finalTranscriptRef.current = '';
            interimTranscriptRef.current = '';
        }
        if (isRecording && mediaRecorderRef.current) {
            stopRecordingTimer();
            const mr = mediaRecorderRef.current;
            // Override onstop to just clean up without transcribing
            mr.onstop = () => {
                mediaStreamRef.current?.getTracks().forEach(t => t.stop());
                mediaStreamRef.current = null;
            };
            if (mr.state === 'recording') mr.stop();
            setIsRecording(false);
            audioChunksRef.current = [];
            savedAudioBlobRef.current = null;
        }
    };

    const stopRecordingTimer = () => {
        if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        setRecordingSeconds(0);
    };

    // Legacy Audio Recording (Upload to Gemini)
    const handleLegacyRecording = async () => {
        if (readOnly) return;
        if (isRecording) {
            stopRecordingTimer();
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
        } else {
            try {
                baseContentBeforeRecordingRef.current = localContent || '';
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaStreamRef.current = stream;

                // Pick the best supported audio format
                const mimeType = ['audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
                    .find(t => MediaRecorder.isTypeSupported(t)) || '';
                const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];

                mediaRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) audioChunksRef.current.push(event.data);
                };

                mediaRecorder.onstop = async () => {
                    stopRecordingTimer();
                    const blobType = mediaRecorder.mimeType || mimeType || 'audio/webm';
                    const audioBlob = new Blob(audioChunksRef.current, { type: blobType });
                    savedAudioBlobRef.current = audioBlob;
                    await transcribeAudioBlob(audioBlob);
                    stream.getTracks().forEach(track => track.stop());
                    mediaStreamRef.current = null;
                };

                mediaRecorder.start();
                setIsRecording(true);
                setRecordingSeconds(0);

                // Auto-stop timer
                recordingTimerRef.current = setInterval(() => {
                    setRecordingSeconds(prev => {
                        const next = prev + 1;
                        if (next >= MAX_RECORDING_SECONDS) {
                            // Auto-stop: trigger normal stop flow
                            if (mediaRecorderRef.current?.state === 'recording') {
                                mediaRecorderRef.current.stop();
                            }
                            setIsRecording(false);
                        }
                        return next;
                    });
                }, 1000);
            } catch (err) {
                console.error("Mic error", err);
                alert('Cannot access microphone.');
            }
        }
    };

    // Real-time Dictation (Web Speech API)
    const handleRealTimeDictation = () => {
        if (readOnly) return;

        if (isListening) {
            // Stop listening
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            // Logic handled in onend
        } else {
            // Start listening
            try {
                // @ts-ignore - Web Speech API may not have full TypeScript support
                const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

                if (!SpeechRecognition) {
                    alert(language === 'vietnamese'
                        ? 'Trình duyệt không hỗ trợ chuyển giọng nói thành văn bản. Vui lòng dùng Chrome hoặc Edge.'
                        : 'Speech recognition not supported. Please use Chrome or Edge.');
                    return;
                }

                const recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = language === 'vietnamese' ? 'vi-VN' : 'en-US';

                // Reset refs on start
                finalTranscriptRef.current = '';
                interimTranscriptRef.current = '';

                // Capture the base content at start of recording session
                baseContentBeforeRecordingRef.current = localContent || '';
                const baseContent = baseContentBeforeRecordingRef.current;

                recognition.onresult = (event: any) => {
                    let newInterim = '';
                    let hasNewFinal = false;

                    for (let i = event.resultIndex; i < event.results.length; i++) {
                        const transcript = event.results[i][0].transcript;
                        if (event.results[i].isFinal) {
                            finalTranscriptRef.current += transcript + ' ';
                            hasNewFinal = true;
                        } else {
                            newInterim += transcript;
                        }
                    }
                    
                    interimTranscriptRef.current = newInterim;

                    // ALWAYS update UI to show everything (Base + Final + Interim)
                    const fullDisplayText = baseContent + (baseContent && !baseContent.endsWith('\n') ? ' ' : '') + finalTranscriptRef.current + interimTranscriptRef.current;
                    setLocalContent(fullDisplayText);
                    if (ENABLE_RICH_TEXT_EDITOR) richEditorRef.current?.setContent(fullDisplayText);

                    // If we have new FINAL text, save to DB immediately to be safe
                    if (hasNewFinal) {
                        const textToSave = baseContent + (baseContent && !baseContent.endsWith('\n') ? ' ' : '') + finalTranscriptRef.current;
                        // Use onUpdate to persist valid checked data
                        onUpdate(node.id, { content: textToSave });
                    }
                };

                recognition.onerror = (event: any) => {
                    console.error('Speech recognition error:', event.error);
                    if (event.error !== 'no-speech') {
                         setIsListening(false);
                         if (event.error !== 'aborted') {
                             const errorMessages: Record<string, { vi: string; en: string }> = {
                                'not-allowed': {
                                    vi: 'Vui lòng cấp quyền sử dụng microphone.',
                                    en: 'Please grant microphone permission.'
                                },
                                'network': {
                                    vi: 'Lỗi mạng. Vui lòng kiểm tra kết nối.',
                                    en: 'Network error. Please check your connection.'
                                }
                            };
                            const message = errorMessages[event.error]?.[language === 'vietnamese' ? 'vi' : 'en']
                                || (language === 'vietnamese' ? 'Lỗi nhận diện giọng nói.' : 'Speech recognition error.');
                            alert(message);
                         }
                    }
                };

                recognition.onend = () => {
                    setIsListening(false);
                    
                    // CRITICAL FIX: When stopping, save EVERYTHING including any leftover interim text
                    const allText = finalTranscriptRef.current + interimTranscriptRef.current;
                    if (allText.trim()) {
                        const finalText = baseContent + (baseContent && !baseContent.endsWith('\n') ? ' ' : '') + allText;
                        setLocalContent(finalText, { forceSnapshot: true });
                        onUpdate(node.id, { content: finalText });
                    }
                    
                    // Cleanup refs
                    finalTranscriptRef.current = '';
                    interimTranscriptRef.current = '';
                };

                recognitionRef.current = recognition;
                recognition.start();
                setIsListening(true);

            } catch (err) {
                console.error('Speech recognition initialization error:', err);
                alert(language === 'vietnamese'
                    ? 'Không thể khởi động chuyển giọng nói thành văn bản.'
                    : 'Failed to start speech recognition.');
            }
        }
    };

    // Main recording handler (routes to appropriate mode)
    const handleVoiceRecording = () => {
        if (useRealTimeDictation) {
            handleRealTimeDictation();
        } else {
            handleLegacyRecording();
        }
    };

    const handleClose = () => {
        // Flush any pending debounced updates before closing
        if (debouncedUpdateRef.current) {
            clearTimeout(debouncedUpdateRef.current);
            onUpdate(node.id, { content: localContent });
        }

        // Warn user if there's unsent audio from failed transcription
        if (hasTranscriptionError && savedAudioBlobRef.current) {
            const confirmMessage = language === 'vietnamese'
                ? 'Bạn có đoạn ghi âm chưa gửi lên AI. Đóng modal sẽ mất dữ liệu này. Bạn có chắc muốn đóng?'
                : 'You have an unsent audio recording. Closing will discard this data. Are you sure?';

            if (!window.confirm(confirmMessage)) {
                return; // User chose to stay
            }
            // User confirmed: clear the saved audio and close
            savedAudioBlobRef.current = null;
            setHasTranscriptionError(false);
        }
        onClose();
    };

    // Math Autocomplete Handlers
    const handleTextChange = (newText: string) => {
        // Skip if we're currently selecting from autocomplete
        if (isSelectingRef.current) {
            return;
        }

        // Update local state immediately for smooth typing
        setLocalContent(newText);

        // Debounce the parent update
        if (debouncedUpdateRef.current) {
            clearTimeout(debouncedUpdateRef.current);
        }
        debouncedUpdateRef.current = setTimeout(() => {
            onUpdate(node.id, { content: newText });
        }, 400);

        // Auto-search removed - now triggered manually via Cmd/Ctrl+Space
    };

    // Trigger autocomplete manually (called by Cmd/Ctrl+Space, or by the mobile `\` trigger with prefixChar='\\')
    const triggerAutocomplete = (prefixChar: string = '') => {
        if (ENABLE_RICH_TEXT_EDITOR && richEditorRef.current) {
            const textBeforeCursor = richEditorRef.current.getTextBeforeCursor();
            const wordMatch = textBeforeCursor.match(/\b(\w+)$/);
            const query = wordMatch ? wordMatch[1] : '';
            const suggestions = query ? searchMathSymbols(query, getSymbolUsages()) : getAllSymbols();
            if (suggestions.length > 0) {
                const coords = richEditorRef.current.getCursorScreenCoords();
                setAutocomplete({
                    visible: true,
                    position: { x: coords.x, y: coords.y + 4 },
                    suggestions,
                    selectedIndex: 0,
                    triggerStart: textBeforeCursor.length - query.length - prefixChar.length,
                    query,
                    triggerChar: prefixChar,
                });
            }
            return;
        }

        if (!textareaRef.current || !isEditing) return;

        const textarea = textareaRef.current;
        const cursorPos = textarea.selectionStart;
        const text = textarea.value;
        const textBeforeCursor = text.substring(0, cursorPos);

        // Find the last word before cursor
        const wordMatch = textBeforeCursor.match(/\b(\w+)$/);

        // Use matched word or empty string if triggered manually
        const query = wordMatch ? wordMatch[1] : '';
        const suggestions = query ? searchMathSymbols(query, getSymbolUsages()) : getAllSymbols();

        if (suggestions.length > 0) {
            // Use accurate caret coordinate detection
            const caretCoords = getCaretCoordinates(textarea, cursorPos);

            setAutocomplete({
                visible: true,
                position: {
                    x: caretCoords.x,
                    y: caretCoords.y + 24 // Offset below cursor (line height)
                },
                suggestions,
                selectedIndex: 0,
                triggerStart: cursorPos - query.length,
                triggerChar: '',
                query,
            });
        }
    };

    // Re-filter the already-open autocomplete menu as the user keeps typing after the trigger
    // (e.g. `\` then "lam" narrows the list to Lambda) — or close it if they typed past the
    // trigger context (a space, punctuation, moved the cursor away, deleted the trigger char).
    const refreshAutocompleteFilter = () => {
        if (!ENABLE_RICH_TEXT_EDITOR || !richEditorRef.current) return;
        setAutocomplete(prev => {
            if (!prev.visible) return prev;
            const textBeforeCursor = richEditorRef.current!.getTextBeforeCursor();
            const sinceTrigger = textBeforeCursor.slice(prev.triggerStart);
            if (prev.triggerChar && !sinceTrigger.startsWith(prev.triggerChar)) {
                return { ...prev, visible: false };
            }
            const query = sinceTrigger.slice(prev.triggerChar.length);
            if (!/^\w*$/.test(query)) {
                return { ...prev, visible: false };
            }
            const suggestions = query ? searchMathSymbols(query, getSymbolUsages()) : getAllSymbols();
            if (suggestions.length === 0) {
                return { ...prev, visible: false };
            }
            return { ...prev, query, suggestions, selectedIndex: 0 };
        });
    };

    const handleAutocompleteSelect = (symbol: MathSymbol) => {
        // Prevent duplicate calls
        if (isSelectingRef.current) {
            return;
        }
        isSelectingRef.current = true;
        recordSymbolUsage(symbolKey(symbol));

        if (ENABLE_RICH_TEXT_EDITOR && richEditorRef.current) {
            richEditorRef.current.replaceWordBeforeCursor(autocomplete.triggerChar + autocomplete.query, symbol.unicode);
            setAutocomplete(prev => ({ ...prev, visible: false }));
            isSelectingRef.current = false;
            return;
        }

        if (!textareaRef.current) {
            isSelectingRef.current = false;
            return;
        }

        const { triggerStart, query } = autocomplete;
        // IMPORTANT: Use textarea.value (DOM) instead of localContent (state may be stale)
        const currentText = textareaRef.current.value;

        // Calculate end position of trigger word
        const triggerEnd = triggerStart + query.length;
        // Replace trigger word with symbol
        const before = currentText.substring(0, triggerStart);
        const after = currentText.substring(triggerEnd);
        const newText = `${before}${symbol.unicode}${after}`;

        // CRITICAL: Update textarea value DIRECTLY first (DOM update)
        textareaRef.current.value = newText;

        // Then update React state
        setLocalContent(newText, { forceSnapshot: true });

        // Use debounced update for consistency (but with shorter delay)
        if (debouncedUpdateRef.current) {
            clearTimeout(debouncedUpdateRef.current);
        }
        debouncedUpdateRef.current = setTimeout(() => {
            onUpdate(node.id, { content: newText });
        }, 100); // Shorter delay for autocomplete

        // Close autocomplete
        setAutocomplete(prev => ({ ...prev, visible: false }));

        // Set cursor after inserted symbol
        const newPos = triggerStart + symbol.unicode.length;
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.setSelectionRange(newPos, newPos);
                textareaRef.current.focus();
            }
            // Reset guard after selection is complete
            isSelectingRef.current = false;
        }, 0);
    };
    
    // LINE-BY-LINE ANALYSIS HANDLER (NEW)
    const handleTranslateToggle = async () => {
        if (readOnly || !currentNotebookId) return;
        // If already has translation → show popover preview, don't delete
        if (node.translateToEnglish && node.englishTranslation) {
            setShowTranslationPopover(p => !p);
            return;
        }
        // Trigger translation now
        if (!node.content?.trim()) return;
        setIsTranslating(true);
        try {
            // Translate note content (CF saves to Firestore automatically)
            const contentTranslation = await translateNoteToEnglish(currentNotebookId, node.id, node.content);
            // Translate AI feedback if exists (skipFirestore — saved via onUpdate below)
            let englishAnalysisFeedback: string | undefined;
            if (node.analysis?.feedback) {
                const a = node.analysis;
                const feedbackParts: string[] = [];
                feedbackParts.push(`Summary: ${a.feedback}`);
                if ((a.criticalIssues?.length ?? 0) > 0) {
                    feedbackParts.push('\nCritical issues:');
                    a.criticalIssues!.forEach((item, i) => feedbackParts.push(`${i + 1}. ${item.quote ? `"${item.quote}" — ` : ''}${item.explanation}`));
                }
                if ((a.minorIssues?.length ?? 0) > 0) {
                    feedbackParts.push('\nMinor issues:');
                    a.minorIssues!.forEach((item, i) => feedbackParts.push(`${i + 1}. ${item.quote ? `"${item.quote}" — ` : ''}${item.explanation}`));
                }
                if ((a.strengths?.length ?? 0) > 0) {
                    feedbackParts.push('\nStrengths:');
                    a.strengths!.forEach(s => feedbackParts.push(`• ${s}`));
                }
                if ((a.nuances?.length ?? 0) > 0) {
                    feedbackParts.push('\nDeeper notes:');
                    a.nuances!.forEach(n => feedbackParts.push(`• ${n}`));
                }
                try {
                    englishAnalysisFeedback = await translateText(feedbackParts.join('\n'));
                } catch { /* non-critical */ }
            }
            onUpdate(node.id, { translateToEnglish: true, englishTranslation: contentTranslation, englishAnalysisFeedback });
        } catch (e) {
            console.error('Translation failed', e);
        } finally {
            setIsTranslating(false);
        }
    };

    const handleLineByLineAnalysis = async () => {
        if (!node.content || readOnly) return;

        setIsLineByLineAnalyzing(true);
        
        try {
            const result = await analyzeLineByLine(
                node.content,
                node.imageBase64 || null,
                node.imageMimeType || null,
                language,
                node.attachments || []
            );
            
            // Persist to database
            onUpdate(node.id, {
                analysis: {
                    ...node.analysis!,
                    lineByLineFeedback: result.lineByLineFeedback
                }
            });
        } catch (error) {
            console.error('Line-by-line analysis failed:', error);
            // Could show toast/alert here if available
        } finally {
            setIsLineByLineAnalyzing(false);
        }
    };

    const handleInsertSymbolFromPicker = (symbol: MathSymbol) => {
        if (ENABLE_RICH_TEXT_EDITOR && richEditorRef.current) {
            richEditorRef.current.insertText(symbol.unicode);
            return;
        }
        if (!textareaRef.current) {
            return;
        }

        const textarea = textareaRef.current;
        // Use saved selection if available (since modal might have stolen focus)
        const start = lastSelectionRef.current?.start ?? textarea.selectionStart;
        const end = lastSelectionRef.current?.end ?? textarea.selectionEnd;
        const currentText = textarea.value;

        const before = currentText.substring(0, start);
        const after = currentText.substring(end);
        const newText = `${before}${symbol.unicode}${after}`;

        textarea.value = newText;
        setLocalContent(newText, { forceSnapshot: true });

        if (debouncedUpdateRef.current) {
            clearTimeout(debouncedUpdateRef.current);
        }
        debouncedUpdateRef.current = setTimeout(() => {
            onUpdate(node.id, { content: newText });
        }, 100);

        const newPos = start + symbol.unicode.length;
        setTimeout(() => {
            textarea.setSelectionRange(newPos, newPos);
            textarea.focus();
        }, 0);
    };

    const handleAutocompleteKeyDown = (e: React.KeyboardEvent) => {
        if (!autocomplete.visible) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setAutocomplete(prev => ({
                ...prev,
                selectedIndex: Math.min(prev.selectedIndex + 1, prev.suggestions.length - 1)
            }));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setAutocomplete(prev => ({
                ...prev,
                selectedIndex: Math.max(prev.selectedIndex - 1, 0)
            }));
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            // Close autocomplete when user moves cursor horizontally
            setAutocomplete(prev => ({ ...prev, visible: false }));
        } else if (e.key === 'Enter') {
            if (autocomplete.visible && autocomplete.suggestions.length > 0) {
                e.preventDefault();
                e.stopPropagation(); // Stop event from bubbling
                const selected = autocomplete.suggestions[autocomplete.selectedIndex];
                if (selected) {
                    handleAutocompleteSelect(selected);
                }
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setAutocomplete(prev => ({ ...prev, visible: false }));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (readOnly) return;

        // Handle autocomplete navigation first (if visible)
        if (autocomplete.visible) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setAutocomplete(prev => ({
                    ...prev,
                    selectedIndex: Math.min(prev.selectedIndex + 1, prev.suggestions.length - 1)
                }));
                return;
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setAutocomplete(prev => ({
                    ...prev,
                    selectedIndex: Math.max(prev.selectedIndex - 1, 0)
                }));
                return;
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                // Close autocomplete when user moves cursor horizontally
                setAutocomplete(prev => ({ ...prev, visible: false }));
                return;
            } else if (e.key === 'Enter') {
                if (autocomplete.suggestions.length > 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    const selected = autocomplete.suggestions[autocomplete.selectedIndex];
                    if (selected) {
                        handleAutocompleteSelect(selected);
                    }
                    return;
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setAutocomplete(prev => ({ ...prev, visible: false }));
                return;
            }
        }

        // Trigger math autocomplete (Ctrl+Space, Option+Space, or Cmd/Ctrl+K)
        if ((e.ctrlKey && e.key === ' ') ||           // Ctrl+Space
            (e.altKey && e.key === ' ') ||            // Option+Space (Mac-friendly)
            ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k')) { // Cmd/Ctrl+K
            e.preventDefault();
            triggerAutocomplete();
            return;
        }

        // Undo/redo (Ctrl+Z, Ctrl+Shift+Z) is no longer handled here — it's registered
        // with the centralized undo arbiter (hooks/useUndoArbiter.ts, §5.4) above, which
        // owns the single window keydown listener for this. Handling it here too would
        // reproduce this incident's original bug class of two listeners racing the same
        // keypress (§5.4.2).

        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
            e.preventDefault();
            handleInsertMarkdown('**', '**');
        }

        // Outline panel toggle: Ctrl+\
        if ((e.ctrlKey || e.metaKey) && e.key === '\\') {
            e.preventDefault();
            if (!isMobileViewport) onToggleOutline?.();
            return;
        }

        // Font size: Ctrl+Shift+] (increase) / Ctrl+Shift+[ (decrease)
        if (e.ctrlKey && e.shiftKey && e.code === 'BracketRight') {
            e.preventDefault();
            const i = FONT_SIZES.indexOf(editorFontSize);
            if (i < FONT_SIZES.length - 1) setEditorFontSize(FONT_SIZES[i + 1]);
            return;
        }
        if (e.ctrlKey && e.shiftKey && e.code === 'BracketLeft') {
            e.preventDefault();
            const i = FONT_SIZES.indexOf(editorFontSize);
            if (i > 0) setEditorFontSize(FONT_SIZES[i - 1]);
            return;
        }

    };

    // Drawing Management Functions
    const handleAnnotateImage = (src: string, type: 'main' | 'attachment', id?: string) => {
        if (readOnly) return;
        setAnnotatingImage({ src, type, id });
        setActiveTab('draw');
        setActiveDrawingId(null);
        setShowDrawingGallery(false);
    };

    const handleSaveAnnotation = (base64: string | null, saveAsNew: boolean = false) => {
        if (!base64 || !annotatingImage) return;
        if (saveAsNew) {
            const newAtt: NodeAttachment = {
                id: `annotated_${Date.now()}`,
                type: 'image',
                content: base64,
                mimeType: 'image/png',
                createdAt: Date.now(),
            };
            onUpdate(node.id, { attachments: [...(node.attachments || []), newAtt] });
        } else {
            if (annotatingImage.type === 'main') {
                onUpdate(node.id, { imageBase64: base64, imageMimeType: 'image/png' });
            } else if (annotatingImage.type === 'attachment' && annotatingImage.id) {
                const updated = (node.attachments || []).map(a =>
                    a.id === annotatingImage.id ? { ...a, content: base64 } : a
                );
                onUpdate(node.id, { attachments: updated });
            }
        }
        setAnnotatingImage(null);
        setActiveTab('write');
    };

    const handleCreateNewDrawing = () => {
        // Clear active drawing to show blank canvas
        setActiveDrawingId('new');
        setShowDrawingGallery(false);
    };

    const handleLoadDrawing = (drawingId: string) => {
        setActiveDrawingId(drawingId);
        setShowDrawingGallery(false);
    };

    const handleDeleteDrawing = (drawingId: string) => {
        if (drawingId === 'legacy') {
            // Delete legacy drawing
            onUpdate(node.id, { drawingBase64: null });
        } else {
            // Delete from attachments
            const updatedAttachments = (node.attachments || []).filter(att => att.id !== drawingId);
            onUpdate(node.id, { attachments: updatedAttachments });
        }
        
        // If deleting active drawing, reset to gallery view
        if (activeDrawingId === drawingId) {
            setActiveDrawingId(null);
            setShowDrawingGallery(true);
        }
    };

    const handleSaveDrawing = (base64: string | null, saveAsNew: boolean = false) => {
        if (readOnly) return;

        console.log('[DEBUG] handleSaveDrawing called:', { 
            activeDrawingId, 
            saveAsNew, 
            hasBase64: !!base64,
            attachmentsCount: node.attachments?.length || 0
        });

        if (!base64) {
            // User cleared the canvas
            if (activeDrawingId && activeDrawingId !== 'new') {
                handleDeleteDrawing(activeDrawingId);
            }
            return;
        }

        // Determine if this is a new drawing or updating existing
        const isNewDrawing = activeDrawingId === 'new' || activeDrawingId === null || saveAsNew;
        posthog.capture('drawing_saved', { is_new: isNewDrawing });
        console.log('[DEBUG] Save mode:', isNewDrawing ? 'NEW' : 'UPDATE');

        if (isNewDrawing) {
            // Create new drawing in attachments
            const newDrawing: NodeAttachment = {
                id: `drawing_${Date.now()}`,
                type: 'drawing',
                content: base64,
                mimeType: 'image/png',
                createdAt: Date.now()
            };

            const updatedAttachments = [...(node.attachments || []), newDrawing];
            console.log('[DEBUG] Creating new drawing:', newDrawing.id, 'Total:', updatedAttachments.length);
            onUpdate(node.id, { attachments: updatedAttachments });
            
            // Set as active drawing
            setActiveDrawingId(newDrawing.id);
        } else {
            // Update existing drawing
            if (activeDrawingId === 'legacy') {
                // Migrate legacy to attachments
                const migratedDrawing: NodeAttachment = {
                    id: `drawing_${Date.now()}`,
                    type: 'drawing',
                    content: base64,
                    mimeType: 'image/png',
                    createdAt: Date.now()
                };
                
                const updatedAttachments = [...(node.attachments || []), migratedDrawing];
                console.log('[DEBUG] Migrating legacy drawing to:', migratedDrawing.id);
                onUpdate(node.id, { 
                    drawingBase64: null, // Remove legacy
                    attachments: updatedAttachments 
                });
                
                setActiveDrawingId(migratedDrawing.id);
            } else {
                // Update in attachments array
                const updatedAttachments = (node.attachments || []).map(att =>
                    att.id === activeDrawingId
                        ? { ...att, content: base64, createdAt: Date.now() }
                        : att
                );
                console.log('[DEBUG] Updating drawing:', activeDrawingId, 'Found:', updatedAttachments.some(a => a.id === activeDrawingId));
                onUpdate(node.id, { attachments: updatedAttachments });
            }
        }
        
        // Show success feedback (but don't reset activeDrawingId immediately)
        setShowDrawingGallery(true);
    };


    if (!isOpen) return null;

    const labels = {
        english: {
            placeholder: "Type details...",
            titlePlaceholder: "Title",
            close: "Close",
            format: "Format",
            aiGuide: "AI Guide",
            aiCheck: "AI Check",
            math: "Math",
            studyFramework: "Study Framework",
            tabWrite: "Write",
            tabDraw: "Draw",
            tabNote: "Note",
            tabInfo: "Info",
            tabOutline: "Outline",
            detailedCheck: "Deep Dive",
            bonus: "Bonus Points"
        },
        vietnamese: {
            placeholder: "Chi tiết...",
            titlePlaceholder: "Tiêu đề",
            close: "Đóng",
            format: "Định dạng",
            aiGuide: "Gợi ý AI",
            aiCheck: "Chấm điểm",
            math: "Toán",
            studyFramework: "Khung Bài Học",
            tabWrite: "Viết",
            tabDraw: "Vẽ",
            tabNote: "Ghi chú",
            tabInfo: "Thông tin",
            tabOutline: "Cấu trúc",
            detailedCheck: "Phân tích sâu",
            bonus: "Điểm thưởng"
        }
    };

    const t = labels[language];

    const visuals: Array<{ src: string; label: string; type: 'main' | 'drawing' | 'attachment'; id?: string }> = [];
    if (node.imageBase64) visuals.push({ src: node.imageBase64, label: language === 'vietnamese' ? 'Ảnh chính' : 'Main Image', type: 'main' });
    if (node.drawingBase64) visuals.push({ src: node.drawingBase64, label: language === 'vietnamese' ? 'Phác thảo' : 'My Sketch', type: 'drawing' });
    if (node.attachments) {
        // An id listed twice (left by an older reorder bug) is shown once; removing it removes both copies.
        const shownIds = new Set<string>();
        node.attachments.forEach((att, idx) => {
            if (shownIds.has(att.id)) return;
            shownIds.add(att.id);
            visuals.push({ src: att.content, label: `Attachment ${idx + 1}`, type: 'attachment', id: att.id });
        });
    }

    // Images passed to Voice Tutor — memoized by node.id so ref stays stable across re-renders
    const nodeImagesForTutor = useMemo(() => {
        const imgs: { base64: string; mimeType: string }[] = [];
        if (node.imageBase64) imgs.push({ base64: node.imageBase64, mimeType: node.imageMimeType || 'image/png' });
        (node.attachments || []).filter(a => a.type === 'image' && a.content).forEach(a => imgs.push({ base64: a.content, mimeType: a.mimeType }));
        return imgs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.id, node.imageBase64, node.imageMimeType, node.attachments]);

    const handleDeleteVisual = (type: 'main' | 'drawing' | 'attachment', id?: string) => {
        if (readOnly) return;
        const att = type === 'attachment' ? (node.attachments || []).find(a => a.id === id) : undefined;
        const isDrawing = type === 'drawing' || att?.type === 'drawing';
        const vn = language === 'vietnamese';
        if (!window.confirm(isDrawing ? (vn ? 'Xoá hình vẽ này khỏi note?' : 'Remove this drawing from the note?') : (vn ? 'Xoá ảnh này khỏi note?' : 'Remove this image from the note?'))) return;
        if (type === 'main') onUpdate(node.id, { imageBase64: null as any, imageMimeType: null as any });
        else if (type === 'drawing') onUpdate(node.id, { drawingBase64: null as any });
        else if (type === 'attachment' && id) onUpdate(node.id, { attachments: (node.attachments || []).filter(a => a.id !== id) });
    };


    const handleReorderVisuals = useCallback((fromIdx: number, toIdx: number) => {
        if (fromIdx === toIdx || readOnly) return;
        const newVisuals = [...visuals];
        const [moved] = newVisuals.splice(fromIdx, 1);
        newVisuals.splice(toIdx, 0, moved);

        // Every attachment (pictures and saved drawings alike) is listed once in `visuals`,
        // so the new order is rebuilt from that list alone — each attachment exactly once.
        const ordered = newVisuals.filter(v => v.type === 'main' || v.type === 'attachment');
        const byId = new Map((node.attachments || []).map(a => [a.id, a] as const));

        // Only a picture can become the main image; a drawing keeps its type and editable strokes.
        const first = ordered[0];
        const firstAtt = first?.type === 'attachment' ? byId.get(first.id!) : undefined;
        const promoted = firstAtt?.type === 'image' ? firstAtt : undefined;
        const newImageBase64 = promoted ? promoted.content : (node.imageBase64 ?? null);
        const newImageMimeType = promoted ? promoted.mimeType : (node.imageMimeType ?? null);

        const seen = new Set<string>();
        const newAtts: NodeAttachment[] = [];
        for (const v of ordered) {
            if (v.type === 'main') {
                // The old main image is demoted only when a picture took its place.
                if (promoted && node.imageBase64) newAtts.push({ id: `img-${Date.now()}`, type: 'image', content: node.imageBase64, mimeType: node.imageMimeType || 'image/png', createdAt: Date.now() });
                continue;
            }
            const att = byId.get(v.id!);
            if (!att || att === promoted || seen.has(att.id)) continue;
            seen.add(att.id);
            newAtts.push(att);
        }

        onUpdate(node.id, {
            imageBase64: newImageBase64,
            imageMimeType: newImageMimeType,
            attachments: newAtts,
        });
    }, [visuals, node, readOnly, onUpdate]);

    const sendAiChatMessage = useCallback(async (inputOverride?: string) => {
        const text = (inputOverride ?? aiChatInput).trim();
        if (!text || aiChatLoading) return;
        const isGemini = aiChatModelId.startsWith('gemini');
        const activeKey = isGemini ? userApiKeys?.gemini : userApiKeys?.openai;
        if (!activeKey) return;

        const noteContent = ENABLE_RICH_TEXT_EDITOR && richEditorRef.current
            ? richEditorRef.current.getMarkdown()
            : localContent;

        const nodeImages: string[] = [];
        if (node.imageBase64 && node.imageBase64.startsWith('data:')) nodeImages.push(node.imageBase64);
        (node.attachments || []).filter(a => a.type === 'image' && a.content).forEach(a => nodeImages.push(a.content));

        const userMsg: { role: 'user'; content: string; images?: string[] } = { role: 'user', content: text };
        if (nodeImages.length > 0) userMsg.images = nodeImages;

        const newMessages = [...aiChatMessages, userMsg];
        setAiChatMessages(newMessages);
        setAiChatInput('');
        setAiChatLoading(true);
        setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

        const systemPrompt = `You are a helpful study assistant. The user is working on a note titled "${node.title || 'Untitled'}".

Note content:
${noteContent || '(empty)'}

Help the user understand this topic. Be concise, educational, and supportive. Use markdown for formatting when helpful.`;

        try {
            const abort = new AbortController();
            aiChatAbortRef.current = abort;
            let assistantText = '';

            if (isGemini) {
                const history = newMessages.slice(0, -1).map(m => ({
                    role: m.role === 'user' ? 'user' : 'model',
                    parts: [{ text: m.content }],
                }));

                const lastUserParts: any[] = [{ text: text }];
                if (nodeImages.length > 0 && newMessages.length === 1) {
                    nodeImages.forEach(img => {
                        const match = img.match(/^data:([^;]+);base64,(.+)$/);
                        if (match) lastUserParts.unshift({ inlineData: { mimeType: match[1], data: match[2] } });
                    });
                }

                const body = {
                    system_instruction: { parts: [{ text: systemPrompt }] },
                    contents: [...history, { role: 'user', parts: lastUserParts }],
                    generationConfig: { temperature: 0.7, maxOutputTokens: 2048 },
                };

                const res = await fetch(
                    `https://generativelanguage.googleapis.com/v1beta/models/${aiChatModelId}:streamGenerateContent?key=${activeKey}&alt=sse`,
                    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: abort.signal }
                );

                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err?.error?.message || `HTTP ${res.status}`);
                }

                setAiChatMessages(prev => [...prev, { role: 'assistant', content: '' }]);

                const reader = res.body!.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const data = line.slice(6).trim();
                        if (data === '[DONE]') continue;
                        try {
                            const parsed = JSON.parse(data);
                            const chunk = parsed.candidates?.[0]?.content?.parts?.[0]?.text || '';
                            if (chunk) {
                                assistantText += chunk;
                                setAiChatMessages(prev => {
                                    const updated = [...prev];
                                    updated[updated.length - 1] = { role: 'assistant', content: assistantText };
                                    return updated;
                                });
                                aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }
                        } catch { /* ignore parse errors */ }
                    }
                }
            } else {
                const messages = [
                    { role: 'system', content: systemPrompt },
                    ...newMessages.map(m => ({ role: m.role, content: m.content })),
                ];

                const res = await fetch('https://api.openai.com/v1/chat/completions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${activeKey}` },
                    body: JSON.stringify({ model: aiChatModelId, messages, stream: true, max_tokens: 2048 }),
                    signal: abort.signal,
                });

                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err?.error?.message || `HTTP ${res.status}`);
                }

                setAiChatMessages(prev => [...prev, { role: 'assistant', content: '' }]);

                const reader = res.body!.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const data = line.slice(6).trim();
                        if (data === '[DONE]') continue;
                        try {
                            const parsed = JSON.parse(data);
                            const chunk = parsed.choices?.[0]?.delta?.content || '';
                            if (chunk) {
                                assistantText += chunk;
                                setAiChatMessages(prev => {
                                    const updated = [...prev];
                                    updated[updated.length - 1] = { role: 'assistant', content: assistantText };
                                    return updated;
                                });
                                aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }
                        } catch { /* ignore parse errors */ }
                    }
                }
            }
        } catch (err: any) {
            if (err?.name !== 'AbortError') {
                const errMsg = err?.message || 'Unknown error';
                setAiChatMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${errMsg}` }]);
            }
        } finally {
            setAiChatLoading(false);
            aiChatAbortRef.current = null;
            setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    }, [aiChatInput, aiChatLoading, aiChatMessages, aiChatModelId, userApiKeys, node, localContent]);

    const handleChatVoice = async () => {
        if (isChatRecording) {
            chatMediaRecorderRef.current?.stop();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            chatAudioChunksRef.current = [];
            const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
                .find(t => MediaRecorder.isTypeSupported(t)) || '';
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            chatMediaRecorderRef.current = mr;
            mr.ondataavailable = (e) => { if (e.data.size > 0) chatAudioChunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                setIsChatRecording(false);
                const audioBlob = new Blob(chatAudioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
                chatAudioChunksRef.current = [];
                setIsChatTranscribing(true);
                try {
                    const reader = new FileReader();
                    reader.readAsDataURL(audioBlob);
                    reader.onloadend = async () => {
                        const base64 = (reader.result as string).split(',')[1];
                        const text = await transcribeAndRefineAudio(base64, language);
                        if (text) setAiChatInput(prev => prev ? `${prev} ${text}` : text);
                        setIsChatTranscribing(false);
                    };
                } catch {
                    setIsChatTranscribing(false);
                }
            };
            mr.start();
            setIsChatRecording(true);
        } catch (err) {
            console.error('Chat mic error', err);
        }
    };

    const hasAiKey = !!(userApiKeys?.gemini || userApiKeys?.openai);
    const hasSidebarContent = visuals.length > 0 ||
        (node.suggestedQuestions && node.suggestedQuestions.length > 0) ||
        !!node.analysis ||
        linkedNodes.length > 0 ||
        (node.tags && node.tags.length > 0) ||
        hasAiKey;

    const narrowSidebar = sidebarWidth <= 240 && isSidebarOpen;
    // On mobile the Info tab reuses this same content but the desktop sidebar (which
    // narrowSidebar is measured from) is display:none, so narrowSidebar is always true
    // there even though the mobile tab has plenty of width. Show labels on mobile regardless.
    const showLabel = !narrowSidebar || isMobileViewport;

    const sidebarInfoContent = (
        <>
                                                {node.analysis && (() => {
                                                    const a = node.analysis;
                                                    const verdict = a.verdict ?? (a.score >= 80 ? 'pass' : a.score >= 50 ? 'minor_issues' : 'critical_issues');
                                                    const moveOn = a.moveOn ?? (verdict !== 'critical_issues');
                                                    const hasCritical = (a.criticalIssues?.length ?? 0) > 0;
                                                    const hasMinor = (a.minorIssues?.length ?? 0) > 0;
                                                    const hasStrengths = (a.strengths?.length ?? 0) > 0;
                                                    const hasNuances = (a.nuances?.length ?? 0) > 0;
                                                    const hasStructured = hasCritical || hasMinor || hasStrengths;
                                                    const verdictLabel = verdict === 'pass' ? '🟢 Pass' : verdict === 'minor_issues' ? '🟡 Minor issues' : '🔴 Critical issues';
                                                    const verdictColor = verdict === 'pass' ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : verdict === 'minor_issues' ? 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
                                                    const isLegacyCheck = !node.aiCheckVersion;
                                                    return (
                                                        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-sm space-y-3">
                                                            {/* Header */}
                                                            <div className={`flex items-center justify-between gap-2 ${narrowSidebar ? 'flex-wrap' : ''}`}>
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${verdictColor}`}>{verdictLabel}</span>
                                                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${moveOn ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                                                                        {moveOn ? '✓ Move on' : '✗ Fix first'}
                                                                    </span>
                                                                    {isLegacyCheck && <span className="text-[10px] px-1.5 py-0.5 rounded border text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700">⏰ Legacy</span>}
                                                                </div>
                                                                <span className={`text-xl font-black shrink-0 ${a.score >= 80 ? 'text-green-500' : a.score >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>{a.score}%</span>
                                                            </div>

                                                            {/* Score bar */}
                                                            <div className="h-1 w-full bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                                <div className={`h-full transition-all duration-1000 ${verdict === 'pass' ? 'bg-green-500' : verdict === 'minor_issues' ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${a.score}%` }} />
                                                            </div>

                                                            {/* Summary */}
                                                            <div className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none ${narrowSidebar && !feedbackExpanded ? 'line-clamp-2' : ''}`}>
                                                                <MathContent text={a.feedback} />
                                                            </div>
                                                            {narrowSidebar && (
                                                                <button onClick={() => setFeedbackExpanded(e => !e)} className="text-[10px] text-indigo-500 hover:text-indigo-700 font-medium">
                                                                    {feedbackExpanded ? '▲ Thu gọn' : '▼ Xem thêm'}
                                                                </button>
                                                            )}

                                                            {/* Critical issues */}
                                                            {hasCritical && (
                                                                <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 p-3 space-y-2 cursor-pointer hover:brightness-95 transition-all" onClick={() => setAISectionFocus({ title: language === 'vietnamese' ? '🔴 Lỗi nghiêm trọng' : '🔴 Critical issues', color: 'red', items: a.criticalIssues!.map(x => ({ quote: x.quote, text: x.explanation })) })}>
                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">🔴 {language === 'vietnamese' ? 'Lỗi nghiêm trọng' : 'Critical issues'}</p>
                                                                    {(narrowSidebar ? a.criticalIssues!.slice(0, 1) : a.criticalIssues!).map((item, i) => (
                                                                        <div key={i} className="space-y-0.5">
                                                                            {item.quote && <p className={`text-xs italic text-red-500 dark:text-red-400 border-l-2 border-red-400 pl-2 ${narrowSidebar ? 'line-clamp-1' : ''}`}>"{item.quote}"</p>}
                                                                            <div className={`text-sm text-red-800 dark:text-red-300 leading-snug [&>p]:m-0 ${narrowSidebar ? 'line-clamp-2' : ''}`} dangerouslySetInnerHTML={{ __html: parseMarkdown(item.explanation) }} />
                                                                        </div>
                                                                    ))}
                                                                    {narrowSidebar && a.criticalIssues!.length > 1 && <p className="text-[10px] text-red-500 dark:text-red-400 font-medium">+{a.criticalIssues!.length - 1} {language === 'vietnamese' ? 'nữa' : 'more'}</p>}
                                                                </div>
                                                            )}

                                                            {/* Minor issues */}
                                                            {hasMinor && (
                                                                <div className="rounded-lg border border-yellow-200 dark:border-yellow-800/50 bg-yellow-50 dark:bg-yellow-900/10 p-3 space-y-2 cursor-pointer hover:brightness-95 transition-all" onClick={() => setAISectionFocus({ title: language === 'vietnamese' ? '🟡 Lỗi nhỏ / chưa chặt' : '🟡 Minor issues', color: 'yellow', items: a.minorIssues!.map(x => ({ quote: x.quote, text: x.explanation })) })}>
                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-yellow-700 dark:text-yellow-400">🟡 {language === 'vietnamese' ? 'Lỗi nhỏ / chưa chặt' : 'Minor issues'}</p>
                                                                    {(narrowSidebar ? a.minorIssues!.slice(0, 1) : a.minorIssues!).map((item, i) => (
                                                                        <div key={i} className="space-y-0.5">
                                                                            {item.quote && <p className={`text-xs italic text-yellow-600 dark:text-yellow-400 border-l-2 border-yellow-400 pl-2 ${narrowSidebar ? 'line-clamp-1' : ''}`}>"{item.quote}"</p>}
                                                                            <div className={`text-sm text-yellow-900 dark:text-yellow-300 leading-snug [&>p]:m-0 ${narrowSidebar ? 'line-clamp-2' : ''}`} dangerouslySetInnerHTML={{ __html: parseMarkdown(item.explanation) }} />
                                                                        </div>
                                                                    ))}
                                                                    {narrowSidebar && a.minorIssues!.length > 1 && <p className="text-[10px] text-yellow-700 dark:text-yellow-400 font-medium">+{a.minorIssues!.length - 1} {language === 'vietnamese' ? 'nữa' : 'more'}</p>}
                                                                </div>
                                                            )}

                                                            {/* Strengths */}
                                                            {hasStrengths && (
                                                                <div className="rounded-lg border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10 p-3 space-y-1 cursor-pointer hover:brightness-95 transition-all" onClick={() => setAISectionFocus({ title: language === 'vietnamese' ? '✓ Điểm tốt' : '✓ Strengths', color: 'green', items: a.strengths!.map(s => ({ text: s })) })}>
                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-green-700 dark:text-green-400">✓ {language === 'vietnamese' ? 'Điểm tốt' : 'Strengths'}</p>
                                                                    {(narrowSidebar ? a.strengths!.slice(0, 2) : a.strengths!).map((s, i) => <p key={i} className={`text-sm text-green-900 dark:text-green-300 leading-snug ${narrowSidebar ? 'line-clamp-2' : ''}`}>• {s}</p>)}
                                                                    {narrowSidebar && a.strengths!.length > 2 && <p className="text-[10px] text-green-700 dark:text-green-400 font-medium">+{a.strengths!.length - 2} {language === 'vietnamese' ? 'nữa' : 'more'}</p>}
                                                                </div>
                                                            )}

                                                            {/* Nuances */}
                                                            {hasNuances && (
                                                                <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/50 p-3 space-y-1 cursor-pointer hover:brightness-95 transition-all" onClick={() => setAISectionFocus({ title: language === 'vietnamese' ? '💡 Ghi chú thêm' : '💡 Deeper notes', color: 'gray', items: a.nuances!.map(n => ({ text: n })) })}>
                                                                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">💡 {language === 'vietnamese' ? 'Ghi chú thêm' : 'Deeper notes'}</p>
                                                                    {(narrowSidebar ? a.nuances!.slice(0, 2) : a.nuances!).map((n, i) => <p key={i} className={`text-sm text-gray-600 dark:text-gray-400 leading-snug ${narrowSidebar ? 'line-clamp-2' : ''}`}>• {n}</p>)}
                                                                    {narrowSidebar && a.nuances!.length > 2 && <p className="text-[10px] text-gray-500 font-medium">+{a.nuances!.length - 2} {language === 'vietnamese' ? 'nữa' : 'more'}</p>}
                                                                </div>
                                                            )}

                                                            {/* Fallback: old detailedFeedback */}
                                                            {!hasStructured && a.detailedFeedback && (
                                                                <div className="space-y-3 pt-2 border-t dark:border-slate-800">
                                                                    <div className={`text-sm text-gray-600 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none overflow-hidden ${detailedExpanded ? '' : 'line-clamp-4'}`}>
                                                                        <MathContent text={a.detailedFeedback} />
                                                                    </div>
                                                                    <button onClick={() => setDetailedExpanded(e => !e)} className="text-[10px] text-indigo-500 hover:text-indigo-700 font-medium">
                                                                        {detailedExpanded ? '▲ Thu gọn' : '▼ Xem thêm'}
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {/* Line-by-line feedback (legacy) */}
                                                            {a.lineByLineFeedback && (
                                                                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-900/30 rounded-xl">
                                                                    <h4 className="text-xs font-bold text-purple-700 dark:text-purple-400 mb-2 uppercase tracking-widest flex items-center justify-between gap-2 cursor-pointer select-none" onClick={() => setLineByLineExpanded(e => !e)}>
                                                                        <span className="flex items-center gap-2"><List size={14} />📝 {language === 'vietnamese' ? 'Phân tích từng dòng' : 'Line-by-line'}</span>
                                                                        <span className="text-[10px] text-indigo-500 font-medium normal-case">{lineByLineExpanded ? '▲ Thu gọn' : '▼ Xem thêm'}</span>
                                                                    </h4>
                                                                    {lineByLineExpanded && <div className="prose prose-sm dark:prose-invert max-w-none text-sm"><MathContent text={a.lineByLineFeedback} /></div>}
                                                                </div>
                                                            )}

                                                            {/* Auto re-check indicator */}
                                                            {isAutoReChecking && (
                                                                <div className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500 justify-center py-1">
                                                                    <Loader2 size={11} className="animate-spin" />
                                                                    {language === 'vietnamese' ? 'Đang cập nhật với AI mới...' : 'Updating with latest AI...'}
                                                                </div>
                                                            )}

                                                            {/* Refresh button */}
                                                            {!readOnly && (
                                                                <button
                                                                    onClick={() => onAnalyze(node.id)}
                                                                    disabled={node.isAnalyzing}
                                                                    className="w-full flex items-center justify-center gap-2 py-2 border border-dashed border-indigo-300 dark:border-indigo-800 rounded-lg text-indigo-600 dark:text-indigo-400 text-xs font-semibold hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all disabled:opacity-50"
                                                                >
                                                                    {node.isAnalyzing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                                                                    {showLabel && (language === 'vietnamese' ? 'Chạy lại AI Check' : 'Re-run AI Check')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {node.suggestedQuestions && node.suggestedQuestions.length > 0 && (
                                                    <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/30 rounded-xl p-4">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <h4 className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-1.5">
                                                                <LifeBuoy size={14} /> {t.studyFramework}
                                                            </h4>
                                                            {!readOnly && (
                                                                <Tip text={language === 'vietnamese' ? 'Xóa khung học tập' : 'Clear Framework'} side="top">
                                                                <button
                                                                    onClick={() => onUpdate(node.id, { suggestedQuestions: undefined })}
                                                                    className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                                </Tip>
                                                            )}
                                                        </div>
                                                        <div className="space-y-2.5">
                                                            {node.suggestedQuestions.map((q, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    onClick={() => !readOnly && handleInsertQuestion(q)}
                                                                    className={`p-3 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900/30 rounded-lg text-sm text-gray-700 dark:text-gray-300 transition-all shadow-sm group relative ${readOnly ? '' : 'hover:border-indigo-400 dark:hover:border-indigo-500 cursor-pointer hover:shadow-md'}`}
                                                                >
                                                                    <div className="flex gap-2.5">
                                                                        <span className="font-bold text-indigo-500 text-xs mt-0.5">{idx + 1}.</span>
                                                                        <span className={`leading-relaxed ${narrowSidebar ? 'line-clamp-3' : ''}`}>{q}</span>
                                                                    </div>
                                                                    {!readOnly && (
                                                                        <div className="absolute right-2 bottom-2 text-[10px] text-indigo-500 font-bold opacity-0 group-hover:opacity-100 transition-opacity bg-indigo-50 dark:bg-slate-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                                            <Copy size={10} /> Insert
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Cross-linked Notes */}
                                                {(linkedNodes.length > 0 || !!onCreateChildNote || !!onEnterOutlineLinkMode || (!readOnly && !!onCreateLink)) && (
                                                    <div className="bg-slate-50 dark:bg-slate-900/10 border border-slate-200 dark:border-slate-800/30 rounded-xl p-4 mt-4">
                                                        <div className="flex items-center justify-between gap-2 mb-3">
                                                            <Tip text={language === 'vietnamese' ? 'Mở outline (Ctrl+\\)' : 'Open outline (Ctrl+\\)'} side="top">
                                                            <button
                                                                type="button"
                                                                onClick={() => { if (isMobileViewport) { setMobileSection('outline'); } else { onToggleOutline?.(); } }}
                                                                className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5 min-w-0 truncate hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                                            >
                                                                <LinkIcon size={isMobileViewport ? 14 : 12} className="shrink-0" /> {language === 'vietnamese' ? 'Liên kết' : 'Links'}
                                                            </button>
                                                            </Tip>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                            {copiedCrossLink && copiedCrossLink.nodeId !== node.id && !linkedNodes.some(ln => ln.id === copiedCrossLink.nodeId) && !readOnly && onCreateLink && (
                                                                <div className="flex items-center gap-0.5">
                                                                    <Tip text={`${language === 'vietnamese' ? 'Dán liên kết: ' : 'Paste link: '}${copiedCrossLink.nodeTitle}${copiedCrossLink.notebookId && copiedCrossLink.notebookId !== currentNotebookId ? ` (${copiedCrossLink.notebookTitle})` : ''}`} side="top">
                                                                    <button
                                                                        onClick={() => {
                                                                            const isCrossNb = copiedCrossLink.notebookId && copiedCrossLink.notebookId !== currentNotebookId;
                                                                            onCreateLink(
                                                                                copiedCrossLink.nodeId,
                                                                                isCrossNb ? copiedCrossLink.notebookId : undefined,
                                                                                isCrossNb ? copiedCrossLink.nodeTitle : undefined,
                                                                                isCrossNb ? (copiedCrossLink.notebookTitle || '') : undefined,
                                                                            );
                                                                            localStorage.removeItem('sb_copied_crosslink');
                                                                            setCopiedCrossLink(null);
                                                                        }}
                                                                        className={`flex items-center gap-1 rounded-md text-xs font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors whitespace-nowrap ${isMobileViewport ? 'px-2.5 py-1.5' : 'px-2 py-0.5'}`}
                                                                    >
                                                                        <Clipboard size={isMobileViewport ? 16 : 11} />
                                                                        {showLabel && <span>{language === 'vietnamese' ? 'Dán' : 'Paste'}</span>}
                                                                    </button>
                                                                    </Tip>
                                                                    <Tip text={language === 'vietnamese' ? 'Hủy sao chép' : 'Clear clipboard'} side="top">
                                                                    <button
                                                                        onClick={() => { localStorage.removeItem('sb_copied_crosslink'); setCopiedCrossLink(null); }}
                                                                        className="p-0.5 rounded text-violet-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors"
                                                                    >
                                                                        <X size={10} />
                                                                    </button>
                                                                    </Tip>
                                                                </div>
                                                            )}
                                                            {onEnterOutlineLinkMode && !readOnly && (
                                                                <Tip text={language === 'vietnamese' ? 'Liên kết từ outline' : 'Link from outline'} side="top">
                                                                <button
                                                                    onClick={onEnterOutlineLinkMode}
                                                                    className={`flex items-center gap-1 rounded-md text-xs font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors whitespace-nowrap ${isMobileViewport ? 'px-2.5 py-1.5' : 'px-2 py-0.5'}`}
                                                                >
                                                                    <LinkIcon size={isMobileViewport ? 16 : 11} />
                                                                    {showLabel && <span>{language === 'vietnamese' ? 'Liên kết' : 'Link'}</span>}
                                                                </button>
                                                                </Tip>
                                                            )}
                                                            {onCreateChildNote && (
                                                                <Tip text={language === 'vietnamese' ? 'Tạo note con' : 'Create child note'} side="top">
                                                                <button
                                                                    onClick={onCreateChildNote}
                                                                    className={`flex items-center gap-1 rounded-md text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors whitespace-nowrap ${isMobileViewport ? 'px-2.5 py-1.5' : 'px-2 py-0.5'}`}
                                                                >
                                                                    <Plus size={isMobileViewport ? 16 : 11} />
                                                                    {showLabel && <span>{language === 'vietnamese' ? 'Note con' : 'Child note'}</span>}
                                                                </button>
                                                                </Tip>
                                                            )}
                                                            {!readOnly && onCreateLink && currentNotebookId && (
                                                                <div className="relative">
                                                                    <Tip text={language === 'vietnamese' ? 'Thêm tùy chọn' : 'More options'} side="top">
                                                                    <button
                                                                        onClick={() => setLinksMoreMenuOpen(v => !v)}
                                                                        className={`flex items-center rounded-md text-xs font-medium text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ${isMobileViewport ? 'px-2.5 py-1.5' : 'px-1.5 py-0.5'}`}
                                                                    >
                                                                        <MoreHorizontal size={isMobileViewport ? 16 : 13} />
                                                                    </button>
                                                                    </Tip>
                                                                    {linksMoreMenuOpen && (
                                                                        <>
                                                                            <div className="fixed inset-0 z-[199]" onClick={() => setLinksMoreMenuOpen(false)} />
                                                                            <div className="absolute right-0 top-full mt-1 z-[200] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 py-1 min-w-[200px] overflow-hidden">
                                                                                <button
                                                                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 transition-colors"
                                                                                    onClick={() => {
                                                                                        const nbTitle = allNotebooks?.find(n => n.id === currentNotebookId)?.title || '';
                                                                                        const data = { nodeId: node.id, nodeTitle: node.title || 'Untitled', notebookId: currentNotebookId, notebookTitle: nbTitle };
                                                                                        localStorage.setItem('sb_copied_crosslink', JSON.stringify(data));
                                                                                        setCopiedCrossLink(data);
                                                                                        setLinksMoreMenuOpen(false);
                                                                                    }}
                                                                                >
                                                                                    <Copy size={13} className="text-sky-400 flex-shrink-0" />
                                                                                    <span>{language === 'vietnamese' ? 'Sao chép tham chiếu ghi chú' : 'Copy note reference'}</span>
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col gap-2">
                                                            {[
                                                                { type: 'parent', symbol: '↑', symbolColor: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30 text-amber-700 dark:text-amber-300', hover: 'hover:bg-amber-100 dark:hover:bg-amber-900/40' },
                                                                { type: 'child',  symbol: '↓', symbolColor: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/30 text-emerald-700 dark:text-emerald-300', hover: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40' },
                                                                { type: 'peer',   symbol: '↔', symbolColor: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-900/30 text-indigo-700 dark:text-indigo-300', hover: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/40' },
                                                            ].map(({ type, symbol, symbolColor, bg, hover }) => {
                                                                const group = type === 'peer'
                                                                    ? linkedNodes.filter(ln => ln.relationType !== 'parent' && ln.relationType !== 'child')
                                                                    : linkedNodes.filter(ln => ln.relationType === type);
                                                                if (!group.length) return null;
                                                                return (
                                                                    <div key={type} className="flex items-start gap-2">
                                                                        <span className={`text-sm font-bold ${symbolColor} w-4 shrink-0 mt-1`}>{symbol}</span>
                                                                        <div className="flex flex-wrap gap-1.5">
                                                                            {group.map(ln => (
                                                                                <div key={ln.linkId} className="relative group/link flex items-center min-w-0 max-w-full overflow-hidden">
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                                                                                            if (ln.notebookId && ln.notebookId !== currentNotebookId) {
                                                                                                onNavigateCrossNotebook?.(ln.notebookId, ln.id);
                                                                                            } else {
                                                                                                onNavigateToNote?.(ln.id);
                                                                                            }
                                                                                        }}
                                                                                        onContextMenu={(e) => { e.preventDefault(); setLinkContextMenu({ x: e.clientX, y: e.clientY, nodeId: ln.id, nodeTitle: ln.title || 'Untitled', notebookId: ln.notebookId || currentNotebookId }); }}
                                                                                        className={`${!readOnly && onUnlink ? 'pr-5' : 'pr-2.5'} pl-1.5 py-1 ${bg} border rounded-full text-xs font-medium ${hover} hover:scale-105 transition-all shadow-sm flex items-center gap-1 max-w-full overflow-hidden`}
                                                                                    >
                                                                                        <span
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                            onMouseEnter={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                                                                                                const x = e.clientX; const y = e.clientY;
                                                                                                if (ln.notebookId && ln.notebookId !== currentNotebookId) {
                                                                                                    // Cross-notebook: show stub immediately, then fetch content on demand
                                                                                                    const targetNb = allNotebooks?.find(n => n.id === ln.notebookId);
                                                                                                    const existingNode = targetNb?.nodes.find(n => n.id === ln.id);
                                                                                                    const stubNode: MindMapNode = existingNode ?? {
                                                                                                        id: ln.id,
                                                                                                        title: ln.title || '',
                                                                                                        content: '',
                                                                                                        parentId: null,
                                                                                                        position: { x: 0, y: 0 },
                                                                                                        analysis: null,
                                                                                                        isAnalyzing: false,
                                                                                                        isExpanded: false,
                                                                                                        createdAt: 0,
                                                                                                        updatedAt: 0,
                                                                                                    } as MindMapNode;
                                                                                                    linkHoverTimerRef.current = setTimeout(() => {
                                                                                                        setLinkHoverPreview({ node: stubNode, top: y, left: x, notebookId: ln.notebookId, notebookTitle: ln.notebookTitle });
                                                                                                        // Fetch full content from Cloud Function
                                                                                                        getNodeContent(ln.notebookId!, ln.id).then(result => {
                                                                                                            setLinkHoverPreview(prev => prev ? { ...prev, node: { ...prev.node, title: result.title, content: result.content, imageBase64: result.imageBase64 ?? prev.node.imageBase64, imageMimeType: result.imageMimeType ?? prev.node.imageMimeType, attachments: result.attachments ?? prev.node.attachments } } : null);
                                                                                                        }).catch(() => { /* ignore — preview shows stub */ });
                                                                                                    }, 200);
                                                                                                } else {
                                                                                                    const fullNode = allNodes.find(n => n.id === ln.id);
                                                                                                    if (!fullNode) return;
                                                                                                    linkHoverTimerRef.current = setTimeout(() => setLinkHoverPreview({ node: fullNode, top: y, left: x, notebookId: ln.notebookId, notebookTitle: ln.notebookTitle }), 200);
                                                                                                }
                                                                                            }}
                                                                                            onMouseLeave={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                                                                                                linkHoverTimerRef.current = setTimeout(() => setLinkHoverPreview(null), 150);
                                                                                            }}
                                                                                            className="opacity-50 hover:opacity-100 transition-opacity shrink-0 cursor-default"
                                                                                        >
                                                                                            <Eye size={11} />
                                                                                        </span>
                                                                                        {ln.notebookId && ln.notebookId !== currentNotebookId && (
                                                                                            <span className="text-[9px] font-bold opacity-60 max-w-[50px] truncate shrink-0">{ln.notebookTitle || '?'}</span>
                                                                                        )}
                                                                                        <span className={`${narrowSidebar ? 'max-w-[60px]' : 'max-w-[130px]'} truncate block whitespace-nowrap`}>{ln.title || 'Untitled'}</span>
                                                                                    </button>
                                                                                    {!readOnly && onUnlink && (
                                                                                        <Tip text={language === 'vietnamese' ? 'Xóa liên kết' : 'Remove link'} side="top">
                                                                                        <button
                                                                                            onClick={(e) => { e.stopPropagation(); onUnlink(ln.id); }}
                                                                                            className="absolute right-1 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover/link:opacity-100 hover:bg-red-200 dark:hover:bg-red-900/40 transition-all"
                                                                                        >
                                                                                            <X size={9} className="text-red-600 dark:text-red-400" />
                                                                                        </button>
                                                                                        </Tip>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Related videos — the node's OWN video(s) pinned at top (permanent, not removable — it's
                                                    not a "reference", it's the video actually recorded for this node), followed by videos that
                                                    live on OTHER nodes but are relevant here (e.g. "already explained this in an earlier video").
                                                    Each related-video pill offers two separate jumps: straight to the YouTube video, and to the
                                                    note that owns it (per explicit product decision — a video and its note both carry useful,
                                                    different information). */}
                                                {(() => {
                                                    const ownVideos = getVideosForNode(node);
                                                    if (ownVideos.length === 0 && !(node.relatedVideoRefs && node.relatedVideoRefs.length > 0) && (readOnly || !onAddRelatedVideo)) return null;
                                                    return (
                                                    <div className="bg-slate-50 dark:bg-slate-900/10 border border-slate-200 dark:border-slate-800/30 rounded-xl p-4 mt-4">
                                                        <div className="flex items-center justify-between gap-2 mb-3">
                                                            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                                <Youtube size={12} className="shrink-0" /> {language === 'vietnamese' ? 'Video liên quan' : 'Related videos'}
                                                            </h4>
                                                            {!readOnly && onAddRelatedVideo && (
                                                                <Tip text={language === 'vietnamese' ? 'Chọn video đã tạo để gắn vào đây' : 'Pick an existing video to attach here'} side="top">
                                                                <button
                                                                    onClick={openVideoGallery}
                                                                    className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
                                                                >
                                                                    <Plus size={14} />
                                                                </button>
                                                                </Tip>
                                                            )}
                                                        </div>
                                                        {ownVideos.length > 0 && (
                                                            <div className="flex flex-col gap-1.5 mb-1.5">
                                                                {ownVideos.map((v, i) => (
                                                                    <div key={v.youtubeVideoId} className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 rounded-lg pl-2.5 pr-2 py-1 text-xs">
                                                                        <span className="px-1.5 py-0.5 rounded bg-amber-200/60 dark:bg-amber-800/40 text-amber-700 dark:text-amber-300 font-bold text-[10px] shrink-0">
                                                                            {language === 'vietnamese' ? 'Video gốc' : 'Native'}
                                                                        </span>
                                                                        <a
                                                                            href={`https://www.youtube.com/watch?v=${v.youtubeVideoId}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="flex items-center gap-1 text-red-600 dark:text-red-400 hover:underline font-medium truncate"
                                                                        >
                                                                            <Youtube size={12} className="shrink-0" /> {v.youtubeTitle || v.videoTitle || (ownVideos.length > 1 ? `${language === 'vietnamese' ? 'Video' : 'Video'} ${i + 1}` : (language === 'vietnamese' ? 'Xem video' : 'Watch'))}
                                                                        </a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {node.relatedVideoRefs && node.relatedVideoRefs.length > 0 && (
                                                            <div className="flex flex-col gap-1.5">
                                                                {node.relatedVideoRefs.map(ref => (
                                                                    <div key={ref.id} className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg pl-2.5 pr-1 py-1 text-xs">
                                                                        <a
                                                                            href={`https://www.youtube.com/watch?v=${ref.videoId}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="flex items-center gap-1 text-red-600 dark:text-red-400 hover:underline font-medium shrink-0"
                                                                        >
                                                                            <Youtube size={12} /> {language === 'vietnamese' ? 'Video' : 'Watch'}
                                                                        </a>
                                                                        <span className="text-slate-300 dark:text-slate-600">·</span>
                                                                        <button
                                                                            onClick={() => {
                                                                                setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                                                                                if (ref.notebookId !== currentNotebookId && onNavigateCrossNotebook) {
                                                                                    onNavigateCrossNotebook(ref.notebookId, ref.nodeId);
                                                                                } else {
                                                                                    onNavigateToNote?.(ref.nodeId);
                                                                                }
                                                                            }}
                                                                            className="flex items-center gap-1 text-slate-600 dark:text-slate-300 hover:underline min-w-0 truncate max-w-[160px]"
                                                                        >
                                                                            <FileText size={11} className="shrink-0" /> <span className="truncate">{ref.nodeTitle || 'Untitled'}</span>
                                                                        </button>
                                                                        {!readOnly && onRemoveRelatedVideo && (
                                                                            <button
                                                                                onClick={() => onRemoveRelatedVideo(ref.id)}
                                                                                className="ml-1 w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0"
                                                                            >
                                                                                <X size={10} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    );
                                                })()}

                                                {(() => {
                                                    let imageCounter = 0;
                                                    return visuals.map((vis, idx) => {
                                                    const isImage = vis.type === 'main' || vis.type === 'attachment';
                                                    if (isImage) imageCounter++;
                                                    const thisImageIndex = isImage ? imageCounter : null;
                                                    return (
                                                    <div
                                                        key={idx}
                                                        {...(thisImageIndex !== null ? { 'data-tutor-img': thisImageIndex } : {})}
                                                        className="group/visual rounded-xl"
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest group-hover/visual:text-blue-500 transition-colors">{vis.label}</h4>
                                                                {!readOnly && visuals.length > 1 && (
                                                                    <div className="flex gap-0.5 opacity-0 group-hover/visual:opacity-100 transition-all">
                                                                        <button disabled={idx === 0} onClick={() => handleReorderVisuals(idx, idx - 1)}
                                                                            className="p-0.5 rounded text-gray-400 hover:text-blue-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
                                                                            <ChevronUp size={12} />
                                                                        </button>
                                                                        <button disabled={idx === visuals.length - 1} onClick={() => handleReorderVisuals(idx, idx + 1)}
                                                                            className="p-0.5 rounded text-gray-400 hover:text-blue-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors">
                                                                            <ChevronDown size={12} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-0.5 opacity-0 group-hover/visual:opacity-100 transition-all">
                                                                <Tip text={imgInverted ? (language === 'vietnamese' ? 'Tắt đảo màu' : 'Disable invert') : (language === 'vietnamese' ? 'Đảo màu (đọc ban đêm)' : 'Invert colors (dark reading)')} side="top">
                                                                <button
                                                                    onClick={() => {
                                                                        const next = !imgInverted;
                                                                        setImgInverted(next);
                                                                        localStorage.setItem('sb_image_invert', String(next));
                                                                    }}
                                                                    className={`p-1 rounded transition-all ${imgInverted ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20' : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'}`}
                                                                >
                                                                    <SunMoon size={12} />
                                                                </button>
                                                                </Tip>
                                                                {!readOnly && (
                                                                    <>
                                                                        {vis.type !== 'drawing' && (
                                                                            <Tip text={language === 'vietnamese' ? 'Vẽ lên ảnh' : 'Draw on image'} side="top">
                                                                            <button
                                                                                onClick={() => handleAnnotateImage(vis.src, vis.type as 'main' | 'attachment', vis.id)}
                                                                                className="p-1 text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-all"
                                                                            >
                                                                                <PenLine size={12} />
                                                                            </button>
                                                                            </Tip>
                                                                        )}
                                                                        <Tip text={language === 'vietnamese' ? 'Xóa ảnh' : 'Delete image'} side="top">
                                                                        <button
                                                                            onClick={() => handleDeleteVisual(vis.type, vis.id)}
                                                                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                                                        >
                                                                            <X size={12} />
                                                                        </button>
                                                                        </Tip>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm hover:ring-2 hover:ring-blue-400 transition-all cursor-pointer"
                                                            onClick={() => setImageGallery({ isOpen: true, initialIndex: idx })}
                                                        >
                                                            <img
                                                                src={vis.src}
                                                                alt={vis.label}
                                                                className="w-full rounded-xl object-contain bg-white dark:bg-slate-900 transition-all duration-200"
                                                                style={{ filter: imgInverted ? 'invert(1) hue-rotate(180deg)' : 'none' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    );
                                                    });
                                                })()}

                                                {!readOnly && (
                                                    <button
                                                        onClick={() => imageFileInputRef.current?.click()}
                                                        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl text-gray-400 hover:text-blue-500 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all text-xs font-semibold"
                                                    >
                                                        <ImagePlus size={14} />
                                                        {language === 'vietnamese' ? 'Thêm ảnh' : 'Add image'}
                                                    </button>
                                                )}

                                                {/* === HASHTAG SECTION === */}
                                                {!readOnly && (node.imageBase64 || (node.attachments && node.attachments.length > 0)) && (
                                                    <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-sm">
                                                        <div className="flex items-center justify-between gap-2 mb-3">
                                                            <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-1.5 min-w-0 truncate">
                                                                <Tag size={12} className="shrink-0" />
                                                                {language === 'vietnamese' ? 'Hashtag' : 'Hashtags'}
                                                            </h4>
                                                            {!isExtractingTags ? (
                                                                <Tip text={language === 'vietnamese' ? 'AI nhận diện hashtag từ ảnh' : 'AI extract tags from image'} side="top">
                                                                <button
                                                                    onClick={() => { const firstImg = node.imageBase64 || node.attachments?.[0]?.content; if (firstImg) triggerExtractTags(firstImg, node.imageMimeType || node.attachments?.[0]?.mimeType || 'image/png'); }}
                                                                    className="flex items-center gap-1 px-2 py-1 text-[10px] text-indigo-500 hover:text-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 rounded-lg border border-indigo-200 dark:border-indigo-700 transition-colors font-semibold shrink-0 whitespace-nowrap"
                                                                >
                                                                    <Sparkles size={10} /> AI tags
                                                                </button>
                                                                </Tip>
                                                            ) : (
                                                                <span className="flex items-center gap-1 text-[10px] text-indigo-400 animate-pulse">
                                                                    <Loader2 size={10} className="animate-spin" />
                                                                    {language === 'vietnamese' ? 'Đang nhận diện...' : 'Extracting...'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {(node.tags || []).slice(0, narrowSidebar ? 3 : undefined).map((tag, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="group/tag inline-flex items-center gap-0.5 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-xs font-semibold rounded-full border border-indigo-200 dark:border-indigo-700/50 cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors min-w-0"
                                                                    onClick={() => onTagSearch?.(tag)}
                                                                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setTagContextMenu({ tag, x: e.clientX, y: e.clientY }); }}
                                                                    title={tag}
                                                                >
                                                                    <span className={`truncate whitespace-nowrap ${narrowSidebar ? 'max-w-[90px]' : 'max-w-[140px]'}`}>{tag}</span>
                                                                    <button
                                                                        className="ml-0.5 opacity-0 group-hover/tag:opacity-100 text-indigo-400 hover:text-red-500 transition-all"
                                                                        onClick={() => {
                                                                            const newTags = (node.tags || []).filter((_, i) => i !== idx);
                                                                            onUpdate(node.id, { tags: newTags });
                                                                        }}
                                                                    >
                                                                        <X size={10} />
                                                                    </button>
                                                                </span>
                                                            ))}
                                                            {narrowSidebar && (node.tags || []).length > 3 && (
                                                                <span className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 text-xs rounded-full border border-gray-200 dark:border-slate-700">
                                                                    +{(node.tags || []).length - 3}
                                                                </span>
                                                            )}
                                                            {addTagInput === null ? (
                                                                <button
                                                                    className="inline-flex items-center gap-0.5 px-2 py-1 text-xs text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-full border border-dashed border-gray-300 dark:border-slate-600 hover:border-indigo-300 transition-colors"
                                                                    onClick={() => { setAddTagInput(''); setTimeout(() => addTagInputRef.current?.focus(), 50); }}
                                                                >
                                                                    <Plus size={10} /> tag
                                                                </button>
                                                            ) : (
                                                                <input
                                                                    ref={addTagInputRef}
                                                                    type="text"
                                                                    value={addTagInput}
                                                                    className="text-xs px-2.5 py-1 rounded-full border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 outline-none w-28 focus:ring-1 focus:ring-indigo-300"
                                                                    placeholder="#tag"
                                                                    onChange={(e) => setAddTagInput(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && addTagInput.trim()) {
                                                                            const raw = addTagInput.trim();
                                                                            const fmt = raw.startsWith('#') ? raw : `#${raw}`;
                                                                            onUpdate(node.id, { tags: [...(node.tags || []), fmt] });
                                                                            setAddTagInput(null);
                                                                        } else if (e.key === 'Escape') setAddTagInput(null);
                                                                    }}
                                                                    onBlur={() => {
                                                                        if (addTagInput.trim()) {
                                                                            const raw = addTagInput.trim();
                                                                            const fmt = raw.startsWith('#') ? raw : `#${raw}`;
                                                                            onUpdate(node.id, { tags: [...(node.tags || []), fmt] });
                                                                        }
                                                                        setAddTagInput(null);
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

        </>
    );

    return (
        <>
        <style>{`
            @keyframes sbVoiceWave {
                0%   { transform: scaleY(0.25); }
                100% { transform: scaleY(1); }
            }
        `}</style>
        <div className={`${elevateAboveOverlay ? 'fixed z-[300]' : 'absolute z-[200]'} inset-0 flex items-center justify-center animate-in fade-in duration-200 ${isFullscreen ? '' : 'md:p-4 bg-black/60 backdrop-blur-sm'}`} onClick={handleClose}>
            <div
                className={`bg-white dark:bg-slate-900 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 relative transition-all duration-700 ${isFullscreen ? 'w-full h-full rounded-none' : 'w-[95%] md:max-w-7xl h-[100dvh] md:h-[85vh] md:rounded-2xl'} border border-gray-200 dark:border-slate-700`}
                onClick={(e) => e.stopPropagation()}
                onDragEnter={!readOnly ? handleModalDragEnter : undefined}
                onDragOver={!readOnly ? handleModalDragOver : undefined}
                onDragLeave={!readOnly ? handleModalDragLeave : undefined}
                onDrop={!readOnly ? handleModalDrop : undefined}
            >
                {isDragActive && !readOnly && (
                    <div className="absolute inset-0 z-[300] bg-blue-50/95 dark:bg-slate-800/95 backdrop-blur-sm md:rounded-2xl border-4 border-dashed border-blue-500 flex flex-col items-center justify-center pointer-events-none animate-in fade-in duration-150">
                        <Upload size={56} className="text-blue-500 mb-3" />
                        <p className="font-bold text-blue-600 dark:text-blue-400 text-xl">
                            {language === 'vietnamese' ? 'Thả ảnh vào đây' : 'Drop image here'}
                        </p>
                    </div>
                )}
                <input ref={imageFileInputRef} type="file" accept="image/*" className="hidden" onChange={(e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files?.[0]) processImageFile(e.target.files[0]); e.target.value = ''; }} />
                {/* Compact Header for Mobile — always visible (never collapses), since it carries the
                    note nav/title/primary actions. mobileChromeCollapsed now only hides the Write/Draw
                    tabs + formatting toolbar below, which are the rows worth folding away to read. */}
                <div ref={headerRef} className={`${activeTab === 'draw' ? 'hidden' : 'flex'} items-center gap-1 px-3 py-2 md:px-6 md:py-4 border-b dark:border-slate-800 bg-gray-50 dark:bg-slate-950 shrink-0`}>
                    <div className="flex items-center gap-1 md:gap-2 flex-1 min-w-0 overflow-hidden">
                        {/* Mobile-only: parent/child note nav, folded into the same row instead of its own strip */}
                        {mobileSection === 'note' && (() => {
                            const mobileParentLink = linkedNodes.find(ln => ln.relationType === 'parent');
                            const mobileChildLinks = linkedNodes.filter(ln => ln.relationType === 'child');
                            const navigateToLinkedNote = (id: string) => {
                                setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                                onNavigateToNote?.(id);
                            };
                            return (
                                <div className="relative md:hidden flex items-center shrink-0 -ml-1">
                                    <button
                                        onClick={() => mobileParentLink && navigateToLinkedNote(mobileParentLink.id)}
                                        disabled={!mobileParentLink}
                                        className="p-1.5 rounded text-gray-400 disabled:opacity-25 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title={language === 'vietnamese' ? 'Đến note cha' : 'Go to parent note'}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (mobileChildLinks.length === 1) navigateToLinkedNote(mobileChildLinks[0].id);
                                            else if (mobileChildLinks.length > 1) setMobileChildNavOpen(o => !o);
                                        }}
                                        disabled={!mobileChildLinks.length}
                                        className="p-1.5 rounded text-gray-400 disabled:opacity-25 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title={language === 'vietnamese' ? 'Đến note con' : 'Go to child note'}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                    {mobileChildNavOpen && mobileChildLinks.length > 1 && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setMobileChildNavOpen(false)} />
                                            <div className="absolute top-full left-1 mt-1 z-50 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg py-1 min-w-[180px] max-w-[260px] max-h-[50vh] overflow-y-auto">
                                                {mobileChildLinks.map(cl => (
                                                    <button
                                                        key={cl.id}
                                                        onClick={() => { navigateToLinkedNote(cl.id); setMobileChildNavOpen(false); }}
                                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 truncate"
                                                    >
                                                        {cl.title || (language === 'vietnamese' ? 'Chưa đặt tên' : 'Untitled')}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                        {/* Back button — from external navigation (e.g. TagPreviewStrip) */}
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors shrink-0 max-w-[160px]"
                            >
                                <ArrowLeft size={15} className="text-gray-500 dark:text-gray-400 shrink-0" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {backLabel || (language === 'vietnamese' ? 'Quay lại' : 'Back')}
                                </span>
                            </button>
                        )}
                        {/* Back button when navigation history exists */}
                        {!onBack && navigationHistory.length > 0 && (
                            <button
                                onClick={() => {
                                    const prev = navigationHistory[navigationHistory.length - 1];
                                    setNavigationHistory(h => h.slice(0, -1));
                                    if (prev.notebookId && prev.notebookId !== currentNotebookId && onNavigateCrossNotebook) {
                                        onNavigateCrossNotebook(prev.notebookId, prev.nodeId);
                                    } else if (onNavigateToNote) {
                                        onNavigateToNote(prev.nodeId);
                                    }
                                }}
                                className="flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors shrink-0 max-w-[160px]"
                            >
                                <ArrowLeft size={15} className="text-gray-500 dark:text-gray-400 shrink-0" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {navigationHistory[navigationHistory.length - 1].noteTitle || (language === 'vietnamese' ? 'Quay lại' : 'Back')}
                                </span>
                            </button>
                        )}
                        <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg shrink-0">
                            {activeTab === 'write' ? <Type size={18} /> : <PenTool size={18} />}
                        </div>
                        <input
                            type="text"
                            value={node.title}
                            onChange={(e) => onUpdate(node.id, { title: e.target.value })}
                            disabled={readOnly}
                            placeholder={t.titlePlaceholder}
                            className="bg-transparent text-base md:text-xl font-bold text-gray-900 dark:text-white outline-none w-full placeholder-gray-400 truncate disabled:opacity-80"
                        />
                        {!readOnly && onGenerateTitle && (node.content || node.imageBase64) && (
                            <Tip text={language === 'vietnamese' ? 'Tạo tiêu đề tự động' : 'Generate title with AI'} side="bottom">
                            <button
                                onClick={async () => {
                                    posthog.capture('note_feature_used', { feature: 'generate_title' });
                                    if (isTitleGenerating) return;
                                    if (node.title?.trim()) {
                                        const confirmMsg = language === 'vietnamese'
                                            ? 'Note này đã có tiêu đề. Bạn có muốn tạo tiêu đề mới thay thế không?'
                                            : 'This note already has a title. Replace it with an AI-generated one?';
                                        if (!window.confirm(confirmMsg)) return;
                                    }
                                    setIsTitleGenerating(true);
                                    try {
                                        const title = await onGenerateTitle(node.id);
                                        if (title) onUpdate(node.id, { title });
                                    } finally {
                                        setIsTitleGenerating(false);
                                    }
                                }}
                                disabled={isTitleGenerating}
                                className="p-1 rounded-full text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors flex-shrink-0 disabled:opacity-50"
                            >
                                {isTitleGenerating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                            </button>
                            </Tip>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                        {activeTab === 'write' && !readOnly && (
                            <>
                                {/* AI Tools dropdown — groups secondary AI features */}
                                <div className="relative">
                                    {showAiMenu && <div className="fixed inset-0 z-40" onClick={() => setShowAiMenu(false)} />}
                                    <button
                                        onClick={() => setShowAiMenu(s => !s)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${showAiMenu ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' : 'bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300'}`}
                                    >
                                        {(isQuickChecking || node.isGeneratingQuestions || isHinting)
                                            ? <Loader2 size={14} className="animate-spin text-violet-500" />
                                            : <BrainCircuit size={14} className={showAiMenu ? 'text-violet-500' : 'text-gray-500 dark:text-gray-400'} />
                                        }
                                        {!isToolbarCompact && <span>AI Tools</span>}
                                        <ChevronDown size={10} className="opacity-50" />
                                    </button>
                                    {showAiMenu && (
                                        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl py-1 min-w-[190px]">
                                            <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">{language === 'vietnamese' ? 'Công cụ AI' : 'AI Tools'}</p>
                                            <button
                                                onClick={() => { posthog.capture('note_feature_used', { feature: 'study_framework' }); onGetFramework(node.id); setShowAiMenu(false); }}
                                                disabled={node.isGeneratingQuestions}
                                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors disabled:opacity-50"
                                            >
                                                {node.isGeneratingQuestions ? <Loader2 size={14} className="animate-spin text-violet-500" /> : <LifeBuoy size={14} className="text-violet-500" />}
                                                {t.aiGuide}
                                            </button>
                                            {!isMobile && (
                                            <button
                                                onClick={() => { posthog.capture('note_feature_used', { feature: 'note_search' }); setIsSearchOverlayOpen(true); setShowAiMenu(false); }}
                                                disabled={allNodes.length === 0}
                                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors disabled:opacity-50"
                                            >
                                                <Search size={14} className="text-violet-500" />
                                                {language === 'vietnamese' ? 'Tìm & liên kết' : 'Search & Link'}
                                            </button>
                                            )}
                                            <button
                                                onClick={() => { posthog.capture('note_feature_used', { feature: 'quick_check' }); handleQuickCheck(); setShowAiMenu(false); }}
                                                disabled={isQuickChecking || node.isAnalyzing}
                                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors disabled:opacity-50"
                                            >
                                                {isQuickChecking ? <Loader2 size={14} className="animate-spin text-violet-500" /> : <Zap size={14} className="text-violet-500" />}
                                                {language === 'vietnamese' ? 'Kiểm tra nhanh' : 'Quick Check'}
                                            </button>
                                            <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                                            <button
                                                onClick={() => { posthog.capture('note_feature_used', { feature: 'voice_tutor' }); setShowVoiceTutor(true); setShowVoiceTutorLive(false); setShowAiMenu(false); }}
                                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
                                            >
                                                <GraduationCap size={14} className="text-violet-500" />
                                                Voice Tutor
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setShowTutorSettingsPopover(s => !s); }}
                                                    className="ml-auto p-0.5 rounded text-gray-400 hover:text-violet-500 transition-colors"
                                                    title={language === 'vietnamese' ? 'Cài đặt' : 'Settings'}
                                                >
                                                    <Settings size={11} />
                                                </button>
                                            </button>
                                            {showTutorSettingsPopover && <TutorSettingsPopover language={language} onClose={() => setShowTutorSettingsPopover(false)} />}
                                        </div>
                                    )}
                                </div>

                                {/* Primary AI action — Chấm điểm always visible */}
                                {node.isAnalyzing && onCancelAnalyze ? (
                                    <button
                                        onClick={() => onCancelAnalyze(node.id)}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap"
                                    >
                                        <X size={14} />
                                        {!isToolbarCompact && <span>{language === 'vietnamese' ? 'Huỷ' : 'Cancel'}</span>}
                                    </button>
                                ) : (
                                <Tip text={t.aiCheck} side="bottom">
                                <button
                                    onClick={() => { posthog.capture('note_feature_used', { feature: 'ai_check' }); aiCheckTriggeredRef.current = true; onAnalyze(node.id, 'detailed'); }}
                                    disabled={node.isAnalyzing}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors whitespace-nowrap"
                                >
                                    {node.isAnalyzing ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
                                    {!isToolbarCompact && <span>{t.aiCheck}</span>}
                                </button>
                                </Tip>
                                )}
                            </>
                        )}

                        {/* Divider */}
                        <div className="w-px h-5 bg-gray-200 dark:bg-slate-700 mx-0.5 hidden md:block" />

                        {/* Secondary icon-only actions */}
                        {!isMobile && (
                        <>
                        <Tip text={sidebarOnLeft ? (language === 'vietnamese' ? 'Sidebar sang phải' : 'Sidebar to right') : (language === 'vietnamese' ? 'Sidebar sang trái' : 'Sidebar to left')} side="bottom">
                        <button
                            onClick={() => { const next = !sidebarOnLeft; setSidebarOnLeft(next); try { localStorage.setItem('expanded_sidebar_left', next ? '1' : '0'); } catch {} }}
                            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            <PanelLeft size={15} />
                        </button>
                        </Tip>
                        <Tip text={isFullscreen ? (language === 'vietnamese' ? 'Thu nhỏ' : 'Exit fullscreen') : (language === 'vietnamese' ? 'Mở rộng' : 'Fullscreen')} side="bottom">
                        <button
                            onClick={() => { posthog.capture('note_feature_used', { feature: 'fullscreen', enabled: !isFullscreen }); setIsFullscreen(f => !f); }}
                            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            {isFullscreen ? <Shrink size={15} /> : <Expand size={15} />}
                        </button>
                        </Tip>
                        </>
                        )}

                        {!readOnly && currentNotebookId && (
                            <Tip text={containingCollections.length > 0
                                ? `${language === 'vietnamese' ? 'Đã lưu trong' : 'Saved in'}: ${containingCollections.map(c => c.title).join(', ')}`
                                : (language === 'vietnamese' ? 'Lưu vào bộ sưu tập' : 'Save to collection')} side="bottom">
                            <button
                                onClick={() => setShowCoursePicker(true)}
                                className={`p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-slate-700 ${containingCollections.length > 0 ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 hover:text-indigo-500'}`}
                            >
                                <Bookmark size={15} fill={containingCollections.length > 0 ? 'currentColor' : 'none'} />
                            </button>
                            </Tip>
                        )}

                        {!readOnly && onSharePost && (
                            <Tip text={language === 'vietnamese' ? 'Đăng lên mạng xã hội' : 'Post to social media'} side="bottom">
                            <button onClick={onSharePost} className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[15px] h-[15px]"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                            </button>
                            </Tip>
                        )}

                        {!readOnly && onUploadYouTube && (
                            <Tip text={language === 'vietnamese' ? 'Quay & upload YouTube' : 'Record & upload to YouTube'} side="bottom">
                            <button onClick={onUploadYouTube} className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-[15px] h-[15px]"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                            </button>
                            </Tip>
                        )}

                        {!readOnly && (
                            <div className="relative" ref={translationPopoverRef}>
                            <Tip text={
                                isTranslating ? (language === 'vietnamese' ? 'Đang dịch...' : 'Translating...') :
                                (node.translateToEnglish && node.englishTranslation) ? (language === 'vietnamese' ? 'Xem bản dịch tiếng Anh' : 'View English translation') :
                                (language === 'vietnamese' ? 'Dịch sang tiếng Anh (lưu cho GitHub)' : 'Translate to English (saved for GitHub)')
                            } side="bottom">
                            <button
                                onClick={handleTranslateToggle}
                                disabled={isTranslating}
                                className={`relative p-2 rounded-lg transition-colors disabled:opacity-60 ${(node.translateToEnglish && node.englishTranslation) ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                            >
                                {isTranslating ? (
                                    <Loader2 size={15} className="animate-spin" />
                                ) : (
                                    <span className="relative inline-flex">
                                        <Globe size={15} />
                                        {(node.translateToEnglish && node.englishTranslation) && (
                                            <span className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full flex items-center justify-center" style={{ width: 8, height: 8 }}>
                                                <Check size={5} className="text-white" strokeWidth={3} />
                                            </span>
                                        )}
                                    </span>
                                )}
                            </button>
                            </Tip>
                            {showTranslationPopover && node.englishTranslation && (
                                <div className="absolute right-0 top-full mt-2 z-50 w-80 max-h-72 overflow-y-auto rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-800 shadow-xl p-4 text-xs text-gray-700 dark:text-gray-300 space-y-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide flex items-center gap-1.5"><Globe size={11} /> English Translation</span>
                                        <button
                                            onClick={() => { onUpdate(node.id, { translateToEnglish: false, englishTranslation: undefined, englishAnalysisFeedback: undefined }); setShowTranslationPopover(false); }}
                                            className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400 transition-colors px-1.5 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                                        >
                                            <Trash2 size={10} /> {language === 'vietnamese' ? 'Xóa' : 'Remove'}
                                        </button>
                                    </div>
                                    <p className="leading-relaxed whitespace-pre-wrap">{node.englishTranslation}</p>
                                </div>
                            )}
                            </div>
                        )}

                        {!isToolbarCompact && (
                        <Tip text="Raw markdown" side="bottom">
                        <button
                            onClick={() => { if (showRaw) setTimeout(() => richEditorRef.current?.setContent(localContent || ''), 0); setShowRaw(p => !p); }}
                            className={`hidden md:flex items-center px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${showRaw ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400'}`}
                        >
                            {'</>'}
                        </button>
                        </Tip>
                        )}

                        {/* Video-potential score (switched on in YouTube Manager's map tab) */}
                        {videoScoreDisplay.note && (
                            <div
                                title={node.videoWorthinessScore != null
                                    ? `${language === 'vietnamese' ? 'Điểm tiềm năng video' : 'Video-potential score'}: ${node.videoWorthinessScore}/100${node.videoWorthinessSummary ? ` — ${node.videoWorthinessSummary}` : ''}`
                                    : (language === 'vietnamese' ? 'Note này chưa được chấm điểm tiềm năng video' : 'This note has no video-potential score yet')}
                                className={`hidden md:flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold tabular-nums select-none ${node.videoWorthinessScore != null ? scoreFill(node.videoWorthinessScore) : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400'}`}
                            >
                                <Youtube size={12} />
                                {node.videoWorthinessScore != null ? node.videoWorthinessScore : '–'}
                            </div>
                        )}

                        {/* Sync status */}
                        {!isToolbarCompact && (lastSavedTime || syncState === 'saving') && (
                            <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg select-none">
                                {syncState === 'saving' ? (
                                    <>
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse shrink-0" />
                                        <span className="text-[10px] text-blue-400 dark:text-blue-500 whitespace-nowrap">
                                            {language === 'vietnamese' ? 'Đang lưu...' : 'Saving...'}
                                        </span>
                                    </>
                                ) : lastSavedTime ? (
                                    <>
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                            {language === 'vietnamese'
                                                ? `Lưu lúc ${lastSavedTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`
                                                : `Saved ${lastSavedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                        </span>
                                    </>
                                ) : null}
                            </div>
                        )}

                        {/* Done + mobile collapse */}
                        <div className="flex items-center gap-0.5 ml-0.5">
                            <button
                                onClick={handleClose}
                                className="flex items-center gap-1 px-3 py-2 bg-gray-200 dark:bg-slate-800 hover:bg-gray-300 dark:hover:bg-slate-700 text-gray-900 dark:text-gray-100 rounded-lg font-bold text-xs transition-colors"
                            >
                                <X size={15} />
                                {!isToolbarCompact && <span>{t.close}</span>}
                            </button>
                            <button
                                onClick={() => setMobileChromeCollapsed(c => !c)}
                                className="md:hidden p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                title={language === 'vietnamese' ? (mobileChromeCollapsed ? 'Hiện thanh công cụ' : 'Ẩn thanh công cụ') : (mobileChromeCollapsed ? 'Show toolbar' : 'Hide toolbar')}
                            >
                                {mobileChromeCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                        </div>
                    </div>
                </div>


                <div ref={contentContainerRef} className={`flex-1 flex-col overflow-hidden relative z-10 bg-white dark:bg-slate-900 ${mobileSection === 'note' ? 'flex' : 'hidden md:flex'} ${sidebarOnLeft ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
                    {activeTab === 'write' ? (
                        <>
                            <div className="flex-1 flex flex-col h-full relative min-w-0">
                                {/* Formatting Toolbar */}
                                {!readOnly && (
                                    <div className={`${mobileChromeCollapsed ? 'hidden md:flex' : 'flex'} items-center gap-2 px-3 py-2 border-b dark:border-slate-800 shrink-0 bg-gray-50 dark:bg-slate-950 overflow-x-auto`}>
                                        <Tip text={language === 'vietnamese' ? 'Đậm (Ctrl+B)' : 'Bold (Ctrl+B)'} side="bottom">
                                        <button
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleInsertMarkdown('**', '**')}
                                            className={`p-2 rounded shadow-sm border transition-colors ${isBoldActive ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600' : 'hover:bg-white dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-700'}`}
                                        >
                                            <Bold size={16} />
                                        </button>
                                        </Tip>

                                        {/* Case Toggle Button — desktop only, tucked into the "..." menu on mobile */}
                                        {!isMobile && (
                                        <Tip text={language === 'vietnamese' ? 'Chuyển HOA/thường (bôi chữ trước)' : 'Toggle UPPER/lower/Capitalize'} side="bottom">
                                        <button
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={handleToggleCase}
                                            className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded shadow-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700"
                                        >
                                            <CaseSensitive size={16} />
                                        </button>
                                        </Tip>
                                        )}


                                        {/* Math Picker Button */}
                                        <Tip text={t.math} side="bottom">
                                        <button
                                            onTouchStart={() => {
                                                // On mobile, touchstart fires BEFORE the textarea loses focus.
                                                // Saving here is the only reliable way to preserve cursor position on Android.
                                                isMathPickerInteractionRef.current = true;
                                                if (textareaRef.current) {
                                                    lastSelectionRef.current = {
                                                        start: textareaRef.current.selectionStart,
                                                        end: textareaRef.current.selectionEnd
                                                    };
                                                }
                                            }}
                                            onMouseDown={(e) => {
                                                e.preventDefault(); // Prevent losing focus if possible
                                                isMathPickerInteractionRef.current = true; // Signal that we are interacting with picker
                                                // Save current selection before opening modal
                                                if (textareaRef.current) {
                                                    lastSelectionRef.current = {
                                                        start: textareaRef.current.selectionStart,
                                                        end: textareaRef.current.selectionEnd
                                                    };
                                                }
                                            }}
                                            onClick={() => {
                                                isMathPickerInteractionRef.current = true; // Ensure ref is true
                                                // If in View Mode (!isEditing), switch to Edit Mode first
                                                if (!isEditing) {
                                                    setIsEditing(true);
                                                    // Default to end of text if switching from View Mode
                                                    setTimeout(() => {
                                                        if (textareaRef.current) {
                                                            const length = textareaRef.current.value.length;
                                                            lastSelectionRef.current = { start: length, end: length };
                                                            textareaRef.current.setSelectionRange(length, length);
                                                            textareaRef.current.focus();
                                                        }
                                                    }, 0);
                                                }
                                                posthog.capture('note_feature_used', { feature: 'math_picker' });
                                                setIsMathPickerOpen(true);
                                            }}
                                            className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded shadow-sm text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
                                        >
                                            <Sigma size={16} />
                                        </button>
                                        </Tip>

                                        {/* Quick Settings Button — desktop only, tucked into the "..." menu on mobile */}
                                        {onOpenSettings && !isMobile && (
                                            <Tip text={language === 'vietnamese' ? 'Cài đặt ký hiệu Toán' : 'Settings (Math Symbols)'} side="bottom">
                                            <button
                                                onClick={onOpenSettings}
                                                className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded shadow-sm text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 border border-transparent hover:border-blue-200 dark:hover:border-blue-900 transition-colors"
                                            >
                                                <Settings size={16} />
                                            </button>
                                            </Tip>
                                        )}


                                        {/* Overflow menu — always visible (mobile + desktop) */}
                                        <div className="relative">
                                            <button
                                                ref={mobileToolsButtonRef}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    if (!showMobileToolsMenu && mobileToolsButtonRef.current) {
                                                        const rect = mobileToolsButtonRef.current.getBoundingClientRect();
                                                        setMobileToolsMenuPos({ top: rect.bottom + 4, left: rect.left });
                                                    }
                                                    setShowMobileToolsMenu(v => !v);
                                                }}
                                                className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded shadow-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700"
                                                title={language === 'vietnamese' ? 'Thêm công cụ' : 'More tools'}
                                            >
                                                <MoreHorizontal size={16} />
                                            </button>
                                            {showMobileToolsMenu && createPortal(
                                                <>
                                                    <div className="fixed inset-0 z-[199]" onClick={() => setShowMobileToolsMenu(false)} />
                                                    <div
                                                        className="fixed z-[200] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden w-56"
                                                        style={{ top: mobileToolsMenuPos.top, left: mobileToolsMenuPos.left }}
                                                    >
                                                        <button
                                                            onClick={() => { setShowMobileToolsMenu(false); handleToggleCase(); }}
                                                            className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 text-gray-700 dark:text-gray-200"
                                                        >
                                                            <CaseSensitive size={16} className="text-gray-400 shrink-0" />
                                                            {language === 'vietnamese' ? 'Chuyển HOA/thường' : 'Toggle UPPER/lower/Capitalize'}
                                                        </button>
                                                        <button
                                                            onClick={() => { setShowMobileToolsMenu(false); handleReflowSelection(); }}
                                                            className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 text-gray-700 dark:text-gray-200"
                                                        >
                                                            <WrapText size={16} className="text-gray-400 shrink-0" />
                                                            {language === 'vietnamese' ? 'Gộp dòng gãy thành đoạn' : 'Reflow broken lines'}
                                                        </button>
                                                        {onOpenSettings && (
                                                            <button
                                                                onClick={() => { setShowMobileToolsMenu(false); onOpenSettings(); }}
                                                                className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 text-gray-700 dark:text-gray-200"
                                                            >
                                                                <Settings size={16} className="text-gray-400 shrink-0" />
                                                                {language === 'vietnamese' ? 'Cài đặt ký hiệu Toán' : 'Settings (Math Symbols)'}
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => { setShowMobileToolsMenu(false); handleConvertToWritten(); }}
                                                            disabled={isConvertingToWritten || !localContent?.trim()}
                                                            className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 text-gray-700 dark:text-gray-200 disabled:opacity-40"
                                                        >
                                                            {isConvertingToWritten ? <Loader2 size={16} className="animate-spin text-gray-400 shrink-0" /> : <PenLine size={16} className="text-gray-400 shrink-0" />}
                                                            {language === 'vietnamese' ? 'Chuyển văn nói → văn viết' : 'Convert spoken → written'}
                                                        </button>
                                                    </div>
                                                </>,
                                                document.body
                                            )}
                                        </div>

                                        {/* Font Size Control */}
                                        <div className="flex items-center gap-0.5 px-1 py-0.5 bg-gray-100 dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700">
                                            <Tip text={language === 'vietnamese' ? 'Giảm cỡ chữ (Ctrl+Shift+[)' : 'Decrease font size (Ctrl+Shift+[)'} side="bottom">
                                            <button
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => { const i = FONT_SIZES.indexOf(editorFontSize); if (i > 0) setEditorFontSize(FONT_SIZES[i - 1]); }}
                                                disabled={editorFontSize <= FONT_SIZES[0]}
                                                className="px-1.5 py-0.5 text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 transition-colors"
                                            >
                                                A<sup>-</sup>
                                            </button>
                                            </Tip>
                                            <Tip text={language === 'vietnamese' ? 'Tăng cỡ chữ (Ctrl+Shift+])' : 'Increase font size (Ctrl+Shift+])'} side="bottom">
                                            <button
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => { const i = FONT_SIZES.indexOf(editorFontSize); if (i < FONT_SIZES.length - 1) setEditorFontSize(FONT_SIZES[i + 1]); }}
                                                disabled={editorFontSize >= FONT_SIZES[FONT_SIZES.length - 1]}
                                                className="px-1.5 py-0.5 text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 transition-colors"
                                            >
                                                A<sup>+</sup>
                                            </button>
                                            </Tip>
                                        </div>

                                        {/* Hint button — only when note has a source image */}
                                        {node.imageBase64 && !readOnly && (
                                            <Tip text={language === 'vietnamese' ? 'AI gợi ý bước tiếp theo' : 'AI hint for next step'} side="bottom">
                                            <button
                                                onClick={async () => {
                                                    posthog.capture('note_feature_used', { feature: 'ai_hint' });
                                                    if (hintResult || isHinting) {
                                                        setHintResult(null);
                                                        setShowHintNudge(false);
                                                        return;
                                                    }
                                                    setShowHintNudge(false);
                                                    setIsHinting(true);
                                                    hintDismissedAtRef.current = 0;
                                                    try {
                                                        const hint = await callHintForNote(node.imageBase64!, node.content || '', language, node.title || undefined);
                                                        setHintResult(hint);
                                                    } catch {
                                                        setHintResult(language === 'vietnamese' ? 'Không thể tải gợi ý lúc này.' : 'Unable to load hint right now.');
                                                    } finally {
                                                        setIsHinting(false);
                                                    }
                                                }}
                                                className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
                                                    hintResult
                                                        ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300'
                                                        : 'bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-300 dark:hover:border-amber-700'
                                                }`}
                                                disabled={isHinting}
                                            >
                                                {isHinting ? <Loader2 size={12} className="animate-spin" /> : <Lightbulb size={12} />}
                                                {language === 'vietnamese' ? 'Gợi ý' : 'Hint'}
                                            </button>
                                            </Tip>
                                        )}

                                        {/* Combined voice button: mode toggle (left) + trigger (right) */}
                                        {!(isRecording || isListening || isTranscribing) && (
                                            <div className="flex items-stretch rounded border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
                                                {/* Left: mode switcher — click to toggle Real-time / Gemini */}
                                                {!isMobile && (
                                                    <Tip text={useRealTimeDictation
                                                        ? (language === 'vietnamese' ? 'Chế độ: Real-time\nBấm để đổi sang Gemini AI' : 'Mode: Real-time\nClick to switch to Gemini AI')
                                                        : (language === 'vietnamese' ? 'Chế độ: Gemini AI\nBấm để đổi sang Real-time' : 'Mode: Gemini AI\nClick to switch to Real-time')} side="bottom">
                                                    <button
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => setUseRealTimeDictation(!useRealTimeDictation)}
                                                        className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border-r border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap text-gray-500 dark:text-gray-400"
                                                    >
                                                        {useRealTimeDictation ? 'Real-time' : 'Gemini'}
                                                    </button>
                                                    </Tip>
                                                )}
                                                {/* Right: trigger recording */}
                                                <Tip text={useRealTimeDictation
                                                    ? (language === 'vietnamese' ? 'Ghi âm Real-time' : 'Real-time voice')
                                                    : (language === 'vietnamese' ? 'Ghi âm Gemini AI' : 'Gemini AI voice')} side="bottom">
                                                <button
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => { posthog.capture('note_feature_used', { feature: 'voice_recording', mode: useRealTimeDictation ? 'realtime' : 'gemini' }); handleVoiceRecording(); }}
                                                    className="p-2 bg-white dark:bg-slate-900 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 text-gray-700 dark:text-gray-300 transition-colors"
                                                >
                                                    <Mic size={16} />
                                                </button>
                                                </Tip>
                                            </div>
                                        )}

                                        {/* Draw button — opens drawing pad */}
                                        <Tip text={language === 'vietnamese' ? 'Mở bảng vẽ' : 'Open drawing pad'} side="bottom">
                                        <button
                                            onClick={() => { posthog.capture('drawing_opened'); setActiveTab('draw'); }}
                                            className="p-2 rounded border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-700 hover:text-purple-600 dark:hover:text-purple-400 text-gray-500 transition-colors"
                                        >
                                            <PenTool size={15} />
                                        </button>
                                        </Tip>
                                        {onDrawOnScreen && (
                                            <Tip text={language === 'vietnamese' ? 'Vẽ lên màn hình (Ctrl/⌘+Shift+D)' : 'Draw on screen (Ctrl/⌘+Shift+D)'} side="bottom">
                                            <button
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => { posthog.capture('note_feature_used', { feature: 'draw_on_screen' }); onDrawOnScreen(); }}
                                                className="p-2 rounded border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-700 hover:text-purple-600 dark:hover:text-purple-400 text-gray-500 transition-colors"
                                            >
                                                <Highlighter size={15} />
                                            </button>
                                            </Tip>
                                        )}

                                        {/* Edit / Done toggle — pushed to the right end of the toolbar */}
                                        <div className="ml-auto flex items-center gap-2 shrink-0">
                                            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700" />
                                            {!isEditing ? (
                                                <Tip text={language === 'vietnamese' ? 'Chỉnh sửa ghi chú' : 'Edit note'} side="bottom">
                                                <button
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={handleEnterEdit}
                                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium text-sm transition-colors"
                                                >
                                                    <Edit size={14} />
                                                    {language === 'vietnamese' ? 'Sửa' : 'Edit'}
                                                </button>
                                                </Tip>
                                            ) : (
                                                <Tip text={language === 'vietnamese' ? 'Hoàn thành chỉnh sửa' : 'Done editing'} side="bottom">
                                                <button
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={handleDone}
                                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400 font-medium text-sm transition-colors"
                                                >
                                                    <Check size={14} />
                                                    {language === 'vietnamese' ? 'Xong' : 'Done'}
                                                </button>
                                                </Tip>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Convert-to-written undo hint banner */}
                                {showConvertBanner && (
                                    <div className="mx-3 mt-2 shrink-0 rounded-xl border border-violet-200 dark:border-violet-800/50 bg-violet-50 dark:bg-violet-900/20 px-4 py-2.5 flex items-center justify-between gap-3 animate-in slide-in-from-top-2 duration-200">
                                        <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300 text-sm">
                                            <CheckCircle2 size={15} className="shrink-0" />
                                            <span>
                                                {language === 'vietnamese'
                                                    ? <>Đã chuyển văn nói → văn viết. Nhấn <kbd className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-800 font-mono text-xs">⌘Z / Ctrl+Z</kbd> để hoàn tác.</>
                                                    : <>Converted to written style. Press <kbd className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-800 font-mono text-xs">⌘Z / Ctrl+Z</kbd> to undo.</>
                                                }
                                            </span>
                                        </div>
                                        <button onClick={() => setShowConvertBanner(false)} className="text-violet-400 hover:text-violet-600 dark:hover:text-violet-200 shrink-0">
                                            <XCircle size={16} />
                                        </button>
                                    </div>
                                )}

                                {/* Quick Check Result Banner */}
                                {quickCheckResult && (
                                    <div className={`mx-3 mt-2 shrink-0 rounded-xl border px-4 py-3 animate-in slide-in-from-top-2 duration-200 ${
                                        quickCheckResult.status === 'on_track'
                                            ? 'bg-green-50 dark:bg-green-900/15 border-green-200 dark:border-green-800/40 text-green-800 dark:text-green-200'
                                            : quickCheckResult.status === 'caution'
                                            ? 'bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800/40 text-amber-800 dark:text-amber-200'
                                            : 'bg-red-50 dark:bg-red-900/15 border-red-200 dark:border-red-800/40 text-red-800 dark:text-red-200'
                                    }`}>
                                        {/* Verdict row */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                {quickCheckResult.status === 'on_track'
                                                    ? <CheckCircle2 size={15} className="shrink-0 mt-0.5" />
                                                    : quickCheckResult.status === 'caution'
                                                    ? <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                                                    : <XCircle size={15} className="shrink-0 mt-0.5" />}
                                                <span className="text-sm font-semibold">{quickCheckResult.verdict}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                {quickCheckResult.status === 'critical' && (
                                                    <button
                                                        onClick={() => { setQuickCheckResult(null); aiCheckTriggeredRef.current = true; onAnalyze(node.id, 'detailed'); }}
                                                        className="text-[11px] font-bold px-2 py-1 rounded-lg border border-current hover:opacity-70 transition-opacity whitespace-nowrap"
                                                    >
                                                        {language === 'vietnamese' ? 'Chấm điểm đầy đủ' : 'Full AI Check'}
                                                    </button>
                                                )}
                                                <button onClick={() => { setQuickCheckResult(null); setQuickCheckPinpoint({ isLoading: false, errorPoints: null }); }} className="opacity-50 hover:opacity-100 transition-opacity">
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        {/* Confirmed points */}
                                        {quickCheckResult.confirmedPoints?.length > 0 && (
                                            <ul className="mt-2 space-y-1 pl-1">
                                                {quickCheckResult.confirmedPoints.map((pt, i) => (
                                                    <li key={i} className="flex gap-2 text-xs leading-snug">
                                                        <span className="shrink-0 opacity-60">•</span>
                                                        <span>{pt}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {/* Next caution */}
                                        {quickCheckResult.nextCaution && (
                                            <p className="mt-2 text-xs opacity-75 leading-snug border-t border-current/10 pt-2">
                                                → {quickCheckResult.nextCaution}
                                            </p>
                                        )}

                                        {/* Pinpoint follow-up — only for caution/critical */}
                                        {quickCheckResult.status !== 'on_track' && (
                                            <div className="mt-2 pt-2 border-t border-current/10">
                                                {quickCheckPinpoint.errorPoints !== null ? (
                                                    <div className="space-y-1.5">
                                                        {quickCheckPinpoint.errorPoints.length === 0 ? (
                                                            <p className="text-xs opacity-70 italic">{language === 'vietnamese' ? 'Không tìm được câu lỗi cụ thể nào.' : 'No specific error quote found.'}</p>
                                                        ) : (
                                                            quickCheckPinpoint.errorPoints.map((ep, i) => (
                                                                <div key={i} className="border-l-2 border-current/40 pl-2 py-0.5">
                                                                    <p className="text-xs italic opacity-85">"{ep.quote}"</p>
                                                                    <p className="text-xs opacity-75 mt-0.5">→ {ep.explanation}</p>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={handlePinpointFromQuickCheck}
                                                        disabled={quickCheckPinpoint.isLoading}
                                                        className="flex items-center gap-1.5 text-xs font-semibold opacity-75 hover:opacity-100 underline underline-offset-2 decoration-dotted disabled:opacity-40 transition-opacity"
                                                    >
                                                        {quickCheckPinpoint.isLoading ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
                                                        {language === 'vietnamese' ? 'Chỉ ra lỗi cụ thể trong bài' : 'Show me the exact error'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {showFindReplace && (
                                    <div className="flex-shrink-0 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-slate-800 px-3 py-2 flex flex-col gap-1.5">
                                        <div className="flex items-center gap-2">
                                            <Search size={14} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                            <input
                                                ref={findBarInputRef}
                                                type="text"
                                                value={findQuery}
                                                onChange={e => setFindQuery(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        const next = (matchIndex + 1) % Math.max(totalMatches, 1);
                                                        setMatchIndex(next);
                                                        richEditorRef.current?.goToMatch(findQuery, next, matchCase);
                                                        requestAnimationFrame(() => findBarInputRef.current?.focus());
                                                    }
                                                    if (e.key === 'Escape') { setShowFindReplace(false); richEditorRef.current?.focus(); }
                                                }}
                                                placeholder={language === 'vietnamese' ? 'Tìm kiếm...' : 'Find...'}
                                                className="flex-1 text-sm bg-white dark:bg-slate-700 border border-amber-300 dark:border-amber-700 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-amber-400 dark:focus:ring-amber-500 text-gray-800 dark:text-gray-200 placeholder-gray-400"
                                            />
                                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap min-w-[40px] text-center">
                                                {totalMatches > 0 ? `${matchIndex + 1}/${totalMatches}` : (findQuery ? '0/0' : '')}
                                            </span>
                                            <button
                                                onClick={() => setMatchCase(v => !v)}
                                                title={matchCase ? 'Case sensitive (click to ignore case)' : 'Ignore case (click for exact match)'}
                                                className={`px-1.5 py-0.5 rounded text-[11px] font-bold border transition-colors ${matchCase ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-300 dark:border-slate-600 text-gray-400 hover:border-amber-400 hover:text-amber-500'}`}
                                            >Aa</button>
                                            <button
                                                onClick={() => {
                                                    const prev = ((matchIndex - 1) + Math.max(totalMatches, 1)) % Math.max(totalMatches, 1);
                                                    setMatchIndex(prev);
                                                    richEditorRef.current?.goToMatch(findQuery, prev, matchCase);
                                                    requestAnimationFrame(() => findBarInputRef.current?.focus());
                                                }}
                                                disabled={totalMatches === 0}
                                                className="p-1 rounded hover:bg-amber-100 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors"
                                                title="Previous match"
                                            >
                                                <ChevronUp size={14} />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const next = (matchIndex + 1) % Math.max(totalMatches, 1);
                                                    setMatchIndex(next);
                                                    richEditorRef.current?.goToMatch(findQuery, next, matchCase);
                                                    requestAnimationFrame(() => findBarInputRef.current?.focus());
                                                }}
                                                disabled={totalMatches === 0}
                                                className="p-1 rounded hover:bg-amber-100 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors"
                                                title="Next match"
                                            >
                                                <ChevronDown size={14} />
                                            </button>
                                            <button
                                                onClick={() => { setShowFindReplace(false); setFindQuery(''); setReplaceText(''); richEditorRef.current?.focus(); }}
                                                className="p-1 rounded hover:bg-amber-100 dark:hover:bg-slate-700 transition-colors text-gray-500"
                                                title="Close"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                        {!readOnly && (
                                            <div className="flex items-center gap-2">
                                                <div className="w-[14px] flex-shrink-0" />
                                                <input
                                                    type="text"
                                                    value={replaceText}
                                                    onChange={e => setReplaceText(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Escape') { setShowFindReplace(false); richEditorRef.current?.focus(); } }}
                                                    placeholder={language === 'vietnamese' ? 'Thay thế bằng...' : 'Replace with...'}
                                                    className="flex-1 text-sm bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-amber-400 dark:focus:ring-amber-500 text-gray-800 dark:text-gray-200 placeholder-gray-400"
                                                />
                                                <button
                                                    onClick={() => {
                                                        if (!findQuery) return;
                                                        const replaced = richEditorRef.current?.replaceCurrent(findQuery, replaceText, matchIndex, matchCase);
                                                        if (replaced) {
                                                            const newCount = richEditorRef.current?.findMatches(findQuery, matchCase) ?? 0;
                                                            setTotalMatches(newCount);
                                                            const newIdx = Math.min(matchIndex, Math.max(newCount - 1, 0));
                                                            setMatchIndex(newIdx);
                                                            if (newCount > 0) richEditorRef.current?.goToMatch(findQuery, newIdx, matchCase);
                                                        }
                                                    }}
                                                    disabled={!findQuery || totalMatches === 0}
                                                    className="text-xs px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-40 transition-colors whitespace-nowrap"
                                                >
                                                    {language === 'vietnamese' ? 'Thay' : 'Replace'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!findQuery) return;
                                                        const count = richEditorRef.current?.replaceAll(findQuery, replaceText, matchCase) ?? 0;
                                                        setTotalMatches(0);
                                                        setMatchIndex(0);
                                                        if (count > 0) {
                                                            const md = richEditorRef.current?.getMarkdown?.() ?? '';
                                                            setLocalContent(md, { forceSnapshot: false });
                                                            onUpdate(node.id, { content: md });
                                                        }
                                                    }}
                                                    disabled={!findQuery || totalMatches === 0}
                                                    className="text-xs px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-40 transition-colors whitespace-nowrap"
                                                >
                                                    {language === 'vietnamese' ? 'Thay tất cả' : 'Replace All'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div
                                    ref={noteContentRef}
                                    className="flex-1 overflow-y-auto hide-scrollbar relative flex flex-col no-dblclick"
                                    style={isAnyTutorSpeaking ? { borderRadius: 8, transition: 'box-shadow 0.05s linear' } : { transition: 'box-shadow 0.4s ease-out' }}
                                >
                                    {/* Mobile: floating Edit button, bottom-right — reachable by thumb, out of the
                                        crowded top chrome. Only relevant in View Mode (tapping content doesn't
                                        enter Edit Mode on mobile the way it does on desktop). */}
                                    {isMobile && !showRaw && mobileSection === 'note' && activeTab === 'write' && !isEditing && !readOnly && !!localContent?.trim() && (
                                        <button
                                            onClick={() => { setIsEditing(true); setTimeout(() => richEditorRef.current?.focus(), 0); }}
                                            className="fixed bottom-20 right-4 z-30 flex items-center gap-1.5 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-transform active:scale-95"
                                        >
                                            <Edit size={16} />
                                            <span className="text-sm font-bold">{language === 'vietnamese' ? 'Sửa' : 'Edit'}</span>
                                        </button>
                                    )}
                                    {/* Writing hint carousel — rotates through fixed + AI hints like a banner */}
                                    {!showRaw && !!node.imageBase64 && (node.content || '').trim().length < 200 && !writingHintDismissed && !readOnly && (() => {
                                        const fixedVi = [
                                            'Hãy thử giải thích nội dung này bằng lời của bạn — không cần hoàn hảo!',
                                            'Bạn hiểu ý chính ở đây là gì? Viết ra thử xem.',
                                            'Nếu giải thích cho bạn bè, bạn sẽ nói gì về đoạn này?',
                                        ];
                                        const fixedEn = [
                                            'Try to explain this in your own words — no need to be perfect!',
                                            'What is the main idea here? Try writing it out.',
                                            'If you were explaining this to a friend, what would you say?',
                                        ];
                                        const fixedPool = language === 'vietnamese' ? fixedVi : fixedEn;
                                        const hintsPool = node.writingHint ? [...fixedPool, node.writingHint] : fixedPool;
                                        const currentHint = hintsPool[hintIndex % hintsPool.length];
                                        return (
                                            <div className="px-3 pt-2">
                                                <div
                                                    className="flex items-center gap-2 px-2.5 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg"
                                                    onMouseEnter={() => { hintHoveredRef.current = true; }}
                                                    onMouseLeave={() => { hintHoveredRef.current = false; }}
                                                >
                                                    <span className="text-amber-500 text-sm shrink-0">💡</span>
                                                    <p
                                                        className="flex-1 text-xs text-amber-800 dark:text-amber-200 leading-snug italic line-clamp-2 min-w-0"
                                                        style={{ transition: 'opacity 0.35s ease', opacity: hintVisible ? 1 : 0 }}
                                                    >{currentHint}</p>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        {hintsPool.length > 1 && hintsPool.map((_, i) => (
                                                            <span key={i} className={`w-1 h-1 rounded-full transition-colors ${i === hintIndex % hintsPool.length ? 'bg-amber-500' : 'bg-amber-200 dark:bg-amber-700'}`} />
                                                        ))}
                                                        <button onClick={() => setWritingHintDismissed(true)} className="ml-0.5 p-0.5 text-amber-400 hover:text-amber-600 dark:hover:text-amber-200 transition-colors">
                                                            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M10 2L2 10M2 2l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {/* Raw markdown editable view */}
                                    {showRaw && (
                                        <div className="flex-1 flex flex-col p-4 gap-2">
                                            <div className="flex items-center gap-2 text-[11px] text-amber-600 dark:text-amber-400 font-mono font-semibold select-none">
                                                <span>{'</>'}</span>
                                                <span>Raw Markdown — {localContent?.length ?? 0} chars</span>
                                                <span className="text-amber-400 dark:text-amber-600 font-normal">· editable</span>
                                            </div>
                                            <textarea
                                                className="flex-1 text-xs font-mono bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-lg p-4 whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300 overflow-y-auto leading-relaxed resize-none outline-none focus:border-amber-400 dark:focus:border-amber-600"
                                                value={localContent || ''}
                                                spellCheck={false}
                                                onChange={e => {
                                                    const newText = e.target.value;
                                                    setLocalContent(newText);
                                                    if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
                                                    debouncedUpdateRef.current = setTimeout(() => {
                                                        onUpdate(node.id, { content: newText });
                                                    }, 400);
                                                }}
                                            />
                                        </div>
                                    )}
                                    {!readOnly && onResolveTextConflict && (node.textConflicts?.length ?? 0) > 0 && (
                                        <TextConflictBanner
                                            copies={node.textConflicts!}
                                            language={language}
                                            onRestore={(i) => onResolveTextConflict('restore', i)}
                                            onDismiss={(i) => onResolveTextConflict('dismiss', i)}
                                        />
                                    )}
                                    {!showRaw && (ENABLE_RICH_TEXT_EDITOR ? (() => {
                                        const inViewMode = (!isEditing || readOnly) && !!localContent?.trim() && !showFindReplace;
                                        return (
                                            <>
                                                {/* HTML render overlay — shown in view mode for proper math/KaTeX rendering */}
                                                {inViewMode && (
                                                    <div
                                                        className="w-full flex-1 p-4 rounded-lg leading-relaxed text-gray-700 dark:text-gray-200"
                                                    >
                                                        <div className="prose dark:prose-invert max-w-none leading-relaxed [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4" style={{ fontSize: `${editorFontSize}px` }} dangerouslySetInnerHTML={{ __html: displayedContentHTML }} />
                                                    </div>
                                                )}
                                                {/* Editor only mounted in edit mode — unmounting in view mode prevents
                                                    ProseMirror's document-level selectionchange listener from clearing
                                                    text selections the user makes in the HTML overlay. */}
                                                {!inViewMode && <div className="flex-1 min-h-0 flex flex-col">
                                                <RichTextEditor
                                                key={node.id}
                                                ref={richEditorRef}
                                                initialValue={localContent}
                                                style={{ fontSize: `${editorFontSize}px` }}
                                                onActiveFormatsChange={(formats) => setIsBoldActive(formats.bold)}
                                                onChange={(md) => {
                                                    // onChange is now only called on blur (flush) — not during typing.
                                                    // Save immediately when editor loses focus.
                                                    if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
                                                    setLocalContent(md, { forceSnapshot: false });
                                                    onUpdate(node.id, { content: md });
                                                }}
                                                onInput={() => {
                                                    // Cheap dirty signal fired on every keystroke — no serialization, no re-render.
                                                    // Debounce the actual save; read fresh markdown only when idle window expires.
                                                    if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
                                                    debouncedUpdateRef.current = setTimeout(() => {
                                                        if (!richEditorRef.current?.getMarkdown) return;
                                                        const md = richEditorRef.current.getMarkdown();
                                                        setLocalContent(md, { forceSnapshot: false });
                                                        onUpdate(node.id, { content: md });
                                                    }, 1000);
                                                }}
                                                readOnly={!isEditing || readOnly}
                                                searchQuery={externalFindQuery || (showFindReplace ? findQuery : '')}
                                                searchCaseSensitive={showFindReplace ? matchCase : false}
                                                currentMatchIndex={showFindReplace ? matchIndex : -1}
                                                autoFocus={!readOnly && !localContent?.trim()}
                                                onFocus={() => {}}
                                                onBlur={() => {}}
                                                onKeyDown={(event: KeyboardEvent) => {
                                                    if (readOnly) return false;
                                                    if (autocomplete.visible) {
                                                        if (event.key === 'ArrowDown') {
                                                            event.preventDefault();
                                                            setAutocomplete(prev => ({ ...prev, selectedIndex: Math.min(prev.selectedIndex + 1, prev.suggestions.length - 1) }));
                                                            return true;
                                                        }
                                                        if (event.key === 'ArrowUp') {
                                                            event.preventDefault();
                                                            setAutocomplete(prev => ({ ...prev, selectedIndex: Math.max(prev.selectedIndex - 1, 0) }));
                                                            return true;
                                                        }
                                                        if (event.key === 'Escape') {
                                                            event.preventDefault();
                                                            setAutocomplete(prev => ({ ...prev, visible: false }));
                                                            return true;
                                                        }
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            const selected = autocomplete.suggestions[autocomplete.selectedIndex];
                                                            if (selected) handleAutocompleteSelect(selected);
                                                            return true;
                                                        }
                                                        // Typing any non-nav key narrows the autocomplete filter
                                                        if (event.key.length === 1) {
                                                            setTimeout(() => refreshAutocompleteFilter(), 0);
                                                        }
                                                    }
                                                    // `\` triggers math autocomplete — but NOT when Ctrl/Meta is held
                                                    if (event.key === '\\' && !event.ctrlKey && !event.metaKey) {
                                                        setTimeout(() => triggerAutocomplete('\\'), 0);
                                                    }
                                                    if ((event.ctrlKey && event.key === ' ') ||
                                                        (event.altKey && event.key === ' ') ||
                                                        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k')) {
                                                        event.preventDefault();
                                                        triggerAutocomplete();
                                                        return true;
                                                    }
                                                    // Outline toggle: Ctrl+\
                                                    if ((event.ctrlKey || event.metaKey) && event.key === '\\') {
                                                        event.preventDefault();
                                                        if (!isMobileViewport) onToggleOutline?.();
                                                        return true;
                                                    }
                                                    // Font size: Ctrl+Shift+] (increase) / Ctrl+Shift+[ (decrease)
                                                    // Use event.code (physical key) so it works regardless of Shift producing } or ]
                                                    if (event.ctrlKey && event.shiftKey && event.code === 'BracketRight') {
                                                        event.preventDefault();
                                                        const i = FONT_SIZES.indexOf(editorFontSize);
                                                        if (i < FONT_SIZES.length - 1) setEditorFontSize(FONT_SIZES[i + 1]);
                                                        return true;
                                                    }
                                                    if (event.ctrlKey && event.shiftKey && event.code === 'BracketLeft') {
                                                        event.preventDefault();
                                                        const i = FONT_SIZES.indexOf(editorFontSize);
                                                        if (i > 0) setEditorFontSize(FONT_SIZES[i - 1]);
                                                        return true;
                                                    }
                                                    return false;
                                                }}
                                            />
                                                </div>}
                                            </>
                                        );
                                    })() : (
                                    <div className="flex-1 p-3 md:p-8 relative">
                                    {!isEditing && localContent && localContent.length > 0 ? (
                                        <div
                                            onDoubleClick={(e) => {
                                                if (!readOnly) {
                                                    const target = e.currentTarget;
                                                    const rect = target.getBoundingClientRect();
                                                    const clickY = e.clientY - rect.top + target.scrollTop;

                                                    setIsEditing(true);

                                                    setTimeout(() => {
                                                        if (textareaRef.current) {
                                                            const textarea = textareaRef.current;
                                                            textarea.focus();

                                                            const computedStyle = getComputedStyle(target);
                                                            const lineHeight = parseInt(computedStyle.lineHeight) || 28;

                                                            const lines = localContent.split('\n');
                                                            const clickedLine = Math.min(Math.floor(clickY / lineHeight), lines.length - 1);

                                                            let cursorPos = 0;
                                                            for (let i = 0; i < clickedLine; i++) {
                                                                cursorPos += lines[i].length + 1;
                                                            }

                                                            textarea.setSelectionRange(cursorPos, cursorPos);

                                                            const textareaLineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
                                                            const scrollTop = Math.max(0, clickedLine * textareaLineHeight - textarea.clientHeight / 2);
                                                            textarea.scrollTop = scrollTop;
                                                        }
                                                    }, 0);
                                                }
                                            }}
                                            className={`w-full flex-1 p-4 rounded-lg leading-relaxed text-gray-700 dark:text-gray-200 ${!readOnly ? 'cursor-text hover:bg-yellow-50/30 dark:hover:bg-slate-800/30 transition-colors' : ''}`}
                                        >
                                            <div className="prose dark:prose-invert max-w-none leading-relaxed [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4" style={{ fontSize: `${editorFontSize}px` }} dangerouslySetInnerHTML={{ __html: displayedContentHTML }} />
                                        </div>
                                    ) : (
                                        <textarea
                                            ref={textareaRef}
                                            value={localContent}
                                            onChange={(e) => handleTextChange(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            onSelect={() => {
                                                if (textareaRef.current) {
                                                    lastSelectionRef.current = {
                                                        start: textareaRef.current.selectionStart,
                                                        end: textareaRef.current.selectionEnd
                                                    };
                                                }
                                            }}
                                            onBlur={() => {
                                                if (textareaRef.current) {
                                                    lastSelectionRef.current = {
                                                        start: textareaRef.current.selectionStart,
                                                        end: textareaRef.current.selectionEnd
                                                    };
                                                }
                                                setTimeout(() => {
                                                    if (isMathPickerOpen || isMathPickerInteractionRef.current) return;
                                                    if (document.activeElement === textareaRef.current) return;
                                                    if (!document.hasFocus()) return;
                                                    setIsEditing(false);
                                                }, 100);
                                            }}
                                            onFocus={() => { if (!readOnly) setIsEditing(true); }}
                                            autoFocus={isEditing}
                                            disabled={readOnly}
                                            placeholder={t.placeholder}
                                            className="w-full h-full resize-none outline-none leading-relaxed text-gray-700 dark:text-gray-200 bg-transparent placeholder-gray-300 dark:placeholder-slate-600 font-sans pb-32 disabled:opacity-100 disabled:text-gray-800 dark:disabled:text-gray-100"
                                            style={{ fontSize: `${editorFontSize}px` }}
                                            spellCheck={false}
                                        />
                                    )}
                                    </div>
                                    ))}

                                    {/* Math Autocomplete Popup */}
                                    <MathAutocomplete
                                        visible={autocomplete.visible}
                                        position={autocomplete.position}
                                        suggestions={autocomplete.suggestions}
                                        selectedIndex={autocomplete.selectedIndex}
                                        onSelect={handleAutocompleteSelect}
                                        onClose={() => setAutocomplete(prev => ({ ...prev, visible: false }))}
                                    />

                                    {!readOnly && (
                                        <>
                                            {/* Voice Panel: shown while recording/listening/transcribing */}
                                            {(isRecording || isListening || isTranscribing) ? (
                                                <div className="sticky bottom-4 z-20 mx-3 mb-2 animate-in slide-in-from-bottom-3 duration-250">
                                                    <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl border ${
                                                        isTranscribing
                                                            ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700'
                                                            : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600'
                                                    }`}>
                                                        {/* Icon */}
                                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                            isTranscribing ? 'bg-indigo-100 dark:bg-indigo-800/50' : 'bg-red-100 dark:bg-red-900/30'
                                                        }`}>
                                                            {isTranscribing
                                                                ? <Loader2 size={17} className="text-indigo-600 dark:text-indigo-400 animate-spin" />
                                                                : <Mic size={17} className="text-red-500 dark:text-red-400" />
                                                            }
                                                        </div>

                                                        {/* Waveform / status */}
                                                        <div className="flex-1 flex items-center gap-2.5 min-w-0">
                                                            {isTranscribing ? (
                                                                <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                                                    {language === 'vietnamese' ? 'Đang xử lý...' : 'Processing...'}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    {/* Animated waveform bars */}
                                                                    <div className="flex items-end gap-[3px]" style={{ height: 22 }}>
                                                                        {[0.55, 1, 0.7, 0.85, 0.45, 0.9, 0.6].map((h, i) => (
                                                                            <div
                                                                                key={i}
                                                                                className="w-[3px] rounded-full bg-red-500 dark:bg-red-400"
                                                                                style={{
                                                                                    height: `${h * 100}%`,
                                                                                    animation: `sbVoiceWave ${0.7 + i * 0.05}s ease-in-out infinite alternate`,
                                                                                    animationDelay: `${i * 0.1}s`,
                                                                                    transformOrigin: 'bottom',
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                    <span className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                                        {language === 'vietnamese' ? 'Đang lắng nghe...' : 'Listening...'}
                                                                    </span>
                                                                    {/* Countdown for Gemini mode */}
                                                                    {isRecording && (
                                                                        <span className={`ml-auto text-xs font-mono tabular-nums flex-shrink-0 ${
                                                                            recordingSeconds >= MAX_RECORDING_SECONDS - 30
                                                                                ? 'text-orange-500 dark:text-orange-400 font-bold'
                                                                                : 'text-gray-400 dark:text-slate-500'
                                                                        }`}>
                                                                            {Math.floor(recordingSeconds / 60)}:{String(recordingSeconds % 60).padStart(2, '0')} / 3:00
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>

                                                        {/* Action buttons */}
                                                        {!isTranscribing && (
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                {/* Cancel */}
                                                                <Tip text={language === 'vietnamese' ? 'Hủy ghi âm' : 'Cancel recording'} side="top">
                                                                <button
                                                                    onClick={handleCancelVoice}
                                                                    className="w-9 h-9 rounded-full bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center transition-colors"
                                                                >
                                                                    <X size={15} className="text-gray-600 dark:text-gray-300" />
                                                                </button>
                                                                </Tip>
                                                                {/* Confirm — Gemini mode only (stop + send) */}
                                                                {!useRealTimeDictation && isRecording && (
                                                                    <Tip text={language === 'vietnamese' ? 'Xong, gửi đi' : 'Done, send'} side="top">
                                                                    <button
                                                                        onClick={handleVoiceRecording}
                                                                        className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center transition-colors shadow-md"
                                                                    >
                                                                        <Check size={15} className="text-white" />
                                                                    </button>
                                                                    </Tip>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Mode label */}
                                                    <p className="text-center text-[10px] text-gray-400 dark:text-slate-500 mt-1 font-medium tracking-wide">
                                                        {useRealTimeDictation
                                                            ? (language === 'vietnamese' ? 'Soạn bằng giọng nói · Real-time' : 'Voice input · Real-time')
                                                            : (language === 'vietnamese' ? 'Soạn bằng giọng nói · Gemini AI' : 'Voice input · Gemini AI')
                                                        }
                                                    </p>
                                                </div>
                                            ) : (
                                                <>

                                                    {/* Retry button after Gemini transcription failure */}
                                                    {hasTranscriptionError && savedAudioBlobRef.current && !useRealTimeDictation && (
                                                        <div className="absolute bottom-4 right-16 z-20 flex flex-col items-end gap-1 animate-in fade-in slide-in-from-right-4 duration-300">
                                                            <span className="text-[10px] font-bold text-orange-600 bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 rounded-full border border-orange-200 dark:border-orange-800 shadow-sm">
                                                                {language === 'vietnamese' ? 'Chưa gửi được' : 'Upload Failed'}
                                                            </span>
                                                            <button
                                                                onClick={handleRetryTranscription}
                                                                disabled={isTranscribing}
                                                                className="h-12 px-5 rounded-full shadow-lg flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold transition-all hover:scale-105 active:scale-95 disabled:opacity-70 disabled:grayscale disabled:cursor-not-allowed"
                                                            >
                                                                {isTranscribing ? (
                                                                    <>
                                                                        <Loader2 size={18} className="animate-spin" />
                                                                        <span>{language === 'vietnamese' ? 'Đang gửi...' : 'Sending...'}</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <RefreshCw size={16} />
                                                                        <span>{language === 'vietnamese' ? 'Gửi lại' : 'Retry'}</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Writing hint nudge chip — pinned at bottom of write panel */}
                                {(showHintNudge || hintResult || isHinting) && !readOnly && (
                                    <div className="mx-3 mb-3 mt-2 shrink-0 animate-in slide-in-from-bottom-2 duration-200">
                                        {!hintResult && !isHinting ? (
                                            /* Stage 1: passive chip */
                                            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/15">
                                                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                                                    <Lightbulb size={14} className="shrink-0" />
                                                    <span className="text-xs font-medium">
                                                        {language === 'vietnamese' ? 'Cần gợi ý để tiếp tục?' : 'Need a nudge to continue?'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <button
                                                        onClick={async () => {
                                                            if (!node.imageBase64 || isHinting) return;
                                                            setShowHintNudge(false);
                                                            setIsHinting(true);
                                                            try {
                                                                const hint = await callHintForNote(node.imageBase64, node.content || '', language, node.title || undefined);
                                                                setHintResult(hint);
                                                            } catch {
                                                                setHintResult(language === 'vietnamese' ? 'Không thể tải gợi ý lúc này.' : 'Unable to load hint right now.');
                                                            } finally {
                                                                setIsHinting(false);
                                                            }
                                                        }}
                                                        className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                                                    >
                                                        {language === 'vietnamese' ? 'Nhận gợi ý' : 'Get hint'}
                                                    </button>
                                                    <button
                                                        onClick={() => { setShowHintNudge(false); hintDismissedAtRef.current = Date.now(); }}
                                                        className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-200"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : isHinting ? (
                                            /* Loading */
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/15 text-amber-600 dark:text-amber-400 text-xs">
                                                <Loader2 size={13} className="animate-spin shrink-0" />
                                                {language === 'vietnamese' ? 'Đang nghĩ gợi ý…' : 'Thinking of a hint…'}
                                            </div>
                                        ) : (
                                            /* Stage 2: hint result */
                                            <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/15 px-3 py-2.5">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-[11px] font-semibold uppercase tracking-wide">
                                                        <Lightbulb size={12} />
                                                        {language === 'vietnamese' ? 'Gợi ý' : 'Hint'}
                                                    </div>
                                                    <button
                                                        onClick={() => { setHintResult(null); setShowHintNudge(false); hintDismissedAtRef.current = Date.now(); }}
                                                        className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-200 shrink-0"
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                </div>
                                                <p className="mt-1.5 text-xs text-amber-800 dark:text-amber-200 leading-relaxed">{hintResult}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Sidebar - Visuals & Framework */}
                            {hasSidebarContent && (
                                <>
                                    {/* Draggable Resizer */}
                                    {isSidebarOpen && (
                                        <div
                                            className="hidden md:flex w-2 bg-gray-50 dark:bg-slate-900 hover:bg-blue-500 cursor-col-resize items-center justify-center transition-colors z-30 flex-shrink-0 border-l border-r border-gray-100 dark:border-slate-800 hover:border-blue-500 group/resizer"
                                            onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
                                        >
                                            <GripVertical size={12} className="text-gray-300 dark:text-slate-600 group-hover/resizer:text-white" />
                                        </div>
                                    )}

                                    {/* Sidebar Content */}
                                    <div
                                        ref={sidebarRef}
                                        className={`hidden md:flex bg-gray-50 dark:bg-slate-950 flex-col flex-shrink-0 transition-all duration-300 ${sidebarOnLeft ? 'border-r' : 'border-l'} dark:border-slate-800 ${isSidebarOpen ? (showAiChat ? 'overflow-hidden' : 'p-4 gap-6 overflow-y-auto hide-scrollbar') : 'w-14 py-4 items-center overflow-hidden'}`}
                                        style={isSidebarOpen ? { width: `${sidebarFraction * 100}%`, minWidth: '200px' } : {}}
                                    >
                                        <div className={`flex ${isSidebarOpen ? 'flex-row self-end mb-2 gap-1' : 'flex-col mb-4 gap-1 items-center'}`}>
                                            {onToggleOutline && (
                                                <Tip text={language === 'vietnamese' ? (outlinePanelOpen ? 'Đóng cấu trúc (Ctrl+\\)' : 'Mở cấu trúc (Ctrl+\\)') : (outlinePanelOpen ? 'Close outline (Ctrl+\\)' : 'Open outline (Ctrl+\\)')} side="bottom" align="right">
                                                <button
                                                    onClick={() => { posthog.capture('note_feature_used', { feature: 'outline_panel', enabled: !outlinePanelOpen }); onToggleOutline?.(); }}
                                                    className={`p-2 rounded-lg transition-colors ${outlinePanelOpen ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-500'}`}
                                                >
                                                    <ListTree size={20} />
                                                </button>
                                                </Tip>
                                            )}
                                            {onToggleMediaPanel && (
                                                <Tip text={language === 'vietnamese' ? (mediaPanelOpen ? 'Đóng media' : 'Mở media / PDF') : (mediaPanelOpen ? 'Close media' : 'Open media / PDF')} side="bottom" align="right">
                                                <button
                                                    onClick={() => { posthog.capture('note_feature_used', { feature: 'media_panel', enabled: !mediaPanelOpen }); onToggleMediaPanel?.(); }}
                                                    className={`p-2 rounded-lg transition-colors ${mediaPanelOpen ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-500'}`}
                                                >
                                                    <Monitor size={20} />
                                                </button>
                                                </Tip>
                                            )}
                                            {hasAiKey && (
                                                <Tip text={language === 'vietnamese' ? (showAiChat ? 'Đóng chat AI' : 'Chat AI (BYOK)') : (showAiChat ? 'Close AI chat' : 'AI Chat (BYOK)')} side="bottom" align="right">
                                                <button
                                                    onClick={() => { posthog.capture('note_feature_used', { feature: 'ai_chat', enabled: !showAiChat }); setShowAiChat(v => !v); if (!isSidebarOpen) setIsSidebarOpen(true); }}
                                                    className={`p-2 rounded-lg transition-colors ${showAiChat ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300' : 'hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-500'}`}
                                                >
                                                    <Brain size={20} />
                                                </button>
                                                </Tip>
                                            )}
                                            <Tip text={isSidebarOpen ? (language === 'vietnamese' ? 'Thu gọn' : 'Collapse sidebar') : (language === 'vietnamese' ? 'Mở rộng' : 'Expand sidebar')} side="bottom" align="right">
                                            <button
                                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-500 transition-colors"
                                            >
                                                {isSidebarOpen ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
                                            </button>
                                            </Tip>
                                        </div>

                                        {!isSidebarOpen && node.analysis && (() => {
                                            const sc = node.analysis!.score;
                                            const tone = sc >= 80 ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : sc >= 50 ? 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
                                            return (
                                                <Tip text={language === 'vietnamese' ? 'Xem AI feedback' : 'Show AI feedback'} side="bottom" align="right">
                                                <button
                                                    onClick={() => setShowFeedbackDrawer(v => !v)}
                                                    className={`mt-1 px-1.5 py-1.5 rounded-lg border text-[11px] font-black transition-colors ${tone} ${showFeedbackDrawer ? 'ring-2 ring-indigo-300 dark:ring-indigo-700' : ''}`}
                                                >
                                                    {sc}%
                                                </button>
                                                </Tip>
                                            );
                                        })()}

                                        {isSidebarOpen && showAiChat && hasAiKey ? (
                                            <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white dark:bg-slate-900 border-t border-indigo-100 dark:border-indigo-900">
                                                {/* Model selector row */}
                                                <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-950 border-b border-indigo-100 dark:border-indigo-900 shrink-0">
                                                    <Brain size={13} className="text-indigo-600 dark:text-indigo-400 shrink-0" />
                                                    <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300">
                                                        {language === 'vietnamese' ? 'Chat AI' : 'AI Chat'}
                                                    </span>
                                                    <select
                                                        value={aiChatModelId}
                                                        onChange={e => setAiChatModelId(e.target.value)}
                                                        className="ml-auto text-[10px] bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-700 rounded px-1.5 py-0.5 text-indigo-700 dark:text-indigo-300 max-w-[130px]"
                                                    >
                                                        {userApiKeys?.gemini && (
                                                            <optgroup label="Gemini">
                                                                <option value="gemini-3.5-flash-lite">3.5 Flash-Lite (nhanh nhất)</option>
                                                                <option value="gemini-3.5-flash">3.5 Flash</option>
                                                                <option value="gemini-3.6-flash">3.6 Flash</option>
                                                                <option value="gemini-3.8-flash">3.8 Flash (mới nhất)</option>
                                                                <option value="gemini-2.5-pro">2.5 Pro (mạnh nhất)</option>
                                                            </optgroup>
                                                        )}
                                                        {userApiKeys?.openai && (
                                                            <optgroup label="OpenAI">
                                                                <option value="gpt-4o">GPT-4o</option>
                                                                <option value="gpt-4o-mini">GPT-4o Mini</option>
                                                                <option value="o3-mini">o3-mini</option>
                                                            </optgroup>
                                                        )}
                                                    </select>
                                                    {aiChatMessages.length > 0 && (
                                                        <button
                                                            onClick={() => { aiChatAbortRef.current?.abort(); setAiChatMessages([]); setAiChatLoading(false); }}
                                                            className="text-gray-400 hover:text-red-500 transition-colors p-0.5 shrink-0"
                                                            title={language === 'vietnamese' ? 'Xóa hội thoại' : 'Clear chat'}
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                                {/* Messages area */}
                                                <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
                                                    {aiChatMessages.length === 0 && (
                                                        <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 dark:text-gray-500 py-8">
                                                            <Brain size={28} className="opacity-20" />
                                                            <p className="text-xs text-center leading-relaxed whitespace-pre-line">
                                                                {language === 'vietnamese'
                                                                    ? 'Hỏi bất kỳ điều gì về note này.\nNội dung note đã được gửi kèm làm context.'
                                                                    : 'Ask anything about this note.\nNote content is included as context.'}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {aiChatMessages.map((msg, i) => (
                                                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                            <div className={`max-w-[88%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                                                                msg.role === 'user'
                                                                    ? 'bg-indigo-600 text-white'
                                                                    : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-200'
                                                            }`}>
                                                                {msg.role === 'assistant' ? (
                                                                    <div className="prose prose-xs dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                                                                        dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content || (aiChatLoading && i === aiChatMessages.length - 1 ? '▋' : '')) }} />
                                                                ) : (
                                                                    <span className="whitespace-pre-wrap">{msg.content}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {aiChatLoading && aiChatMessages[aiChatMessages.length - 1]?.role !== 'assistant' && (
                                                        <div className="flex justify-start">
                                                            <div className="bg-gray-100 dark:bg-slate-800 rounded-xl px-3 py-2">
                                                                <Loader2 size={13} className="animate-spin text-indigo-500" />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div ref={aiChatEndRef} />
                                                </div>
                                                {/* Input */}
                                                <div className="shrink-0 border-t border-gray-100 dark:border-slate-800 p-2">
                                                    <div className="flex items-end gap-1.5">
                                                        <textarea
                                                            ref={aiChatInputRef}
                                                            value={aiChatInput}
                                                            onChange={e => setAiChatInput(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiChatMessage(); }
                                                            }}
                                                            placeholder={language === 'vietnamese' ? 'Hỏi AI về note này...' : 'Ask AI about this note...'}
                                                            rows={2}
                                                            className="flex-1 resize-none text-xs border border-gray-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 leading-relaxed"
                                                            disabled={aiChatLoading || isChatRecording || isChatTranscribing}
                                                        />
                                                        <button
                                                            onClick={handleChatVoice}
                                                            disabled={aiChatLoading || isChatTranscribing}
                                                            title={isChatRecording ? (language === 'vietnamese' ? 'Dừng ghi âm' : 'Stop recording') : (language === 'vietnamese' ? 'Nói thay vì gõ' : 'Speak instead of typing')}
                                                            className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                                                                isChatRecording
                                                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-500 animate-pulse'
                                                                    : isChatTranscribing
                                                                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-400'
                                                                    : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                                            }`}
                                                        >
                                                            {isChatTranscribing ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
                                                        </button>
                                                        {aiChatLoading ? (
                                                            <button onClick={() => { aiChatAbortRef.current?.abort(); setAiChatLoading(false); }}
                                                                className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 transition-colors shrink-0">
                                                                <X size={14} />
                                                            </button>
                                                        ) : (
                                                            <button onClick={() => sendAiChatMessage()} disabled={!aiChatInput.trim() || isChatRecording || isChatTranscribing}
                                                                className="p-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0">
                                                                <ArrowUpRight size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 mt-1 text-center">
                                                        {isChatRecording
                                                            ? (language === 'vietnamese' ? '🔴 Đang ghi... nhấn mic để dừng' : '🔴 Recording... tap mic to stop')
                                                            : (language === 'vietnamese' ? 'Enter để gửi · Shift+Enter xuống dòng' : 'Enter to send · Shift+Enter for newline')}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : isSidebarOpen ? (
                                            <div className="flex flex-col gap-6 p-4 overflow-y-auto hide-scrollbar flex-1">
                                                {sidebarInfoContent}
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Overlay drawer — full AI feedback while the sidebar is collapsed (PDF open) */}
                                    {!isSidebarOpen && showFeedbackDrawer && node.analysis && (
                                        <>
                                            <div className="hidden md:block absolute inset-0 z-30" onClick={() => setShowFeedbackDrawer(false)} />
                                            <div className={`hidden md:flex absolute top-0 bottom-0 z-40 w-[340px] max-w-[85%] flex-col bg-gray-50 dark:bg-slate-950 shadow-2xl ${sidebarOnLeft ? 'left-14 border-r' : 'right-14 border-l'} dark:border-slate-800`}>
                                                <div className="flex items-center justify-between px-4 pt-3 shrink-0">
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{language === 'vietnamese' ? 'AI feedback' : 'AI feedback'}</span>
                                                    <button onClick={() => setShowFeedbackDrawer(false)} className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-800 text-gray-400"><X size={14} /></button>
                                                </div>
                                                <div className="flex flex-col gap-6 p-4 overflow-y-auto hide-scrollbar flex-1">
                                                    {sidebarInfoContent}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-slate-900 relative overflow-hidden">
                            {annotatingImage ? (
                                /* Annotation Mode — draw on top of an existing image */
                                <div className="flex-1 flex flex-col">
                                    <div className="flex items-center gap-3 px-4 py-2.5 bg-white dark:bg-slate-800 border-b dark:border-slate-700 shrink-0">
                                        <button
                                            onClick={() => { setAnnotatingImage(null); setActiveTab('write'); }}
                                            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                        >
                                            <ArrowLeft size={13} />
                                            {language === 'vietnamese' ? 'Hủy' : 'Cancel'}
                                        </button>
                                        <span className="text-gray-300 dark:text-slate-600">·</span>
                                        <span className="text-xs text-purple-600 dark:text-purple-400 font-semibold flex items-center gap-1.5">
                                            <PenLine size={12} />
                                            {language === 'vietnamese' ? 'Vẽ lên ảnh' : 'Annotate image'}
                                        </span>
                                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto hidden md:block">
                                            {language === 'vietnamese'
                                                ? 'Save → thay thế ảnh gốc  ·  Save as New → giữ bản gốc'
                                                : 'Save → replace original  ·  Save as New → keep original'}
                                        </span>
                                    </div>
                                    <div className="flex-1 relative">
                                        <SketchPad
                                            key={`annotate_${annotatingImage.id ?? 'main'}`}
                                            initialImage={annotatingImage.src}
                                            onSave={handleSaveAnnotation}
                                            isEditingExisting={true}
                                        />
                                    </div>
                                </div>
                            ) : showDrawingGallery && !activeDrawingId ? (
                                /* Gallery View */
                                <div className="flex-1 p-6 overflow-y-auto">
                                    {/* Header with New Drawing Button */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setActiveTab('write')}
                                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                            >
                                                <ArrowLeft size={15} />
                                                {language === 'vietnamese' ? 'Quay lại' : 'Back'}
                                            </button>
                                            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700" />
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                <PenTool size={20} className="text-purple-600" />
                                                {language === 'vietnamese' ? 'Bộ Sưu Tập Vẽ' : 'Drawing Gallery'}
                                            </h3>
                                        </div>
                                        <button
                                            onClick={handleCreateNewDrawing}
                                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold text-sm transition-colors shadow-md"
                                        >
                                            <Plus size={16} />
                                            {language === 'vietnamese' ? 'Vẽ Mới' : 'New Drawing'}
                                        </button>
                                    </div>

                                    {/* Drawings Grid */}
                                    {(() => {
                                        const allDrawings = getAllDrawings();
                                        
                                        if (allDrawings.length === 0) {
                                            return (
                                                <div className="flex flex-col items-center justify-center h-64 text-center">
                                                    <PenTool size={48} className="text-gray-300 dark:text-slate-600 mb-4" />
                                                    <p className="text-gray-500 dark:text-slate-400 mb-4">
                                                        {language === 'vietnamese' 
                                                            ? 'Chưa có bản vẽ nào. Bắt đầu vẽ ngay!' 
                                                            : 'No drawings yet. Start creating!'}
                                                    </p>
                                                    <button
                                                        onClick={handleCreateNewDrawing}
                                                        className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold transition-colors shadow-md"
                                                    >
                                                        <Plus size={20} />
                                                        {language === 'vietnamese' ? 'Tạo Vẽ Đầu Tiên' : 'Create First Drawing'}
                                                    </button>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {allDrawings.map((drawing, idx) => (
                                                    <div
                                                        key={drawing.id}
                                                        className="group relative bg-white dark:bg-slate-800 rounded-xl border-2 border-gray-200 dark:border-slate-700 overflow-hidden hover:border-purple-400 dark:hover:border-purple-600 transition-all hover:shadow-lg"
                                                    >
                                                        {/* Thumbnail */}
                                                        <div 
                                                            className="aspect-video bg-gray-100 dark:bg-slate-900 cursor-pointer relative overflow-hidden"
                                                            onClick={() => handleLoadDrawing(drawing.id)}
                                                        >
                                                            <img 
                                                                src={drawing.content} 
                                                                alt={`Drawing ${idx + 1}`}
                                                                className="w-full h-full object-contain"
                                                            />
                                                            {/* Overlay on hover */}
                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <Edit size={32} className="text-white drop-shadow-lg" />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Info Bar */}
                                                        <div className="p-3 flex items-center justify-between bg-gray-50 dark:bg-slate-900">
                                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                <PenTool size={14} className="text-purple-600" />
                                                                {language === 'vietnamese' ? 'Hình' : 'Drawing'} {idx + 1}
                                                                {drawing.isLegacy && (
                                                                    <span className="text-xs text-gray-400">
                                                                        ({language === 'vietnamese' ? 'Cũ' : 'Legacy'})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <Tip text={language === 'vietnamese' ? 'Xóa bản vẽ' : 'Delete drawing'} side="top">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteDrawing(drawing.id);
                                                                }}
                                                                className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 rounded transition-colors"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                            </Tip>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            ) : (
                                /* Canvas View */
                                <div className="flex-1 flex flex-col">
                                    <SketchPad
                                        key={activeDrawingId || 'new'}
                                        initialImage={activeDrawingId && activeDrawingId !== 'new'
                                            ? getAllDrawings().find(d => d.id === activeDrawingId)?.content || null
                                            : null}
                                        onSave={handleSaveDrawing}
                                        isEditingExisting={activeDrawingId !== 'new' && activeDrawingId !== null}
                                        onBack={() => { setActiveDrawingId(null); setShowDrawingGallery(true); }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Mobile "Info" tab — feedback / crosslinks / visuals / hashtags, same content as the desktop sidebar */}
                {mobileSection === 'info' && (
                    <div className="md:hidden flex-1 flex flex-col p-4 gap-6 overflow-y-auto hide-scrollbar">
                        {hasSidebarContent ? sidebarInfoContent : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 dark:text-slate-600 gap-2 py-12">
                                <Info size={32} />
                                <span className="text-sm">
                                    {language === 'vietnamese' ? 'Chưa có phản hồi AI, ảnh, hay liên kết nào.' : 'No AI feedback, images, or links yet.'}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Mobile "Outline" tab — embedded notebook tree, avoids leaving the note to browse/jump */}
                {mobileSection === 'outline' && (
                    <div className="md:hidden flex-1 flex flex-col overflow-hidden">
                        <OutlinePanel
                            nodes={allNodes}
                            activeNodeId={node.id}
                            onFocusNode={(id) => { onNavigateToNote?.(id); setMobileSection('note'); }}
                            onExpandNode={(id) => { onNavigateToNote?.(id); setMobileSection('note'); }}
                            onClose={() => setMobileSection('note')}
                            onApplyOrder={onApplyOrder ?? (() => {})}
                            onAutoTitle={onAutoTitle}
                            onAutoTitleBranch={onAutoTitleBranch}
                            onCreateChildNote={onCreateChildNoteAt}
                            onRenameNode={onRenameNode}
                            language={language === 'vietnamese' ? 'vietnamese' : 'english'}
                            linkMode={linkMode}
                            onLinkSelect={onLinkSelect}
                            onCancelLinkMode={onCancelLinkMode}
                            onCreateLink={onCreateLink}
                            pendingLink={pendingLink}
                            onPendingLinkChange={onPendingLinkChange}
                            onLinkNotes={onLinkNotes}
                            allNotebooks={allNotebooks}
                            currentNotebookId={currentNotebookId}
                            onLoadNotebook={onLoadNotebook}
                        />
                    </div>
                )}

                {/* Mobile bottom tab bar — Note / Info / Outline, in-modal switch, no navigation away */}
                <div className="flex md:hidden shrink-0 border-t dark:border-slate-800 bg-white dark:bg-slate-900">
                    <button
                        onClick={() => setMobileSection('note')}
                        className={`flex-1 flex flex-col items-center gap-1 py-3 text-[12px] font-semibold transition-colors ${mobileSection === 'note' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}
                    >
                        <FileText size={22} /> {t.tabNote}
                    </button>
                    <button
                        onClick={() => setMobileSection('info')}
                        className={`relative flex-1 flex flex-col items-center gap-1 py-3 text-[12px] font-semibold transition-colors ${mobileSection === 'info' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}
                    >
                        <span className="relative">
                            <Info size={22} />
                            {hasSidebarContent && <span className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        </span>
                        {t.tabInfo}
                    </button>
                    <button
                        onClick={() => setMobileSection('outline')}
                        className={`flex-1 flex flex-col items-center gap-1 py-3 text-[12px] font-semibold transition-colors ${mobileSection === 'outline' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}
                    >
                        <ListTree size={22} /> {t.tabOutline}
                    </button>
                </div>
            </div>

            {/* Image Gallery Modal */}
            {
                imageGallery?.isOpen && (
                    <ImageGalleryModal
                        images={visuals.map(v => ({ src: v.src, alt: v.label }))}
                        initialIndex={imageGallery.initialIndex}
                        onClose={() => setImageGallery(null)}
                    />
                )
            }

            <MathAutocomplete
                visible={autocomplete.visible}
                position={autocomplete.position}
                suggestions={autocomplete.suggestions}
                selectedIndex={autocomplete.selectedIndex}
                onSelect={handleAutocompleteSelect}
                onClose={() => setAutocomplete(prev => ({ ...prev, visible: false }))}
            />

            <MathSymbolPickerModal
                isOpen={isMathPickerOpen}
                onClose={() => {
                    setIsMathPickerOpen(false);
                    // Reset interaction ref when closing, allow blur to work again naturally
                    setTimeout(() => {
                        isMathPickerInteractionRef.current = false;
                        // Optional: restore focus to textarea
                        if (textareaRef.current) {
                            textareaRef.current.focus();
                        }
                    }, 50);
                }}
                onInsert={handleInsertSymbolFromPicker}
            />

            {/* Search Overlay */}
            {isSearchOverlayOpen && (
                <div 
                    className="absolute inset-0 z-50 flex items-start justify-center pt-10 md:pt-20 bg-black/50 backdrop-blur-sm animate-in fade-in px-4"
                    onClick={(e) => { e.stopPropagation(); if (pendingModalSearchRef.current) saveModalSearchHistory(pendingModalSearchRef.current); setIsSearchOverlayOpen(false); setShowModalHistoryDropdown(false); }}
                >
                    <div 
                        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[70vh] flex flex-col border border-gray-200 dark:border-slate-700 animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Search Input */}
                        <div className="p-4 border-b dark:border-slate-700 space-y-3">
                            {/* Mode tabs */}
                            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-slate-900 rounded-lg">
                                <button
                                    onClick={() => { setSearchMode('keyword'); setSemanticResults([]); setSemanticError(null); }}
                                    className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center justify-center gap-1.5 ${searchMode === 'keyword' ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                >
                                    <Search size={11} />
                                    {language === 'vietnamese' ? 'Từ khóa' : 'Keyword'}
                                </button>
                                <button
                                    onClick={() => { setSearchMode('semantic'); setShowModalHistoryDropdown(false); }}
                                    className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors flex items-center justify-center gap-1.5 ${searchMode === 'semantic' ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                >
                                    <Sparkles size={11} />
                                    {language === 'vietnamese' ? 'AI Semantic' : 'AI Semantic'}
                                </button>
                            </div>
                            <div className="relative">
                                {searchMode === 'semantic' ? <Sparkles size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-400" /> : <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
                                <input
                                    ref={searchInputRef}
                                    autoFocus
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSearchQuery(val);
                                        if (val) setShowModalHistoryDropdown(false);
                                        if (!val.trim()) { setSemanticResults([]); setCrossResults([]); }
                                        // Auto-search semantic on type (debounced)
                                        if (searchMode === 'semantic') {
                                            if (semanticDebounceRef.current) clearTimeout(semanticDebounceRef.current);
                                            if (val.trim().length >= 3) {
                                                semanticDebounceRef.current = setTimeout(async () => {
                                                    setSemanticLoading(true);
                                                    setSemanticError(null);
                                                    try {
                                                        const res = await semanticSearchNotes(val.trim());
                                                        setSemanticResults(res);
                                                    } catch (err: any) {
                                                        setSemanticError(err?.code || err?.message || 'error');
                                                        setSemanticResults([]);
                                                    } finally {
                                                        setSemanticLoading(false);
                                                    }
                                                }, 700);
                                            } else {
                                                setSemanticResults([]);
                                            }
                                        }
                                    }}
                                    placeholder={searchMode === 'semantic'
                                        ? (language === 'vietnamese' ? 'Mô tả ý tưởng bạn đang tìm...' : 'Describe what you\'re looking for...')
                                        : (language === 'vietnamese' ? 'Nhập từ khóa để tìm ghi chú...' : 'Type to search notes...')}
                                    className={`w-full pl-10 pr-10 py-3 rounded-lg border focus:ring-2 dark:bg-slate-900 dark:text-white outline-none ${searchMode === 'semantic' ? 'border-violet-300 dark:border-violet-700 focus:ring-violet-500' : 'border-gray-300 dark:border-slate-600 focus:ring-violet-500'}`}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Escape') {
                                            if (searchQuery.trim()) saveModalSearchHistory(searchQuery);
                                            setIsSearchOverlayOpen(false);
                                            setSearchQuery('');
                                            setShowModalHistoryDropdown(false);
                                        }
                                        if (e.key === 'Enter' && searchQuery.trim()) {
                                            saveModalSearchHistory(searchQuery);
                                            const q = searchQuery.trim();
                                            if (searchMode === 'semantic') {
                                                setSemanticLoading(true);
                                                setSemanticError(null);
                                                try {
                                                    const res = await semanticSearchNotes(q);
                                                    setSemanticResults(res);
                                                } catch (err: any) {
                                                    setSemanticError(err?.code || err?.message || 'error');
                                                    setSemanticResults([]);
                                                } finally {
                                                    setSemanticLoading(false);
                                                }
                                            } else if (searchScope === 'all') {
                                                setCrossLoading(true);
                                                setCrossError(null);
                                                try {
                                                    const res = await searchAcrossNotebooks(q);
                                                    setCrossResults(res);
                                                } catch (err: any) {
                                                    setCrossError(err?.code || err?.message || 'error');
                                                    setCrossResults([]);
                                                } finally {
                                                    setCrossLoading(false);
                                                }
                                            }
                                        }
                                    }}
                                />
                                {searchQuery
                                    ? (<button onClick={() => { setSearchQuery(''); setShowModalHistoryDropdown(false); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={16} /></button>)
                                    : modalSearchHistory.length > 0 && (
                                        <button onMouseDown={(e) => { e.preventDefault(); setShowModalHistoryDropdown(v => !v); }} className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${showModalHistoryDropdown ? 'text-violet-500 bg-violet-50 dark:bg-violet-900/30' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>
                                            <Clock size={15} />
                                        </button>
                                    )
                                }
                                {/* History dropdown */}
                                {showModalHistoryDropdown && !searchQuery && modalSearchHistory.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-10 overflow-hidden">
                                        <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 dark:border-slate-700">
                                            <span className="text-[11px] text-gray-400 flex items-center gap-1.5"><Clock size={10} />{language === 'vietnamese' ? 'Tìm kiếm gần đây' : 'Recent searches'}</span>
                                            <button onMouseDown={(e) => { e.preventDefault(); setModalSearchHistory([]); localStorage.removeItem('sb_modal_search_history'); setShowModalHistoryDropdown(false); }} className="text-[11px] text-red-400 hover:text-red-500">{language === 'vietnamese' ? 'Xoá tất cả' : 'Clear all'}</button>
                                        </div>
                                        {modalSearchHistory.map(h => (
                                            <div key={h} className="flex items-center border-b border-gray-50 dark:border-slate-700/50 last:border-0">
                                                <button onMouseDown={(e) => { e.preventDefault(); setSearchQuery(h); setShowModalHistoryDropdown(false); }} className="flex-1 flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left">
                                                    <Clock size={12} className="shrink-0 text-gray-300" />{h}
                                                </button>
                                                <button onMouseDown={(e) => { e.preventDefault(); setModalSearchHistory(prev => { const next = prev.filter(x => x !== h); localStorage.setItem('sb_modal_search_history', JSON.stringify(next)); return next; }); }} className="px-2 py-2.5 text-gray-300 hover:text-red-400 transition-colors"><X size={12} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {searchMode === 'keyword' && allNotebooks.length > 1 && (
                                <div className="flex items-center gap-3 mt-1">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={searchScope === 'all'}
                                            onChange={e => { setSearchScope(e.target.checked ? 'all' : 'current'); setCrossResults([]); }}
                                            className="w-3.5 h-3.5 rounded accent-violet-600"
                                        />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {language === 'vietnamese' ? 'Tìm trong mọi Notebook' : 'Search all notebooks'}
                                        </span>
                                    </label>
                                    {searchScope === 'all' && (
                                        <span className="text-[10px] text-amber-500 dark:text-amber-400 font-medium">
                                            ↵ {language === 'vietnamese' ? 'Nhấn Enter để tìm' : 'Press Enter to search'}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Results */}
                        <div className="flex-1 overflow-y-auto hide-scrollbar p-2">
                            {searchMode === 'semantic' ? (
                                <div>
                                    {semanticLoading && (
                                        <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                            <Loader2 size={28} className="mx-auto mb-2 animate-spin text-violet-400" />
                                            <p className="text-sm">{language === 'vietnamese' ? 'Đang tìm kiếm...' : 'Searching...'}</p>
                                        </div>
                                    )}
                                    {!semanticLoading && semanticError && (
                                        <div className="p-8 text-center text-red-400 dark:text-red-500">
                                            <p className="text-sm">{semanticError.includes('429') || semanticError.includes('resource-exhausted') || semanticError.includes('rate_limit') ? (language === 'vietnamese' ? 'Quota đang bận. Thử lại sau 1 phút.' : 'Rate limited. Try again in 1 minute.') : (language === 'vietnamese' ? 'Có lỗi xảy ra. Thử lại nhé.' : 'Something went wrong.')}</p>
                                        </div>
                                    )}
                                    {!semanticLoading && !semanticError && searchQuery.trim().length < 3 && (
                                        <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                            <Sparkles size={32} className="mx-auto mb-2 opacity-50 text-violet-400" />
                                            <p className="text-sm">{language === 'vietnamese' ? 'Mô tả ý tưởng bạn đang tìm (ít nhất 3 ký tự)' : 'Describe what you\'re looking for (min 3 chars)'}</p>
                                        </div>
                                    )}
                                    {!semanticLoading && !semanticError && semanticResults.length === 0 && searchQuery.trim().length >= 3 && (
                                        <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                            <p className="text-sm">{language === 'vietnamese' ? 'Không tìm thấy ghi chú liên quan' : 'No relevant notes found'}</p>
                                        </div>
                                    )}
                                    {!semanticLoading && semanticResults.map((r) => (
                                        <div
                                            key={`${r.notebookId}-${r.nodeId}`}
                                            className="p-3 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                                        >
                                            <div className="text-[10px] font-semibold text-violet-500 dark:text-violet-400 mb-0.5 flex items-center gap-1">
                                                <BookOpen size={9} />{r.notebookTitle}
                                            </div>
                                            <h4 className="font-bold text-gray-900 dark:text-white mb-1">{r.nodeTitle || (language === 'vietnamese' ? 'Không có tiêu đề' : 'Untitled')}</h4>
                                            {r.excerpt && <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">{r.excerpt}</p>}
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        setIsSearchOverlayOpen(false);
                                                        setSearchQuery('');
                                                        setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                                                        if (r.notebookId && r.notebookId !== currentNotebookId && onNavigateCrossNotebook) {
                                                            onNavigateCrossNotebook(r.notebookId, r.nodeId);
                                                        } else if (onNavigateToNote) {
                                                            onNavigateToNote(r.nodeId);
                                                        }
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                                                        const x = e.clientX; const y = e.clientY;
                                                        const stubNode: MindMapNode = { id: r.nodeId, title: r.nodeTitle || '', content: r.excerpt || '', parentId: undefined, position: { x: 0, y: 0 }, imageBase64: null, imageMimeType: null, analysis: null, isAnalyzing: false, isExpanded: false, createdAt: 0, updatedAt: 0 };
                                                        linkHoverTimerRef.current = setTimeout(() => {
                                                            setLinkHoverPreview({ node: stubNode, top: y, left: x, notebookId: r.notebookId, notebookTitle: r.notebookTitle });
                                                            getNodeContent(r.notebookId, r.nodeId).then(result => {
                                                                setLinkHoverPreview(prev => prev ? { ...prev, node: { ...prev.node, title: result.title, content: result.content, imageBase64: result.imageBase64 ?? null, imageMimeType: result.imageMimeType ?? null, attachments: result.attachments ?? [] } } : null);
                                                            }).catch(() => {});
                                                        }, 250);
                                                    }}
                                                    onMouseLeave={() => {
                                                        if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                                                        linkHoverTimerRef.current = setTimeout(() => setLinkHoverPreview(null), 150);
                                                    }}
                                                    className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-bold hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex items-center gap-1"
                                                >
                                                    <ArrowUpRight size={12} />
                                                    {language === 'vietnamese' ? 'Xem' : 'View'}
                                                </button>
                                                {onCreateLink && (
                                                    <button
                                                        onClick={() => {
                                                            if (r.notebookId && r.notebookId !== currentNotebookId) {
                                                                onCreateLink(r.nodeId, r.notebookId, r.nodeTitle || '', r.notebookTitle);
                                                            } else {
                                                                onCreateLink(r.nodeId);
                                                            }
                                                            setIsSearchOverlayOpen(false);
                                                            setSearchQuery('');
                                                        }}
                                                        className="px-3 py-1.5 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-lg text-xs font-bold hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors flex items-center gap-1"
                                                    >
                                                        <LinkIcon size={12} />
                                                        {language === 'vietnamese' ? 'Liên kết' : 'Link'}
                                                    </button>
                                                )}
                                                <span className="ml-auto text-[10px] font-semibold text-violet-400 dark:text-violet-500 tabular-nums">
                                                    {Math.round(r.similarity * 100)}%
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (() => {
                                const query = debouncedSearchQuery.toLowerCase().trim();
                                if (!query) {
                                    return (
                                        <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                            <Search size={32} className="mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">{language === 'vietnamese' ? 'Nhập từ khóa để bắt đầu tìm kiếm' : 'Start typing to search'}</p>
                                        </div>
                                    );
                                }

                                // Cross-notebook search: use Cloud Function results
                                if (searchScope === 'all') {
                                    if (crossLoading) {
                                        return (
                                            <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                                <div className="w-6 h-6 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin mx-auto mb-2" />
                                                <p className="text-sm">{language === 'vietnamese' ? 'Đang tìm kiếm...' : 'Searching...'}</p>
                                            </div>
                                        );
                                    }
                                    if (crossError) {
                                        return (
                                            <div className="p-8 text-center text-red-400">
                                                <p className="text-sm">{language === 'vietnamese' ? `Lỗi: ${crossError}` : `Error: ${crossError}`}</p>
                                            </div>
                                        );
                                    }
                                    if (crossResults.length === 0) {
                                        return (
                                            <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                                <p className="text-sm">{language === 'vietnamese' ? 'Không tìm thấy kết quả' : 'No results found'}</p>
                                            </div>
                                        );
                                    }
                                    return crossResults.map((r) => (
                                        <div
                                            key={`${r.notebookId}-${r.nodeId}`}
                                            className="p-3 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                                        >
                                            <div className="text-[10px] font-semibold text-violet-500 dark:text-violet-400 mb-0.5 flex items-center gap-1">
                                                <BookOpen size={9} />
                                                {r.notebookTitle}
                                            </div>
                                            <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                                                {r.nodeTitle || (language === 'vietnamese' ? 'Không có tiêu đề' : 'Untitled')}
                                            </h4>
                                            {r.excerpt && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                                                    {r.excerpt}
                                                </p>
                                            )}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        if (pendingModalSearchRef.current) saveModalSearchHistory(pendingModalSearchRef.current);
                                                        setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                                                        if (r.notebookId !== currentNotebookId && onNavigateCrossNotebook) {
                                                            onNavigateCrossNotebook(r.notebookId, r.nodeId);
                                                        } else if (onNavigateToNote) {
                                                            onNavigateToNote(r.nodeId);
                                                        }
                                                        setIsSearchOverlayOpen(false);
                                                        setSearchQuery('');
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                                                        const x = e.clientX; const y = e.clientY;
                                                        const stubNode: MindMapNode = { id: r.nodeId, title: r.nodeTitle || '', content: r.excerpt || '', parentId: undefined, position: { x: 0, y: 0 }, imageBase64: null, imageMimeType: null, analysis: null, isAnalyzing: false, isExpanded: false, createdAt: 0, updatedAt: 0 };
                                                        linkHoverTimerRef.current = setTimeout(() => {
                                                            setLinkHoverPreview({ node: stubNode, top: y, left: x, notebookId: r.notebookId, notebookTitle: r.notebookTitle });
                                                            getNodeContent(r.notebookId, r.nodeId).then(result => {
                                                                setLinkHoverPreview(prev => prev ? { ...prev, node: { ...prev.node, title: result.title, content: result.content, imageBase64: result.imageBase64 ?? null, imageMimeType: result.imageMimeType ?? null, attachments: result.attachments ?? [] } } : null);
                                                            }).catch(() => {});
                                                        }, 250);
                                                    }}
                                                    onMouseLeave={() => {
                                                        if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                                                        linkHoverTimerRef.current = setTimeout(() => setLinkHoverPreview(null), 150);
                                                    }}
                                                    className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-bold hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex items-center gap-1"
                                                >
                                                    <ArrowUpRight size={12} />
                                                    {language === 'vietnamese' ? 'Xem' : 'View'}
                                                </button>
                                                {onCreateLink && (
                                                    <button
                                                        onClick={() => {
                                                            if (r.notebookId !== currentNotebookId) {
                                                                onCreateLink(r.nodeId, r.notebookId, r.nodeTitle || '', r.notebookTitle);
                                                            } else {
                                                                onCreateLink(r.nodeId);
                                                            }
                                                            setIsSearchOverlayOpen(false);
                                                            setSearchQuery('');
                                                        }}
                                                        className="px-3 py-1.5 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-lg text-xs font-bold hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors flex items-center gap-1"
                                                    >
                                                        <LinkIcon size={12} />
                                                        {language === 'vietnamese' ? 'Liên kết' : 'Link'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ));
                                }

                                // Current notebook: client-side search
                                type SearchResult = { node: MindMapNode; notebookId: string | null; notebookTitle: string | null; score: number };

                                const nodeMap = new Map<string, MindMapNode>(
                                    allNodes.map(n => [n.id, n] as [string, MindMapNode])
                                );

                                const pool = modalSearchIndex.filter(e => e.notebookId === null);

                                const results: SearchResult[] = pool
                                    .filter(e => e.id !== node.id)
                                    .map(e => {
                                        const inTitle = e.titleLower.includes(query);
                                        const inTags = e.tagsLower.includes(query);
                                        const inContent = e.contentLower.includes(query);
                                        if (!inTitle && !inTags && !inContent) return null;
                                        return { node: nodeMap.get(e.id)!, notebookId: e.notebookId, notebookTitle: e.notebookTitle, score: (inTitle ? 4 : 0) + (inTags ? 2 : 0) + (inContent ? 1 : 0) };
                                    })
                                    .filter((r): r is SearchResult => r !== null && r.node !== undefined)
                                    .sort((a, b) => b.score - a.score)
                                    .slice(0, 15);

                                if (results.length === 0) {
                                    return (
                                        <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                                            <p className="text-sm">{language === 'vietnamese' ? 'Không tìm thấy kết quả' : 'No results found'}</p>
                                        </div>
                                    );
                                }

                                return results.map(({ node: result, notebookId: resultNbId, notebookTitle: resultNbTitle }) => (
                                    <div
                                        key={`${resultNbId ?? 'cur'}-${result.id}`}
                                        className="p-3 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors group"
                                        onMouseEnter={(e) => {
                                            if (searchHoverTimerRef.current) clearTimeout(searchHoverTimerRef.current);
                                            const x = e.clientX; const y = e.clientY;
                                            searchHoverTimerRef.current = setTimeout(() => setSearchHoverPreview({ node: result, top: y + 14, left: x + 18 }), 260);
                                        }}
                                        onMouseLeave={() => {
                                            if (searchHoverTimerRef.current) clearTimeout(searchHoverTimerRef.current);
                                            searchHoverTimerRef.current = setTimeout(() => setSearchHoverPreview(null), 120);
                                        }}
                                    >
                                        <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                                            {result.title || (language === 'vietnamese' ? 'Không có tiêu đề' : 'Untitled')}
                                        </h4>
                                        {result.content && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
                                                {result.content.replace(/\*\*|__|\*|_|~~|`{1,3}|#{1,6}\s?|\$\$?/g, '').trim()}
                                            </p>
                                        )}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    if (pendingModalSearchRef.current) saveModalSearchHistory(pendingModalSearchRef.current);
                                                    setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                                                    if (onNavigateToNote) {
                                                        onNavigateToNote(result.id);
                                                        setIsSearchOverlayOpen(false);
                                                        setSearchQuery('');
                                                    }
                                                }}
                                                className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-bold hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex items-center gap-1"
                                            >
                                                <ArrowUpRight size={12} />
                                                {language === 'vietnamese' ? 'Xem' : 'View'}
                                            </button>
                                            {onCreateLink && (
                                                <button
                                                    onClick={() => {
                                                        onCreateLink(result.id);
                                                        setIsSearchOverlayOpen(false);
                                                        setSearchQuery('');
                                                    }}
                                                    className="px-3 py-1.5 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-lg text-xs font-bold hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors flex items-center gap-1"
                                                >
                                                    <LinkIcon size={12} />
                                                    {language === 'vietnamese' ? 'Liên kết' : 'Link'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>

                    </div>
                </div>
            )}
        </div >

        {/* Video gallery — every video the author has ever recorded, browsable/filterable by video
            title. Replaces an earlier note-search-based flow per explicit feedback: picking a
            related video should mean picking from actual videos, not guessing which note has one. */}
        {showVideoGallery && (
            <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40" onClick={() => setShowVideoGallery(false)}>
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 p-4 w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                            {language === 'vietnamese' ? 'Chọn video để gắn' : 'Pick a video to attach'}
                        </h4>
                        <button onClick={() => setShowVideoGallery(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="flex gap-2 mb-3">
                        <input
                            type="text"
                            autoFocus
                            value={videoGalleryFilter}
                            onChange={e => setVideoGalleryFilter(e.target.value)}
                            placeholder={language === 'vietnamese' ? 'Lọc theo tên video...' : 'Filter by video title...'}
                            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-rose-300"
                        />
                        {videoGalleryList && videoGalleryList.length > 0 && (
                            <select
                                value={videoGalleryNotebookFilter}
                                onChange={e => setVideoGalleryNotebookFilter(e.target.value)}
                                className="shrink-0 max-w-[40%] px-2 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 text-xs text-gray-600 dark:text-gray-300 outline-none focus:ring-2 focus:ring-rose-300"
                            >
                                <option value="">{language === 'vietnamese' ? 'Tất cả notebook' : 'All notebooks'}</option>
                                {[...new Map(videoGalleryList.map(v => [v.notebookId, v.notebookTitle])).entries()].map(([id, nbTitle]) => (
                                    <option key={id} value={id}>{nbTitle}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto flex flex-col gap-1.5">
                        {videoGalleryLoading && (
                            <div className="flex items-center justify-center py-10 text-gray-400">
                                <Loader2 size={20} className="animate-spin" />
                            </div>
                        )}
                        {videoGalleryError && <p className="text-xs text-red-500 text-center py-4">{videoGalleryError}</p>}
                        {!videoGalleryLoading && !videoGalleryError && videoGalleryList && (() => {
                            const q = videoGalleryFilter.toLowerCase().trim();
                            let filtered = videoGalleryNotebookFilter
                                ? videoGalleryList.filter(v => v.notebookId === videoGalleryNotebookFilter)
                                : videoGalleryList;
                            filtered = q
                                ? filtered.filter(v => v.title.toLowerCase().includes(q) || v.nodeTitle.toLowerCase().includes(q))
                                : filtered;
                            if (filtered.length === 0) {
                                return <p className="text-xs text-gray-400 text-center py-8">{language === 'vietnamese' ? 'Không tìm thấy video nào' : 'No videos found'}</p>;
                            }
                            return filtered.map(v => (
                                <button
                                    key={`${v.nodeId}-${v.videoId}`}
                                    onClick={() => {
                                        onAddRelatedVideo?.({ nodeId: v.nodeId, notebookId: v.notebookId, videoId: v.videoId, nodeTitle: v.nodeTitle, notebookTitle: v.notebookTitle });
                                        setShowVideoGallery(false);
                                        setVideoGalleryFilter('');
                                    }}
                                    className="flex items-center gap-2.5 text-left px-2 py-2 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                                >
                                    <img
                                        src={`https://i.ytimg.com/vi/${v.videoId}/mqdefault.jpg`}
                                        className="w-16 h-9 rounded-md object-cover bg-gray-200 dark:bg-slate-700 flex-shrink-0"
                                        loading="lazy"
                                    />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{v.title}</p>
                                        <p className="text-xs text-gray-400 truncate">{v.nodeTitle} · {v.notebookTitle}</p>
                                    </div>
                                </button>
                            ));
                        })()}
                    </div>
                </div>
            </div>
        )}

        {/* "Save to..." dialog (YouTube-playlist style) — each row toggles this note in/out of that
            collection; stays open so several can be ticked in one go. */}
        {showCoursePicker && (
            <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40" onClick={() => setShowCoursePicker(false)}>
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 p-4 w-full max-w-sm max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                            {language === 'vietnamese' ? 'Lưu vào bộ sưu tập' : 'Save to collection'}
                        </h4>
                        <button onClick={() => setShowCoursePicker(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X size={16} /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto flex flex-col gap-0.5 mb-3">
                        {allCollections.map(c => {
                            const checked = c.items.some(isThisNote);
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => {
                                        const uid = getAuth().currentUser?.uid;
                                        if (!uid || !currentNotebookId) return;
                                        if (checked) removeItemFromCollection(uid, c.id, c.items, node.id, currentNotebookId);
                                        else addThisNoteTo(c.id, c.items);
                                    }}
                                    className="flex items-center gap-2.5 text-left px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                                >
                                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300 dark:border-slate-500'}`}>
                                        {checked && <Check size={12} className="text-white" />}
                                    </span>
                                    <span className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-100 truncate">{c.title}</span>
                                    <span className="text-[11px] text-gray-400 tabular-nums shrink-0">{c.items.length}</span>
                                </button>
                            );
                        })}
                        {allCollections.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-4">{language === 'vietnamese' ? 'Chưa có bộ sưu tập nào — tạo cái đầu tiên bên dưới' : 'No collections yet — create your first below'}</p>
                        )}
                    </div>
                    <div className="flex gap-1.5 pt-3 border-t border-gray-100 dark:border-slate-700">
                        <input
                            autoFocus={allCollections.length === 0}
                            value={newCourseTitle}
                            onChange={e => setNewCourseTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') createCourseWithThisNote(); }}
                            placeholder={language === 'vietnamese' ? 'Tạo bộ sưu tập mới...' : 'New collection...'}
                            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                        <button
                            onClick={createCourseWithThisNote}
                            disabled={!newCourseTitle.trim()}
                            className="px-3 rounded-lg bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 text-white text-xs font-bold shrink-0 flex items-center gap-1"
                        >
                            <Plus size={14} /> {language === 'vietnamese' ? 'Tạo' : 'Create'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Hover preview card for cross-links */}
        {linkHoverPreview && (() => {
            const cardW = linkHoverExpanded ? 720 : 380;
            const cardH = linkHoverExpanded ? 900 : 480;
            const vpW = window.innerWidth; const vpH = window.innerHeight;
            let left = linkHoverPreview.left - cardW - 16;
            if (left < 8) left = linkHoverPreview.left + 16;
            if (left + cardW > vpW - 8) left = vpW - cardW - 8;
            let top = linkHoverPreview.top - 16;
            if (top + cardH > vpH - 8) top = vpH - cardH - 8;
            if (top < 8) top = 8;
            const stripped = (linkHoverPreview.node.content || '').trim();
            // Prev/next in the target note's OWN notebook document order (cross-notebook links
            // preview a note that may not live in the currently-open notebook at all).
            const isCrossNbLink = !!linkHoverPreview.notebookId && linkHoverPreview.notebookId !== currentNotebookId;
            const linkPreviewList = isCrossNbLink
                ? (allNotebooksRef.current.find(nb => nb.id === linkHoverPreview.notebookId)?.nodes ?? [])
                : allNodes;
            const { prevId: linkPrevId, nextId: linkNextId } = getAdjacentNoteIds(linkPreviewList, linkHoverPreview.node.id);
            const goToAdjacentLink = (id: string | null) => {
                if (!id) return;
                if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                const stub = linkPreviewList.find(n => n.id === id);
                if (!stub) return;
                setLinkHoverPreview(prev => prev ? { ...prev, node: stub } : prev);
                if (isCrossNbLink) {
                    // Cross-notebook nodes carried in allNotebooks may be metadata-only stubs —
                    // fetch full content the same way the initial cross-notebook hover does.
                    getNodeContent(linkHoverPreview.notebookId!, id).then(result => {
                        setLinkHoverPreview(prev => (prev && prev.node.id === id)
                            ? { ...prev, node: { ...prev.node, title: result.title, content: result.content, imageBase64: result.imageBase64 ?? prev.node.imageBase64, imageMimeType: result.imageMimeType ?? prev.node.imageMimeType, attachments: result.attachments ?? prev.node.attachments } }
                            : prev);
                    }).catch(() => { /* ignore — preview shows stub */ });
                }
            };
            return (
                <div
                    style={{ position: 'fixed', top, left, width: cardW, maxHeight: cardH, zIndex: 99999, transition: 'width 0.2s ease, max-height 0.2s ease' }}
                    className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 relative ${linkHoverExpanded ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}
                    onMouseEnter={() => { if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current); }}
                    onMouseLeave={() => { if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current); linkHoverTimerRef.current = setTimeout(() => { setLinkHoverPreview(null); setLinkHoverExpanded(false); }, 150); }}
                >
                    {onCreateLink && (
                        <Tip text={language === 'vietnamese' ? 'Tạo liên kết đến note này' : 'Create link to this note'} side="bottom">
                        <button
                            onClick={() => {
                                if (linkHoverPreview.notebookId) {
                                    onCreateLink(linkHoverPreview.node.id, linkHoverPreview.notebookId, linkHoverPreview.node.title || '', linkHoverPreview.notebookTitle || '');
                                } else {
                                    onCreateLink(linkHoverPreview.node.id);
                                }
                            }}
                            className="absolute top-2 right-[4.5rem] z-10 p-1.5 rounded-md bg-black/20 hover:bg-teal-500/80 text-white transition-colors"
                        >
                            <Link2 size={12} />
                        </button>
                        </Tip>
                    )}
                    <Tip text={language === 'vietnamese' ? 'Ghim cửa sổ này' : 'Pin this window'} side="bottom">
                    <button
                        onClick={() => {
                            if (linkHoverTimerRef.current) clearTimeout(linkHoverTimerRef.current);
                            const alreadyPinned = pinnedPreviews.some(p => p.node.id === linkHoverPreview.node.id);
                            if (!alreadyPinned) {
                                setPinnedPreviews(prev => [...prev, { id: `pin-${Date.now()}`, node: linkHoverPreview.node, x: left, y: top, expanded: linkHoverExpanded, notebookId: linkHoverPreview.notebookId }]);
                            }
                            setLinkHoverPreview(null);
                            setLinkHoverExpanded(false);
                        }}
                        className="absolute top-2 right-10 z-10 p-1.5 rounded-md bg-black/20 hover:bg-indigo-500/80 text-white transition-colors"
                    >
                        <Pin size={12} />
                    </button>
                    </Tip>
                    <Tip text={linkHoverExpanded ? (language === 'vietnamese' ? 'Thu nhỏ' : 'Collapse') : (language === 'vietnamese' ? 'Phóng to' : 'Expand')} side="bottom">
                    <button
                        onClick={() => setLinkHoverExpanded(v => !v)}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/20 hover:bg-black/40 text-white transition-colors"
                    >
                        {linkHoverExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    </Tip>
                    <NodeImageSlider node={linkHoverPreview.node} expanded={linkHoverExpanded} />
                    <div className={linkHoverExpanded ? 'p-3.5' : 'flex flex-col flex-1 min-h-0 p-3.5'}>
                        {linkHoverPreview.notebookTitle && (
                            <div className="text-[10px] font-semibold text-violet-500 dark:text-violet-400 flex items-center gap-1 mb-1 shrink-0">
                                <BookOpen size={9} /> {linkHoverPreview.notebookTitle}
                            </div>
                        )}
                        {linkHoverPreview.node.title ? (
                            <div className="font-semibold text-sm text-gray-900 dark:text-white leading-snug shrink-0 mb-2 pr-16">{linkHoverPreview.node.title}</div>
                        ) : (
                            <div className="text-xs text-gray-400 italic shrink-0 mb-2">Untitled</div>
                        )}
                        {stripped ? (
                            <div
                                className={`${linkHoverExpanded ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 leading-relaxed ${linkHoverExpanded ? '' : 'overflow-y-auto flex-1'} pr-1 [&>p]:my-1 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4 [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-semibold`}
                                dangerouslySetInnerHTML={{ __html: parseMarkdown(stripped) }}
                                onWheel={(e) => e.stopPropagation()}
                            />
                        ) : (
                            <p className="text-xs text-gray-300 dark:text-gray-600 italic flex items-center gap-1">
                                <FileText size={10} /> {language === 'vietnamese' ? 'Không có nội dung' : 'No content'}
                            </p>
                        )}
                    </div>
                    {(linkPrevId || linkNextId) && (
                        <div className="shrink-0 flex items-center justify-between border-t border-gray-100 dark:border-slate-700 px-2 py-1.5 bg-gray-50/80 dark:bg-slate-900/40">
                            <button
                                onClick={() => goToAdjacentLink(linkPrevId)}
                                disabled={!linkPrevId}
                                title={language === 'vietnamese' ? 'Note trước đó' : 'Previous note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <ChevronLeft size={13} /> {language === 'vietnamese' ? 'Lùi' : 'Back'}
                            </button>
                            <button
                                onClick={() => goToAdjacentLink(linkNextId)}
                                disabled={!linkNextId}
                                title={language === 'vietnamese' ? 'Note tiếp theo' : 'Next note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                {language === 'vietnamese' ? 'Tiến' : 'Next'} <ChevronRight size={13} />
                            </button>
                        </div>
                    )}
                </div>
            );
        })()}

        {/* Pinned preview windows — draggable, persistent */}
        {pinnedPreviews.map((pinned, pinnedIndex) => {
            const cardW = pinned.expanded ? 720 : 380;
            const cardH = pinned.expanded ? 900 : 480;
            const stripped = (pinned.node.content || '').trim();
            const isCrossNbPin = !!pinned.notebookId && pinned.notebookId !== currentNotebookId;
            const pinnedList = isCrossNbPin
                ? (allNotebooksRef.current.find(nb => nb.id === pinned.notebookId)?.nodes ?? [])
                : allNodes;
            const { prevId: pinPrevId, nextId: pinNextId } = getAdjacentNoteIds(pinnedList, pinned.node.id);
            const goToAdjacentPinned = (id: string | null) => {
                if (!id) return;
                const stub = pinnedList.find(n => n.id === id);
                if (!stub) return;
                setPinnedPreviews(prev => prev.map(p => p.id === pinned.id ? { ...p, node: stub } : p));
                if (isCrossNbPin) {
                    getNodeContent(pinned.notebookId!, id).then(result => {
                        setPinnedPreviews(prev => prev.map(p => p.id === pinned.id
                            ? { ...p, node: { ...p.node, title: result.title, content: result.content, imageBase64: result.imageBase64 ?? p.node.imageBase64, imageMimeType: result.imageMimeType ?? p.node.imageMimeType, attachments: result.attachments ?? p.node.attachments } }
                            : p));
                    }).catch(() => { /* ignore — preview shows stub */ });
                }
            };
            return (
                <div
                    key={pinned.id}
                    onMouseDown={() => setPinnedPreviews(prev => {
                        if (prev[prev.length - 1]?.id === pinned.id) return prev;
                        const item = prev.find(p => p.id === pinned.id)!;
                        return [...prev.filter(p => p.id !== pinned.id), item];
                    })}
                    style={{ position: 'fixed', top: pinned.y, left: pinned.x, width: cardW, maxHeight: cardH, zIndex: 99990 + pinnedIndex, transition: 'width 0.2s ease, max-height 0.2s ease' }}
                    className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border-2 border-indigo-200 dark:border-indigo-800 relative ${pinned.expanded ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}
                >
                    {/* Drag handle header */}
                    <div
                        className="h-7 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-700 dark:to-slate-700 rounded-t-xl cursor-grab active:cursor-grabbing flex items-center px-2.5 gap-1.5 border-b border-indigo-100 dark:border-slate-600 shrink-0 select-none"
                        onMouseDown={(e) => {
                            draggingPinRef.current = { id: pinned.id, offsetX: e.clientX - pinned.x, offsetY: e.clientY - pinned.y };
                            e.preventDefault();
                        }}
                    >
                        <Pin size={10} className="text-indigo-400 shrink-0" />
                        <span className="text-[11px] text-indigo-500 dark:text-indigo-400 font-medium truncate flex-1">{pinned.node.title || 'Untitled'}</span>
                    </div>
                    {/* Expand / Link / Close buttons */}
                    {onCreateLink && (
                        <Tip text={language === 'vietnamese' ? 'Tạo liên kết đến note này' : 'Create link to this note'} side="bottom">
                        <button
                            onClick={() => onCreateLink(pinned.node.id)}
                            className="absolute top-0.5 right-16 z-10 p-1 rounded-md hover:bg-teal-100 dark:hover:bg-teal-900/30 text-teal-400 transition-colors"
                        >
                            <Link2 size={12} />
                        </button>
                        </Tip>
                    )}
                    <Tip text={pinned.expanded ? (language === 'vietnamese' ? 'Thu nhỏ' : 'Collapse') : (language === 'vietnamese' ? 'Phóng to' : 'Expand')} side="bottom">
                    <button
                        onClick={() => setPinnedPreviews(prev => prev.map(p => p.id === pinned.id ? { ...p, expanded: !p.expanded } : p))}
                        className="absolute top-0.5 right-8 z-10 p-1 rounded-md hover:bg-indigo-100 dark:hover:bg-slate-600 text-indigo-400 transition-colors"
                    >
                        {pinned.expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    </Tip>
                    <Tip text={language === 'vietnamese' ? 'Đóng' : 'Close'} side="bottom">
                    <button
                        onClick={() => setPinnedPreviews(prev => prev.filter(p => p.id !== pinned.id))}
                        className="absolute top-0.5 right-1 z-10 p-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors"
                    >
                        <X size={12} />
                    </button>
                    </Tip>
                    <NodeImageSlider node={pinned.node} expanded={pinned.expanded} />
                    <div className={pinned.expanded ? 'p-3.5' : 'flex flex-col flex-1 min-h-0 p-3.5'}>
                        {pinned.node.title ? (
                            <div className="font-semibold text-sm text-gray-900 dark:text-white leading-snug shrink-0 mb-2">{pinned.node.title}</div>
                        ) : (
                            <div className="text-xs text-gray-400 italic shrink-0 mb-2">Untitled</div>
                        )}
                        {stripped ? (
                            <div
                                className={`${pinned.expanded ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 leading-relaxed ${pinned.expanded ? '' : 'overflow-y-auto flex-1'} pr-1 [&>p]:my-1 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4 [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-semibold`}
                                dangerouslySetInnerHTML={{ __html: parseMarkdown(stripped) }}
                                onWheel={(e) => e.stopPropagation()}
                            />
                        ) : (
                            <p className="text-xs text-gray-300 dark:text-gray-600 italic flex items-center gap-1">
                                <FileText size={10} /> {language === 'vietnamese' ? 'Không có nội dung' : 'No content'}
                            </p>
                        )}
                    </div>
                    {(pinPrevId || pinNextId) && (
                        <div className="shrink-0 flex items-center justify-between border-t border-indigo-100 dark:border-slate-600 px-2 py-1.5 bg-indigo-50/60 dark:bg-slate-900/40">
                            <button
                                onClick={() => goToAdjacentPinned(pinPrevId)}
                                disabled={!pinPrevId}
                                title={language === 'vietnamese' ? 'Note trước đó' : 'Previous note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-indigo-500 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <ChevronLeft size={13} /> {language === 'vietnamese' ? 'Lùi' : 'Back'}
                            </button>
                            <button
                                onClick={() => goToAdjacentPinned(pinNextId)}
                                disabled={!pinNextId}
                                title={language === 'vietnamese' ? 'Note tiếp theo' : 'Next note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-indigo-500 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                {language === 'vietnamese' ? 'Tiến' : 'Next'} <ChevronRight size={13} />
                            </button>
                        </div>
                    )}
                </div>
            );
        })}

        {/* Hover preview card for modal search results */}
        {/* Tag right-click context menu */}
        {tagContextMenu && (
            <>
                <div className="fixed inset-0 z-[9998]" onClick={() => setTagContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setTagContextMenu(null); }} />
                <div
                    style={{
                        position: 'fixed',
                        top: Math.min(tagContextMenu.y, window.innerHeight - 110),
                        left: Math.min(tagContextMenu.x, window.innerWidth - 220),
                        zIndex: 9999,
                    }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 py-1 min-w-[210px] overflow-hidden"
                >
                    <button
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 transition-colors"
                        onClick={() => {
                            const t = tagContextMenu.tag;
                            setTagContextMenu(null);
                            setSearchQuery(t.startsWith('#') ? t.slice(1) : t);
                            setIsSearchOverlayOpen(true);
                        }}
                    >
                        <Search size={13} className="text-indigo-400 flex-shrink-0" />
                        <span className="font-medium truncate">
                            {language === 'vietnamese' ? 'Tìm kiếm' : 'Search'}: <span className="text-indigo-500">{tagContextMenu.tag.startsWith('#') ? tagContextMenu.tag : `#${tagContextMenu.tag}`}</span>
                        </span>
                    </button>
                    <div className="h-px bg-gray-100 dark:bg-slate-700 mx-2" />
                    <button
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 transition-colors"
                        onClick={() => {
                            const t = tagContextMenu.tag;
                            setTagContextMenu(null);
                            onTagSearch?.(t);
                        }}
                    >
                        <Tag size={13} className="text-gray-400 flex-shrink-0" />
                        <span>{language === 'vietnamese' ? 'Xem ghi chú liên quan' : 'View related notes'}</span>
                    </button>
                </div>
            </>
        )}
        {searchHoverPreview && (() => {
            const isExpanded = searchHoverExpanded;
            const cardW = isExpanded ? 680 : 320;
            const cardH = isExpanded ? 880 : 360;
            const vpW = window.innerWidth; const vpH = window.innerHeight;
            let left = searchHoverPreview.left + cardW > vpW - 8 ? searchHoverPreview.left - cardW - 36 : searchHoverPreview.left;
            if (left < 8) left = 8;
            let top = searchHoverPreview.top;
            if (top + cardH > vpH - 8) top = vpH - cardH - 8;
            if (top < 8) top = 8;
            // This search pool is always scoped to the current notebook (see the `notebookId ===
            // null` filter above it), so allNodes is always the right list here.
            const { prevId: searchPrevId, nextId: searchNextId } = getAdjacentNoteIds(allNodes, searchHoverPreview.node.id);
            const goToAdjacentSearch = (id: string | null) => {
                if (!id) return;
                if (searchHoverTimerRef.current) clearTimeout(searchHoverTimerRef.current);
                const target = allNodes.find(n => n.id === id);
                if (target) setSearchHoverPreview(prev => prev ? { ...prev, node: target } : prev);
            };
            return (
                <div
                    style={{ position: 'fixed', top, left, width: cardW, maxHeight: cardH, zIndex: 99999, transition: 'width 0.2s ease, max-height 0.2s ease' }}
                    className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-150 relative ${isExpanded ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}
                    onMouseEnter={() => { if (searchHoverTimerRef.current) clearTimeout(searchHoverTimerRef.current); }}
                    onMouseLeave={() => { if (searchHoverTimerRef.current) clearTimeout(searchHoverTimerRef.current); searchHoverTimerRef.current = setTimeout(() => { setSearchHoverPreview(null); setSearchHoverExpanded(false); }, 120); }}
                >
                    {onCreateLink && (
                        <Tip text={language === 'vietnamese' ? 'Tạo liên kết đến note này' : 'Create link to this note'} side="bottom">
                        <button
                            onClick={() => onCreateLink(searchHoverPreview.node.id)}
                            className="absolute top-2 right-[4.5rem] z-10 p-1.5 rounded-md bg-white/90 dark:bg-slate-700/90 text-teal-500 shadow-sm border border-gray-200 dark:border-slate-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
                        >
                            <Link2 size={12} />
                        </button>
                        </Tip>
                    )}
                    <Tip text={language === 'vietnamese' ? 'Ghim cửa sổ này' : 'Pin this window'} side="bottom">
                    <button
                        onClick={() => {
                            if (searchHoverTimerRef.current) clearTimeout(searchHoverTimerRef.current);
                            const alreadyPinned = pinnedPreviews.some(p => p.node.id === searchHoverPreview.node.id);
                            if (!alreadyPinned) {
                                setPinnedPreviews(prev => [...prev, { id: `pin-${Date.now()}`, node: searchHoverPreview.node, x: left, y: top, expanded: isExpanded }]);
                            }
                            setSearchHoverPreview(null);
                            setSearchHoverExpanded(false);
                        }}
                        className="absolute top-2 right-10 z-10 p-1.5 rounded-md bg-white/90 dark:bg-slate-700/90 text-gray-500 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-slate-600 hover:bg-indigo-50 hover:text-indigo-500 dark:hover:bg-indigo-900/30 transition-colors"
                    >
                        <Pin size={12} />
                    </button>
                    </Tip>
                    <Tip text={isExpanded ? (language === 'vietnamese' ? 'Thu nhỏ' : 'Collapse') : (language === 'vietnamese' ? 'Phóng to' : 'Expand')} side="bottom">
                    <button
                        onClick={() => setSearchHoverExpanded(v => !v)}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-white/90 dark:bg-slate-700/90 text-gray-600 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                    >
                        {isExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    </Tip>
                    <NodeImageSlider node={searchHoverPreview.node} expanded={isExpanded} />
                    <div className={isExpanded ? 'p-3.5' : 'p-3.5 overflow-y-auto flex-1 min-h-0'} onWheel={(e) => e.stopPropagation()}>
                        {searchHoverPreview.node.title && (
                            <div className={`font-semibold text-sm text-gray-900 dark:text-white mb-2 ${isExpanded ? 'pr-16' : 'pr-14'}`}>{searchHoverPreview.node.title}</div>
                        )}
                        {searchHoverPreview.node.content ? (
                            <div className={`${isExpanded ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 leading-relaxed [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-3 [&>ol]:pl-3 [&>strong]:font-semibold [&>em]:italic`} dangerouslySetInnerHTML={{ __html: parseMarkdown(searchHoverPreview.node.content) }} />
                        ) : (
                            <p className="text-xs text-gray-300 dark:text-gray-600 italic">{language === 'vietnamese' ? 'Không có nội dung' : 'No content'}</p>
                        )}
                    </div>
                    {(searchPrevId || searchNextId) && (
                        <div className="shrink-0 flex items-center justify-between border-t border-gray-100 dark:border-slate-700 px-2 py-1.5 bg-gray-50/80 dark:bg-slate-900/40">
                            <button
                                onClick={() => goToAdjacentSearch(searchPrevId)}
                                disabled={!searchPrevId}
                                title={language === 'vietnamese' ? 'Note trước đó' : 'Previous note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                <ChevronLeft size={13} /> {language === 'vietnamese' ? 'Lùi' : 'Back'}
                            </button>
                            <button
                                onClick={() => goToAdjacentSearch(searchNextId)}
                                disabled={!searchNextId}
                                title={language === 'vietnamese' ? 'Note tiếp theo' : 'Next note'}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                            >
                                {language === 'vietnamese' ? 'Tiến' : 'Next'} <ChevronRight size={13} />
                            </button>
                        </div>
                    )}
                </div>
            );
        })()}
        {/* Cross-link right-click context menu */}
        {linkContextMenu && (
            <>
                <div className="fixed inset-0 z-[9998]" onClick={() => setLinkContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setLinkContextMenu(null); }} />
                <div
                    style={{
                        position: 'fixed',
                        top: Math.min(linkContextMenu.y, window.innerHeight - 165),
                        left: Math.min(linkContextMenu.x, window.innerWidth - 230),
                        zIndex: 9999,
                    }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 py-1 min-w-[220px] overflow-hidden"
                >
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-slate-700">
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[190px]">{linkContextMenu.nodeTitle}</p>
                    </div>
                    <button
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 transition-colors"
                        onClick={() => {
                            const data = { nodeId: linkContextMenu.nodeId, nodeTitle: linkContextMenu.nodeTitle, notebookId: linkContextMenu.notebookId };
                            localStorage.setItem('sb_copied_crosslink', JSON.stringify(data));
                            setCopiedCrossLink(data);
                            setLinkContextMenu(null);
                        }}
                    >
                        <Copy size={13} className="text-indigo-400 flex-shrink-0" />
                        <span>{language === 'vietnamese' ? 'Sao chép liên kết' : 'Copy link'}</span>
                    </button>
                    <button
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 transition-colors"
                        onClick={() => {
                            navigator.clipboard.writeText(linkContextMenu.nodeTitle).catch(() => {});
                            setLinkContextMenu(null);
                        }}
                    >
                        <Type size={13} className="text-gray-400 flex-shrink-0" />
                        <span>{language === 'vietnamese' ? 'Sao chép tên ghi chú' : 'Copy note name'}</span>
                    </button>
                    <button
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2.5 transition-colors"
                        onClick={() => {
                            setNavigationHistory(prev => [...prev, { nodeId: node.id, notebookId: currentNotebookId ?? null, noteTitle: node.title || '' }]);
                            if (linkContextMenu.notebookId && linkContextMenu.notebookId !== currentNotebookId) {
                                onNavigateCrossNotebook?.(linkContextMenu.notebookId, linkContextMenu.nodeId);
                            } else {
                                onNavigateToNote?.(linkContextMenu.nodeId);
                            }
                            setLinkContextMenu(null);
                        }}
                    >
                        <ArrowUpRight size={13} className="text-gray-400 flex-shrink-0" />
                        <span>{language === 'vietnamese' ? 'Mở ghi chú' : 'Open note'}</span>
                    </button>
                    {!readOnly && onUnlink && (
                        <>
                            <div className="h-px bg-gray-100 dark:bg-slate-700 mx-2" />
                            <button
                                className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2.5 transition-colors"
                                onClick={() => {
                                    onUnlink(linkContextMenu.nodeId);
                                    setLinkContextMenu(null);
                                }}
                            >
                                <X size={13} className="flex-shrink-0" />
                                <span>{language === 'vietnamese' ? 'Xóa liên kết' : 'Remove link'}</span>
                            </button>
                        </>
                    )}
                </div>
            </>
        )}
        {showVoiceTutor && (
            <VoiceTutorModal
                nodeId={node.id}
                nodeTitle={node.title ?? ''}
                nodeContent={node.content ?? ''}
                nodeImages={nodeImagesForTutor.length > 0 ? nodeImagesForTutor : undefined}
                selectedText={tutorSelectedText || undefined}
                onClearSelectedText={() => setTutorSelectedText('')}
                hoverContext={tutorHoverLine || undefined}
                language={language}
                onClose={() => { setShowVoiceTutor(false); setIsTutorSpeaking(false); setHighlightedImageIndex(null); setTutorSelectedText(''); }}
                onSessionEnd={(msgs) => sendTutorSessionEmail({ messages: msgs, nodeTitle: node.title ?? '', language: language === 'vietnamese' ? 'vietnamese' : 'english' })}
                onPhaseChange={setIsTutorSpeaking}
                onHighlightImage={setHighlightedImageIndex}
            />
        )}

        {showVoiceTutorLive && (
            <VoiceTutorLiveModal
                nodeTitle={node.title ?? ''}
                nodeContent={node.content ?? ''}
                language={language}
                onClose={() => setShowVoiceTutorLive(false)}
                onFallback={() => { setShowVoiceTutorLive(false); setShowVoiceTutor(true); }}
            />
        )}

        {/* AI Section Focus Dialog */}
        {aiSectionFocus && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={() => setAISectionFocus(null)}>
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                <div
                    className="relative w-full max-w-lg max-h-[70vh] overflow-y-auto rounded-2xl shadow-2xl bg-white dark:bg-slate-800 p-6 space-y-4"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold text-base text-gray-800 dark:text-gray-100">{aiSectionFocus.title}</h3>
                        <button onClick={() => setAISectionFocus(null)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 transition-colors"><X size={16} /></button>
                    </div>
                    <div className="space-y-4">
                        {aiSectionFocus.items.map((item, i) => (
                            <div key={i} className={`space-y-1.5 ${i > 0 ? 'pt-4 border-t border-gray-100 dark:border-slate-700' : ''}`}>
                                {item.quote && (
                                    <p className={`text-sm italic border-l-2 pl-3 leading-relaxed ${
                                        aiSectionFocus.color === 'red' ? 'text-red-500 dark:text-red-400 border-red-400' :
                                        aiSectionFocus.color === 'yellow' ? 'text-yellow-600 dark:text-yellow-400 border-yellow-400' :
                                        'text-gray-500 border-gray-300'
                                    }`}>"{item.quote}"</p>
                                )}
                                <div className={`text-sm leading-relaxed [&>p]:m-0 ${
                                    item.quote ? 'text-gray-700 dark:text-gray-200' :
                                    aiSectionFocus.color === 'green' ? 'text-green-800 dark:text-green-300' :
                                    aiSectionFocus.color === 'gray' ? 'text-gray-600 dark:text-gray-300' :
                                    'text-gray-700 dark:text-gray-200'
                                }`} dangerouslySetInnerHTML={{ __html: parseMarkdown(item.quote ? item.text : `• ${item.text}`) }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}

        </>
    );
};